// src/pages/projects/InquiriesPage.jsx
// Inquiries pipeline - admin / admin-pa only
// BRIO Migration: Listing table code "1a", Drawer form table code "1b"
// ============================================================================
// UPDATES:
//   #1 Fullscreen mode (fixed overlay, preserves state, Esc to exit)
//   #2 Inquiry → Client separation (source_inquiry_id filtering)
//   #3 Four-state status model: active / not_converted / converted / archived
//   #4 Platform: added "website" option
//   #5 Date column shows Last Follow-Up Date (fallback: "No follow-up yet")
//   #6 Priority: low / medium / high (replaces 0–10 numeric)
//   #7 Follow-up attribution: stores userId + displayName per follow-up
// ── Prompt 2 ──
//   #8  Area Statement module (Excel-style table, versioned, stored in areaStatements[])
//   #9  Default area categories: Indoor / Outdoor / Back of House (new scenarios only)
//   #10 Offers link to Area Statement rows by ID (no manual area duplication)
//   #11 Data integrity: ID-based references, sync warnings, clear comments
// ── Prompt 3 ──
//   #12 Fee Recommendation Model ("Historical" drawer → Fee Intelligence)
//       - Supabase-backed fee_inquiry_models / comparables / factors
//       - Auto-scored comparables from activeProjects (type, location, size)
//       - Manual comparable add via searchable dropdown
//       - 9 adjustment factor sliders (−1 … +1, debounced)
//       - RPC-computed metrics: base rate, multiplier, recommended rate
// ── Prompt 4 ──
//   #13 Follow-up "by" fix: dropdown uses followupByEmployeeId (BIGINT)
//       not auth UUID; new DB col followup_by_employee_id
//   #14 Search crash fix: try-catch + String() coercion in filteredList
//   #15 Fullscreen full-width/full-height: remove all max-width constraints,
//       drawer content uses calc(100vh - …) instead of fixed 60vh
// ── Prompt 5 ──
//   #16 Post-hoc inquiry ↔ project linking (searchable dropdown, link/unlink)
//       - projects.source_inquiry_id (new column) for bidirectional linkage
//       - Conflict detection when project already linked to another inquiry
//   #17 Avg Conversion Probability (Bayesian smoothed, hierarchical buckets)
//       - RPC: compute_inquiry_conversion_probability (per-inquiry, shown in Status)
//       - RPC: compute_fee_model_metrics (per-comparable, shown in Fee Intelligence)
//       - Confidence indicator (low/medium/high) + sample size + bucket info
// ============================================================================
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Plus, Search, ChevronDown, ChevronUp, Trash2, Archive,
  X, Mail, ArrowUpDown, BarChart3,
  Maximize2, Minimize2, Copy, Clock,
  Download, Upload, Paperclip, ExternalLink
} from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioActionGroup,
  BrioFrame,
  BrioModal,
  BrioDatePicker,
  BrioSelect,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSupabaseUsers, useSupabaseFeeModels } from '../../hooks/useSupabase';
import { useSupabaseInquiryIntake } from '../../hooks/useSupabaseInquiryIntake';

import EmailIntakeSection from '../../components/EmailIntakeSection';
import InquiryPitchDecks from '../comms/inquiryPitchDecks';
import { exportInquiryToXlsx, importInquiryFromXlsx, downloadXlsxBuffer } from '../../utils/inquiriesXlsx';
import { parseInquiryLocation } from '../../utils/inquiryLocation';

// ── Constants ────────────────────────────────────────────────────────
const TABLE_CODE = '1a';
const FORM_CODE = '1b';
const SQFT_TO_M2 = 10.7639; // legacy alias — prefer SQFT_PER_M2
const BRIO_PROJECT_TYPES = [
  'Cultural', 'Educational', 'Houses', 'Housing',
  'Hospitality', 'Institutional', 'Interior', 'NGO', 'Public', 'BRIO',
];
const PLATFORM_TYPES = [
  { value: 'social_media', label: 'Social Media' },
  { value: 'direct', label: 'Direct' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'email_forward', label: 'Email Forward' },
  { value: 'website', label: 'Website' },
  { value: 'other', label: 'Other' },
];
const POTENTIAL_OPTIONS = ['10', '20', '30', '40', '50', '60', '70', '80', '90', '100'];
const getPotentialColor = (val) => {
  const n = parseInt(val) || 0;
  if (n <= 30) return 'var(--brio-grey)';
  if (n <= 60) return 'var(--brio-orange, #DB6B5D)';
  return 'var(--brio-green)';
};

// (#6) Priority: categorical low/medium/high replaces numeric 0–10
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const PRIORITY_COLORS = { low: 'var(--brio-grey)', medium: 'var(--brio-orange, #DB6B5D)', high: 'var(--brio-green)', urgent: 'var(--brio-red, #B83D3D)' };
const PRIORITY_SORT_ORDER = { urgent: 4, high: 3, medium: 2, low: 1 };

// ── Smart sort system ────────────────────────────────────────────────
const INQ_SORTABLE_COLUMNS = [
  { key: 'projectName',        label: 'Project Name'  },
  { key: 'clientName',         label: 'Client'        },
  { key: 'lastFollowupDate',   label: 'Last Follow-up' },
  { key: 'nextFollowupDate',   label: 'Next Follow-up' },
  { key: 'potential',          label: 'Potential'     },
  { key: 'priority',           label: 'Priority'      },
  { key: 'projectType',        label: 'Project Type'  },
  { key: 'locationCity',       label: 'Location'      },
  { key: 'estimatedFee',       label: 'Est. Fee'      },
  { key: 'estimatedStartDate', label: 'Est. Start'    },
];
const INQ_SORT_LS_KEY = 'brio_inquiry_sortRules';
const INQ_CALC_MODE_LS_KEY = 'brio_inquiry_calcMode';

function applyInqSorts(list, rules, getClientName) {
  if (!rules || rules.length === 0) return list;
  return list.slice().sort((a, b) => {
    for (const rule of rules) {
      const dir = rule.dir === 'desc' ? -1 : 1;
      let cmp = 0;
      switch (rule.key) {
        case 'projectName':
          cmp = (a.projectName || '').localeCompare(b.projectName || '');
          break;
        case 'clientName':
          cmp = (getClientName(a) || '').localeCompare(getClientName(b) || '');
          break;
        case 'lastFollowupDate':
        case 'nextFollowupDate':
        case 'estimatedStartDate': {
          const av = a[rule.key] || '', bv = b[rule.key] || '';
          if (!av && !bv) cmp = 0;
          else if (!av) cmp = 1;
          else if (!bv) cmp = -1;
          else cmp = av.localeCompare(bv);
          break;
        }
        case 'potential':
          cmp = (parseInt(a.potential) || 0) - (parseInt(b.potential) || 0);
          break;
        case 'priority':
          cmp = (PRIORITY_SORT_ORDER[a.priority] || 0) - (PRIORITY_SORT_ORDER[b.priority] || 0);
          break;
        case 'projectType':
          cmp = (a.projectType || '').localeCompare(b.projectType || '');
          break;
        case 'locationCity':
          cmp = (a.locationCity || '').localeCompare(b.locationCity || '');
          break;
        case 'estimatedFee':
          cmp = (parseFloat(a.estimatedFee) || 0) - (parseFloat(b.estimatedFee) || 0);
          break;
        default: break;
      }
      if (cmp !== 0) return cmp * dir;
    }
    return 0;
  });
}

const INQ_PANEL_BORDER = '#e5e5e5';
const INQ_PANEL_GOLD   = '#9f902a';
const INQ_PANEL_GREY   = '#666666';
const INQ_PANEL_RED    = '#934948';

function InqSortPanel({ sortRules, onChangeSortRules, onClose }) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const usedKeys = sortRules.map(r => r.key);

  const addRule = () => {
    const available = INQ_SORTABLE_COLUMNS.filter(c => !usedKeys.includes(c.key));
    if (available.length === 0) return;
    onChangeSortRules([...sortRules, { key: available[0].key, dir: 'asc' }]);
  };
  const removeRule = idx => onChangeSortRules(sortRules.filter((_, i) => i !== idx));
  const updateRule = (idx, field, value) =>
    onChangeSortRules(sortRules.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const moveRule = (idx, delta) => {
    const next = sortRules.slice();
    const to = idx + delta;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    onChangeSortRules(next);
  };

  const selSt = {
    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
    padding: '4px 8px', border: `1px solid ${INQ_PANEL_BORDER}`, borderRadius: '3px',
    background: '#fff', color: '#333', cursor: 'pointer',
  };

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
      background: '#fff', border: `1px solid ${INQ_PANEL_BORDER}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '380px',
      padding: '12px',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 500, color: INQ_PANEL_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
        Sort rules
      </div>
      {sortRules.length === 0 && (
        <div style={{ fontSize: '12px', fontWeight: 300, color: '#aaa', fontStyle: 'italic', padding: '8px 0' }}>
          No sorting applied
        </div>
      )}
      {sortRules.map((rule, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
          {/* Reorder arrows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <button onClick={() => moveRule(idx, -1)} disabled={idx === 0}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '14px', border: 'none', background: 'transparent', color: idx === 0 ? '#ddd' : '#999', cursor: idx === 0 ? 'default' : 'pointer', padding: 0 }}>
              <ChevronUp size={10} />
            </button>
            <button onClick={() => moveRule(idx, 1)} disabled={idx === sortRules.length - 1}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '14px', border: 'none', background: 'transparent', color: idx === sortRules.length - 1 ? '#ddd' : '#999', cursor: idx === sortRules.length - 1 ? 'default' : 'pointer', padding: 0 }}>
              <ChevronDown size={10} />
            </button>
          </div>
          {/* Column */}
          <select value={rule.key} onChange={e => updateRule(idx, 'key', e.target.value)} style={{ ...selSt, flex: 1 }}>
            {INQ_SORTABLE_COLUMNS.map(c => (
              <option key={c.key} value={c.key} disabled={usedKeys.includes(c.key) && c.key !== rule.key}>{c.label}</option>
            ))}
          </select>
          {/* Direction */}
          <select value={rule.dir} onChange={e => updateRule(idx, 'dir', e.target.value)} style={{ ...selSt, width: '110px' }}>
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
          {/* Remove */}
          <button onClick={() => removeRule(idx)}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', border: 'none', background: 'transparent', color: '#bbb', cursor: 'pointer', padding: 0, borderRadius: '3px' }}
            onMouseEnter={e => { e.currentTarget.style.color = INQ_PANEL_RED; e.currentTarget.style.background = '#93494810'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.background = 'transparent'; }}>
            <X size={14} />
          </button>
        </div>
      ))}
      <button onClick={addRule} disabled={usedKeys.length >= INQ_SORTABLE_COLUMNS.length}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '4px 8px', border: 'none', background: 'transparent', color: usedKeys.length >= INQ_SORTABLE_COLUMNS.length ? '#ccc' : INQ_PANEL_GOLD, cursor: usedKeys.length >= INQ_SORTABLE_COLUMNS.length ? 'default' : 'pointer', marginTop: '4px' }}>
        <Plus size={12} /> Add sort
      </button>
    </div>
  );
}

// ── Pipeline calculator ──────────────────────────────────────────────
// Five touchpoints in the inquiry funnel. Four are auto-derived from
// existing fields; "founderMeeting" is a dedicated field on inquiries.
const INQ_TOUCHPOINTS = [
  { key: 'firstResponse', label: 'First Response',     hint: 'Any follow-up logged'                  },
  { key: 'founderMeeting', label: 'Meeting with Founders', hint: 'Founder meeting date set in Status' },
  { key: 'feeProposal',    label: 'Fee Proposal',          hint: 'At least one fee offer with a date' },
  { key: 'negotiation',    label: 'Negotiation',           hint: 'Any fee offer with rates entered (fee sent)' },
  { key: 'appointment',    label: 'Appointment',           hint: 'Inquiry converted to a project'      },
];

function inquiryHasTouchpoint(inq, key) {
  if (!inq) return false;
  switch (key) {
    case 'firstResponse':
      return !!inq.lastFollowupDate;
    case 'founderMeeting':
      return !!inq.founderMeetingDate;
    case 'feeProposal': {
      const offers = Array.isArray(inq.feeOffers) ? inq.feeOffers : [];
      return offers.some(o => o && (o.date || o.sentDate));
    }
    case 'negotiation': {
      const offers = Array.isArray(inq.feeOffers) ? inq.feeOffers : [];
      // Any offer with rate data entered = fee sent = negotiation started
      return offers.some(o => o && (
        Object.values(o.groupRates || {}).some(v => v && v !== '') ||
        (o.rows || []).some(r => r.rateSqft && r.rateSqft !== '')
      ));
    }
    case 'appointment':
      return inq.status === 'converted' || inq.converted === true;
    default:
      return false;
  }
}

// Two ways to scope the pipeline funnel (toggle inside the Performance panel):
//  - 'all'           : every inquiry in view counts toward the denominator, whether or
//                      not a first response was ever logged (original behaviour / default).
//  - 'firstResponse' : restrict the funnel to inquiries that have reached the First
//                      Response stage, so every percentage is measured only against the
//                      inquiries the studio has actually engaged with.
const INQ_FUNNEL_MODES = [
  {
    key: 'all',
    label: 'All inquiries',
    denomLabel: 'of all inquiries',
    hint: 'Percentages measured against every inquiry, whether or not a first response exists',
  },
  {
    key: 'firstResponse',
    label: 'With first response',
    denomLabel: 'of first-response inquiries',
    hint: 'Percentages measured only against inquiries that have had a first response',
  },
];
const INQ_FUNNEL_MODE_KEYS = INQ_FUNNEL_MODES.map(m => m.key);

// An inquiry has "reached" First Response when it carries any touchpoint at all —
// either an explicit follow-up date or, via the cumulative funnel, a later stage such as
// a founder meeting or a completed conversion. This keeps every conversion inside the
// first-response population (older wins with no logged follow-up still count).
function inquiryReachedFirstResponse(inq) {
  return INQ_TOUCHPOINTS.some(tp => inquiryHasTouchpoint(inq, tp.key));
}

function computeInquiryFunnel(list) {
  const total = list.length;
  const counts = {};
  for (const tp of INQ_TOUCHPOINTS) counts[tp.key] = 0;

  // The touchpoints form an ordered funnel, so reaching a later stage implies
  // every earlier one — an inquiry that converted (Appointment) necessarily had
  // a Meeting with Founders, a Fee Proposal and a Negotiation, even when those
  // fields were never filled in (older wins imported from before the app existed
  // often carry only a conversion flag). We find the furthest stage each inquiry
  // explicitly reached and credit it with every stage up to and including that
  // one. This keeps the funnel monotonic (counts never rise as you go deeper)
  // and stops intermediate stages from under-counting completed conversions.
  for (const inq of list) {
    let furthest = -1;
    for (let i = 0; i < INQ_TOUCHPOINTS.length; i++) {
      if (inquiryHasTouchpoint(inq, INQ_TOUCHPOINTS[i].key)) furthest = i;
    }
    for (let i = 0; i <= furthest; i++) counts[INQ_TOUCHPOINTS[i].key]++;
  }

  const rate = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const reached = INQ_TOUCHPOINTS.map((tp, i) => {
    const c = counts[tp.key];
    // Stage rate: relative to previous stage (or total for the first)
    const prevCount = i === 0 ? total : counts[INQ_TOUCHPOINTS[i - 1].key];
    return {
      key: tp.key,
      label: tp.label,
      hint: tp.hint,
      count: c,
      ofTotalPct: rate(c, total),
      stageRatePct: rate(c, prevCount),
      stageBase: prevCount,
      stageBaseLabel: i === 0 ? 'all inquiries' : INQ_TOUCHPOINTS[i - 1].label,
    };
  });
  return { total, stages: reached, conversionPct: rate(counts.appointment, total) };
}

function InqCalculatorPanel({ funnel, scopeLabel, mode = 'all', onChangeMode, onClose }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const barTrack = '#eee';
  const barFill  = INQ_PANEL_GOLD;
  const denomLabel = (INQ_FUNNEL_MODES.find(m => m.key === mode) || INQ_FUNNEL_MODES[0]).denomLabel;

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
      background: '#fff', border: `1px solid ${INQ_PANEL_BORDER}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '320px',
      padding: '14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '4px' }}>
        <div style={{ fontSize: '11px', fontWeight: 500, color: INQ_PANEL_GREY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Pipeline Performance
        </div>
        <div style={{ fontSize: '10px', color: '#aaa' }}>{scopeLabel}</div>
      </div>
      <div style={{ fontSize: '10px', color: '#888', marginBottom: '10px' }}>
        {funnel.total} {funnel.total === 1 ? 'inquiry' : 'inquiries'} · {funnel.conversionPct}% end-to-end conversion
      </div>
      {/* Scope toggle — choose which inquiries form the funnel denominator */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '10px', background: '#f4f4f4', padding: '3px', borderRadius: '5px' }}>
        {INQ_FUNNEL_MODES.map((opt) => {
          const active = mode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChangeMode && onChangeMode(opt.key)}
              title={opt.hint}
              style={{
                flex: 1, padding: '4px 6px', fontSize: '10px', lineHeight: 1.2,
                border: 'none', borderRadius: '3px', cursor: active ? 'default' : 'pointer',
                background: active ? '#fff' : 'transparent',
                color: active ? INQ_PANEL_GOLD : '#8a8a8a',
                fontWeight: active ? 600 : 400,
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                fontFamily: 'Raleway, sans-serif', transition: 'background 0.15s ease, color 0.15s ease',
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>
      {funnel.total === 0 ? (
        <div style={{ fontSize: '12px', fontWeight: 300, color: '#aaa', fontStyle: 'italic', padding: '8px 0' }}>
          No inquiries in the current view.
        </div>
      ) : funnel.stages.map((s) => (
        <div key={s.key} style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '11px', marginBottom: '3px' }}>
            <span style={{ fontWeight: 500, color: '#333' }} title={s.hint}>{s.label}</span>
            <span style={{ color: '#666', fontFamily: 'Raleway, sans-serif' }}>
              <span style={{ fontWeight: 600, color: INQ_PANEL_GOLD }}>{s.ofTotalPct}%</span>
              <span style={{ color: '#aaa', marginLeft: '6px' }}>{s.count}/{funnel.total}</span>
            </span>
          </div>
          <div style={{ height: '4px', background: barTrack, borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${s.ofTotalPct}%`, background: barFill, transition: 'width 0.2s ease' }} />
          </div>
          <div style={{ fontSize: '9px', color: '#aaa', marginTop: '2px' }}>
            {denomLabel}
          </div>
        </div>
      ))}
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${INQ_PANEL_BORDER}`, fontSize: '9px', color: '#999', lineHeight: 1.4 }}>
        Auto-derived from follow-ups, founder meeting date, fee offers, and conversion status.
        Cumulative funnel — an inquiry that reached a later stage counts toward every earlier one.
      </div>
    </div>
  );
}

// (#3) Four-state inquiry status model
// - active:         ongoing inquiries
// - not_converted:  paused / on hold / to be revisited
// - converted:      successfully copied into ClientsPage
// - archived:       very unlikely to convert, retained for analytics only
const INQUIRY_STATUSES = ['active', 'not_converted', 'converted', 'archived'];

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'not_converted', label: 'Not Converted' },
  { key: 'converted', label: 'Converted' },
  { key: 'archived', label: 'Archived' },
];

// Default columns (user can reorder via drag)
const DEFAULT_COLUMNS = [
  { key: 'client', label: 'Project Name / Client', minWidth: 150 },
  { key: 'date', label: 'Last Follow-up', minWidth: 110 },
  { key: 'nextFollowup', label: 'Next Follow-up', minWidth: 110 },
  { key: 'overdue', label: '', minWidth: 36 },
  { key: 'potential', label: 'Potential', minWidth: 80 },
  { key: 'priority', label: 'Priority', minWidth: 80 },
  { key: 'projectType', label: 'Type', minWidth: 100 },
  { key: 'location', label: 'Location', minWidth: 90 },
  { key: 'estimatedFee', label: 'Est. Fee', minWidth: 90 },
  { key: 'estimatedStartDate', label: 'Est. Start', minWidth: 100 },
  { key: 'nextSteps', label: 'Next Steps', minWidth: 110 },
];

// ── Shared styles ────────────────────────────────────────────────────
const INPUT_STYLE = {
  width: '100%', padding: '4px 8px',
  border: '1px solid var(--brio-border, rgba(0,0,0,0.1))',
  borderRadius: 0, fontSize: '12px', lineHeight: '1.4',
  background: 'var(--brio-white, #fff)', color: 'var(--brio-black, #111111)', boxSizing: 'border-box',
};
const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };
const TEXTAREA_STYLE = { ...INPUT_STYLE, minHeight: '52px', resize: 'vertical' };
const HINT_STYLE = { fontSize: '11px', color: 'var(--brio-grey, #666666)', marginTop: '2px', lineHeight: '1.3' };

// ── Drawer autosave ─────────────────────────────────────────────────────────
// Inquiry fields that persist automatically when a drawer input loses focus
// (mirrors the Comms page's on-blur save). Deliberately EXCLUDES:
//   • status / converted / archived — saved on-change via the Status <select>
//     (conversion/deletion go through their own confirm modals)
//   • nextFollowupDate / founderMeetingDate — already saved on-change
//   • historicalComparison and all transient `_`-prefixed keys
const INQUIRY_AUTOSAVE_FIELDS = [
  'projectName', 'invoicingName', 'invoicingAddress',
  'clientNameOverride', 'clientOrgNameOverride', 'clientPhoneOverride',
  'clientEmailOverride', 'clientAddressOverride', 'clientLinkedinOverride',
  'clientInterestingLinksOverride',
  'siteDropboxLink', 'siteNotes', 'plotArea', 'locationCity', 'locationCoordinates',
  'briefNotes', 'estimatedFee', 'estimatedStartDate', 'briefAreaStatementLink',
  'nextSteps', 'inquiryDate', 'platformType', 'platformDetail',
  'feeScenarios', 'feeOffers', 'areaStatements',
];

// Loose equality for autosave diffing: treats null/undefined/'' as equal and
// deep-compares arrays/objects (fee scenarios, offers, area statements) by value.
const inquiryValueEqual = (a, b) => {
  if (a === b) return true;
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return true;
  if (aEmpty !== bEmpty) return false;
  if (typeof a === 'object' || typeof b === 'object') {
    try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
  }
  return false;
};

// Build a persisted-values snapshot for autosave diffing. Deep-copies object/
// array fields so later edits (even in-place) are detected as changes.
const inquiryDraftSnapshot = (d) => {
  const snap = {};
  if (!d) return snap;
  for (const key of INQUIRY_AUTOSAVE_FIELDS) {
    const v = d[key];
    snap[key] = (v && typeof v === 'object') ? JSON.parse(JSON.stringify(v)) : v;
  }
  return snap;
};
const CELL_TRUNCATE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
// Compact inline overrides for BrioSelect / BrioDatePicker inside table cells
const COMPACT_SELECT = { padding: '4px 28px 4px 8px', fontSize: '11px' };
const INLINE_CELL_DATE = {
  width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box',
};

// (#12) Fee factor definitions now loaded from fee_factor_definitions table at runtime.
// See feeFactorDefs state + loadFeeModelData().

// ── Overdue follow-up helpers ────────────────────────────────────────
// "Not closed" = status is active or not_converted (converted/archived are closed)
const CLOSED_STATUSES = new Set(['converted', 'archived']);
const getOverdueDays = (inq) => {
  if (!inq.nextFollowupDate) return 0;
  const status = inq._status || inq.status || 'active';
  if (CLOSED_STATUSES.has(status)) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(inq.nextFollowupDate + 'T00:00:00'); // local-time safe
  if (isNaN(due.getTime())) return 0;
  const diff = Math.floor((today - due) / 86400000);
  return diff > 0 ? diff : 0;
};
const OVERDUE_AMBER = 'var(--brio-yellow, #d97706)';
const OVERDUE_RED   = 'var(--brio-red, #dc2626)';
const overdueColor  = (days) => days >= 4 ? OVERDUE_RED : OVERDUE_AMBER;
const SECTION_HEADER_STYLE = {
  padding: '8px 10px 5px', fontWeight: 500, fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--brio-gold, #9F902A)',
  borderBottom: '1px solid var(--brio-border, #e2e8f0)',
  background: 'var(--brio-bg-subtle, #fafaf6)',
};

// ── Fee Benchmarking layout primitives ───────────────────────────────
// Shared by the reorganized Fee Benchmarking tab (drawerSection === 'historical').
// No background fills on cells/rows — borders + weight/color carry the structure.
const FB_SECTION_LABEL = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  fontSize: '10px', letterSpacing: '0.10em', textTransform: 'uppercase',
  fontWeight: 600, color: 'var(--brio-gold, #9F902A)',
  paddingBottom: '6px', marginBottom: '6px',
  borderBottom: '1px solid var(--brio-border, rgba(0,0,0,0.10))',
};
const FB_HERO_LABEL = {
  fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--brio-muted, #666666)', fontWeight: 500, marginBottom: '4px',
};
const FB_TERM = { display: 'inline-flex', flexDirection: 'column', lineHeight: 1.1 };
const FB_TLABEL = {
  fontSize: '9px', letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--brio-muted, #666666)', fontWeight: 500, marginBottom: '2px',
};
const FB_TVAL = { fontSize: '12px', fontWeight: 600, color: 'var(--brio-text, #111)' };
const FB_OP = { color: 'var(--brio-muted, #666)', fontWeight: 300, fontSize: '14px' };
const FB_CHIP = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '2px 7px', fontSize: '10px',
  border: '1px solid var(--brio-border, rgba(0,0,0,0.10))',
  color: 'var(--brio-text)',
};
const FB_TH = {
  textAlign: 'left', fontSize: '9.5px', letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--brio-muted, #666)',
  fontWeight: 600, padding: '6px 8px', whiteSpace: 'nowrap',
  background: '#f4f4f0',
  borderBottom: '1px solid var(--brio-border, rgba(0,0,0,0.10))',
};
const FB_TH_NUM = { ...FB_TH, textAlign: 'right' };
const FB_TD = { padding: '6px 8px', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' };
const FB_TD_NUM = { ...FB_TD, textAlign: 'right' };

// ── Helpers ──────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// (#9) Default area categories for NEW scenarios/area-statements only.
// Changed from: Built Up / Construction Area / Carpet Area
// Existing saved scenarios are NOT affected - normScenario preserves their rows.
const DEFAULT_AREA_CATEGORIES = ['indoor', 'covered_outdoor', 'non_covered_outdoor'];
const AREA_CAT_LABELS = { indoor: 'Indoor', covered_outdoor: 'Covered Outdoor', non_covered_outdoor: 'Non-covered Outdoor' };
const SQFT_PER_M2 = 10.7639;

// ── Unit conversion helpers ──────────────────────────────────────────
// Canonical storage unit: sq.ft.  Toggle converts on-the-fly.
const toM2 = (sqft) => (parseFloat(sqft) || 0) / SQFT_PER_M2;
const toSqft = (m2) => (parseFloat(m2) || 0) * SQFT_PER_M2;
const displayArea = (val, unit) => { const n = parseFloat(val) || 0; return n === 0 ? '' : unit === 'm2' ? toM2(n).toFixed(1) : n.toFixed(0); };
const parseAreaInput = (input, unit) => { const n = parseFloat(input) || 0; return unit === 'm2' ? toSqft(n) : n; };

// ── Area group/subrow summation for linking ──────────────────────────
// Sum total area (all categories) for a set of group IDs from the active version
const sumAreaByGroupIds = (groups, groupIds) => {
  if (!groups || !groupIds?.length) return 0;
  const idSet = new Set(groupIds);
  return groups.filter(g => idSet.has(g.id)).reduce((sum, g) =>
    sum + (g.subrows || []).reduce((s, r) => s + (parseFloat(r.indoor) || 0) + (parseFloat(r.covered_outdoor) || 0) + (parseFloat(r.non_covered_outdoor) || 0), 0)
  , 0);
};

// Get carpet area per group (all categories summed), in canonical sqft
const getGroupCarpetArea = (grp) => (grp.subrows || []).reduce((s, r) =>
  s + (parseFloat(r.indoor) || 0) + (parseFloat(r.covered_outdoor) || 0) + (parseFloat(r.non_covered_outdoor) || 0), 0);

// Backward-compat alias
const getGroupTotal = getGroupCarpetArea;

// Get walls & circulation percentage for a group (backward compat: default 0)
const getGroupWallsPct = (grp) => parseFloat(grp.wallsCirculationPct) || 0;

// Compute constructed area for a group:
//   walls & circulation applies ONLY to indoor carpet area (not outdoor categories)
//   constructed = total carpet area + (indoor carpet × pct/100)
const getGroupConstructedArea = (grp) => {
  const totalCarpet = getGroupCarpetArea(grp);
  const indoorCarpet = (grp.subrows || []).reduce((s, r) => s + (parseFloat(r.indoor) || 0), 0);
  const pct = getGroupWallsPct(grp);
  const wallsAmount = indoorCarpet * (pct / 100);
  // Round to a whole ft² (the canonical storage unit) BEFORE it is used anywhere.
  // Fees, per-row displays and running totals are all derived from this single
  // value, so the number shown in the fee table is exactly the number multiplied
  // by the rate — displayed area × rate === displayed fee, and the rows add up to
  // the displayed total. Previously the raw fractional area (e.g. 6681.5, from the
  // walls-&-circulation %) was used for the fee while only the on-screen area was
  // rounded (→ 6682), so the visible numbers never tallied.
  return Math.round(totalCarpet + wallsAmount);
};

// Sum constructed area by group IDs (replaces sumAreaByGroupIds for scenarios/offers)
const sumConstructedAreaByGroupIds = (groups, groupIds) => {
  if (!groups || !groupIds?.length) return 0;
  const idSet = new Set(groupIds);
  return groups.filter(g => idSet.has(g.id)).reduce((sum, g) => sum + getGroupConstructedArea(g), 0);
};

const formatGroupCodes = (groups, groupIds) => {
  if (!groups || !groupIds?.length) return '—';
  const m = new Map(groups.map(g => [g.id, g]));
  return groupIds.map(id => m.get(id)?.code || '?').join(' + ');
};

const blankScenarioRow = () => ({ category: '', areaSqft: '', rateSqft: '' });
const blankScenario = (n) => ({
  name: n || '',
  rows: DEFAULT_AREA_CATEGORIES.map(cat => ({ category: AREA_CAT_LABELS[cat] || cat, areaSqft: '', rateSqft: '' })),
  groupRates: {},
});
const blankOffer = () => ({ label: '', rows: [blankScenarioRow()], groupRates: {}, cc: '', date: '', notes: '' });

// Compute total fee from groupRates × area group constructed areas.
// Returns a rounded integer or null if no usable data.
function _computeTotalFromGroupRates(groupRates, areaGroups) {
  if (!groupRates || Object.keys(groupRates).length === 0) return null;
  const total = areaGroups.reduce((sum, g) => {
    const rate = parseFloat(groupRates[g.id]) || 0;
    const area = getGroupConstructedArea(g);
    return sum + rate * area;
  }, 0);
  return total > 0 ? Math.round(total) : null;
}

// Auto-fill helper: tries feeOffers (latest by date) then feeScenarios (first with rates).
function computeLatestOfferTotal(inq) {
  const areaVersions = Array.isArray(inq.areaStatements) && inq.areaStatements.length > 0 ? inq.areaStatements : null;
  if (!areaVersions) return null;
  const activeStmt = areaVersions[areaVersions.length - 1];
  const areaGroups = activeStmt?.groups || [];
  if (areaGroups.length === 0) return null;

  // 1. Try feeOffers: Final Agreement first, then latest dated, then latest (last) undated
  const offers = Array.isArray(inq.feeOffers) && inq.feeOffers.length > 0 ? inq.feeOffers : null;
  if (offers) {
    // a. Final Agreement takes priority if it has rates
    const fa = offers.find(o => o && /final\s*agreement/i.test(o.label || ''));
    if (fa) {
      const result = _computeTotalFromGroupRates(fa.groupRates, areaGroups);
      if (result) return result;
    }
    // b. Most recently dated offer with rates
    const datedOffers = offers.filter(o => o && o.date);
    if (datedOffers.length > 0) {
      datedOffers.sort((a, b) => b.date.localeCompare(a.date));
      for (const o of datedOffers) {
        const result = _computeTotalFromGroupRates(o?.groupRates, areaGroups);
        if (result) return result;
      }
    }
    // c. Latest (last) undated offer with rates
    for (let i = offers.length - 1; i >= 0; i--) {
      const result = _computeTotalFromGroupRates(offers[i]?.groupRates, areaGroups);
      if (result) return result;
    }
  }

  // 2. Fall back to feeScenarios — first scenario with rates
  const scenarios = Array.isArray(inq.feeScenarios) && inq.feeScenarios.length > 0 ? inq.feeScenarios : null;
  if (scenarios) {
    for (const sc of scenarios) {
      const result = _computeTotalFromGroupRates(sc?.groupRates, areaGroups);
      if (result) return result;
    }
  }

  return null;
}

// ── Area Statement ID generators (#8) ───────────────────────────────
let _areaIdSeq = 0;
const genAreaRowId = () => `ar_${Date.now()}_${++_areaIdSeq}`;
const genAreaGroupId = () => `ag_${Date.now()}_${++_areaIdSeq}`;
const genAreaVersionId = () => `av_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// (#8) Blank subrow - one row inside a group, with per-category area values
const blankSubrow = (num, label) => ({ id: genAreaRowId(), num: num || '1', label: label || '', indoor: '', covered_outdoor: '', non_covered_outdoor: '' });

// (#8) Blank area group - main expandable section (A, B, C, …)
const blankAreaGroup = (code) => ({
  id: genAreaGroupId(),
  code: code || 'A',
  label: '',
  collapsed: false,
  subrows: [blankSubrow('1', 'Living Room'), blankSubrow('2', 'Kitchen'), blankSubrow('3', 'Dining Room')],
});

// (#8) Blank area statement version
const blankAreaStatement = (name) => ({
  id: genAreaVersionId(),
  name: name || 'Version 1',
  createdAt: new Date().toISOString(),
  groups: [
    { ...blankAreaGroup('A'), label: 'Main Areas' },
    { ...blankAreaGroup('B'), label: 'Amenities + Staff' },
  ],
  // Legacy compat: rows field is kept empty - new model uses groups
  rows: [],
});

// (#10) Build offer rows from area statement
const blankOfferFromArea = (areaStmt) => ({
  label: '',
  rows: (areaStmt?.rows || []).map(ar => ({
    areaStatementRowId: ar.id,
    category: ar.category,
    areaSqft: ar.areaSqft,
    rateSqft: '',
  })),
  groupRates: {},
  cc: '', date: '', notes: '',
});

// Convert legacy rows that have area/rate (m² based) to areaSqft/rateSqft
const normRow = (r) => {
  if (r.areaSqft !== undefined) return r;
  const aM2 = parseFloat(r.area) || 0;
  const rM2 = parseFloat(r.rate) || 0;
  return {
    category: r.category || '',
    areaSqft: aM2 > 0 ? (aM2 * SQFT_PER_M2).toFixed(0) : '',
    rateSqft: rM2 > 0 ? (rM2 / SQFT_PER_M2).toFixed(2) : '',
  };
};
const normScenario = (s) => ({ ...s, rows: (s.rows || []).map(normRow), groupRates: s.groupRates || {} });

// Migrate legacy area version: old flat rows → new group/subrow model
const migrateAreaVersion = (v) => {
  if (v.groups && v.groups.length > 0) {
    // Migrate old field names: outdoor → covered_outdoor, back_of_house → non_covered_outdoor
    const groups = v.groups.map(g => ({
      ...g,
      subrows: (g.subrows || []).map(r => ({
        ...r,
        covered_outdoor: r.covered_outdoor ?? r.outdoor ?? '',
        non_covered_outdoor: r.non_covered_outdoor ?? r.back_of_house ?? '',
      })),
    }));
    return { ...v, groups };
  }
  // Old format: v.rows = [{ id, category, areaSqft }]
  const oldRows = v.rows || [];
  const subrows = oldRows.map((r, i) => ({
    id: r.id || genAreaRowId(),
    num: String(i + 1),
    label: r.category || '',
    indoor: r.areaSqft || '',
    covered_outdoor: '',
    non_covered_outdoor: '',
  }));
  return {
    ...v,
    groups: [{ id: genAreaGroupId(), code: 'A', label: 'Main Areas', collapsed: false, subrows: subrows.length > 0 ? subrows : [blankSubrow('1', 'Living Room')] }],
  };
};
// Convert legacy offer from { rates, totalFee } to row-based
const normOffer = (o) => {
  // Always preserve groupRates (new model) alongside legacy rows
  const base = {
    label: o.label || '',
    rows: (o.rows || []).map(normRow),
    groupRates: o.groupRates || {},
    cc: o.cc || '',
    date: o.date || '',
    notes: o.notes || '',
  };
  // Spread any extra fields the offer may carry
  return { ...o, ...base };
};

const rowCalc = (r) => {
  const aSqft = parseFloat(r.areaSqft) || 0;
  const rSqft = parseFloat(r.rateSqft) || 0;
  return {
    areaSqft: aSqft,
    areaM2: aSqft > 0 ? (aSqft / SQFT_TO_M2) : 0,
    rateSqft: rSqft,
    rateM2: rSqft > 0 ? (rSqft * SQFT_TO_M2) : 0,
    subtotal: aSqft * rSqft,
  };
};



// (#3) Derive status string from legacy boolean fields or explicit status field
// Handles backward compatibility: old records may use archived/converted booleans.
function deriveInquiryStatus(inq) {
  if (inq.status && INQUIRY_STATUSES.includes(inq.status)) return inq.status;
  if (inq.converted) return 'converted';
  if (inq.archived) return 'archived';
  return 'active';
}

// (#6) Migrate legacy numeric priority (0–10) to categorical
function migratePriority(val) {
  if (typeof val === 'string' && PRIORITY_OPTIONS.includes(val)) return val;
  const num = parseInt(val);
  if (isNaN(num)) return 'medium';
  if (num <= 3) return 'low';
  if (num <= 6) return 'medium';
  return 'high';
}


// ═══════════════════════════════════════════════════════════════════════
// Unified Area-Group Fee Table (used by both Scenarios and Offers)
// One row per area group: Program | Area | Rate | Fee
// groupRates = { [groupId]: rateSqft }  (canonical sq.ft.)
// ═══════════════════════════════════════════════════════════════════════
function AreaGroupFeeTable({ areaGroups, areaUnit, groupRates, onGroupRateChange, fmtCurr }) {
  const unitLabel = areaUnit === 'm2' ? 'm²' : 'ft²';
  const AREA_CATS = ['indoor', 'covered_outdoor', 'non_covered_outdoor'];

  const dA = (v) => { const n = parseFloat(v) || 0; return n === 0 ? '0' : areaUnit === 'm2' ? (n / SQFT_PER_M2).toFixed(1) : Math.round(n).toLocaleString(); };

  const groupTotals = useMemo(() => {
    const m = {};
    (areaGroups || []).forEach(g => {
      // A5: Use constructed area (carpet + walls%) for fee calculations
      m[g.id] = getGroupConstructedArea(g);
    });
    return m;
  }, [areaGroups]);

  const rates = groupRates || {};
  const groups = areaGroups || [];

  // Compute per-group
  const groupRows = groups.map(g => {
    const area = groupTotals[g.id] || 0;
    const rateSqft = parseFloat(rates[g.id]) || 0;
    const displayRate = areaUnit === 'm2' && rateSqft > 0 ? (rateSqft * SQFT_PER_M2).toFixed(2) : rateSqft;
    const fee = area * rateSqft;
    return { ...g, area, rateSqft, displayRate, fee };
  });
  const totalArea = groupRows.reduce((s, r) => s + r.area, 0);
  const totalFee = groupRows.reduce((s, r) => s + r.fee, 0);

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '11px' }}>
        <colgroup>
          <col /> {/* Program — takes remaining space */}
          <col style={{ width: '72px' }} />
          <col style={{ width: '80px' }} />
          <col style={{ width: '80px' }} />
        </colgroup>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--brio-border, #e2e8f0)' }}>
            <th style={{ textAlign: 'left', padding: '3px 4px', fontWeight: 500, fontSize: '10px' }}>Program</th>
            <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, fontSize: '10px' }}>Area<br/><span style={{ fontWeight: 400, fontSize: '9px', color: 'var(--brio-muted)' }}>constructed {unitLabel}</span></th>
            <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, fontSize: '10px' }}>Rate<br/><span style={{ fontWeight: 400, fontSize: '9px', color: 'var(--brio-muted)' }}>₹/{unitLabel}</span></th>
            <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, fontSize: '10px' }}>Fee</th>
          </tr>
        </thead>
        <tbody>
          {groupRows.map(gr => (
            <tr key={gr.id} style={{ borderBottom: '1px solid var(--brio-border, #f1f5f9)' }}>
              <td style={{ padding: '3px 4px' }}>
                <span style={{ fontWeight: 700 }}>{gr.code}</span>
                {gr.label && <span style={{ color: 'var(--brio-muted)', fontSize: '10px', marginLeft: '6px' }}>{gr.label}</span>}
              </td>
              <td style={{ textAlign: 'right', padding: '3px 4px', color: 'var(--brio-text)' }}>
                {dA(gr.area)}
              </td>
              <td style={{ padding: '3px 3px' }}>
                <input type="number"
                  value={areaUnit === 'm2' ? gr.displayRate : (gr.rateSqft || '')}
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 0;
                    const canonical = areaUnit === 'm2' ? (v / SQFT_PER_M2) : v;
                    onGroupRateChange(gr.id, canonical || '');
                  }}
                  style={{ ...INPUT_STYLE, padding: '2px 4px', fontSize: '11px', textAlign: 'right', width: '100%', boxSizing: 'border-box' }}
                />
              </td>
              <td style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, color: 'var(--brio-gold)' }}>
                {gr.fee > 0 ? (fmtCurr ? fmtCurr(gr.fee) : gr.fee.toLocaleString()) : '—'}
              </td>
            </tr>
          ))}
          {groups.length === 0 && (
            <tr><td colSpan={4} style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--brio-muted)', fontStyle: 'italic', fontSize: '10px' }}>No area groups defined — add groups in the Area Statement section</td></tr>
          )}
        </tbody>
        {groups.length > 0 && (
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--brio-gold)' }}>
              <td style={{ padding: '4px', fontWeight: 700, fontSize: '11px' }}>Total</td>
              <td style={{ textAlign: 'right', padding: '4px', fontWeight: 500, color: 'var(--brio-text)' }}>{dA(totalArea)}</td>
              <td></td>
              <td style={{ textAlign: 'right', padding: '4px', fontWeight: 700, color: 'var(--brio-gold)', fontSize: '12px' }}>
                {totalFee > 0 ? (fmtCurr ? fmtCurr(totalFee) : totalFee.toLocaleString()) : '—'}
              </td>
            </tr>
            {totalFee > 0 && totalArea > 0 && (
              <tr style={{ borderTop: '1px solid var(--brio-border)' }}>
                <td style={{ padding: '4px', fontWeight: 500, fontSize: '10px', color: 'var(--brio-muted)' }}>Avg Rate</td>
                <td colSpan={2} style={{ textAlign: 'right', padding: '4px', fontSize: '10px', color: 'var(--brio-muted)' }}>per {areaUnit === 'm2' ? 'm²' : 'ft²'}</td>
                <td style={{ textAlign: 'right', padding: '4px', fontWeight: 600, color: 'var(--brio-text)', fontSize: '11px' }}>
                  {fmtCurr ? fmtCurr(totalFee / totalArea) : Math.round(totalFee / totalArea).toLocaleString()}
                </td>
              </tr>
            )}
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Legacy RateAreaTable kept for backward compat (simple category/area/rate rows)
function RateAreaTable({ rows, onUpdateRow, onAddRow, onRemoveRow, fmtCurr }) {
  const total = (rows || []).reduce((s, r) => s + rowCalc(r).subtotal, 0);
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--brio-border)' }}>
            <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: 500 }}>Category</th>
            <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 500 }}>Area (sq.ft.)</th>
            <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, color: 'var(--brio-muted)', fontSize: '10px' }}>m²</th>
            <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 500 }}>Rate (₹/sq.ft.)</th>
            <th style={{ textAlign: 'right', padding: '3px 4px', fontWeight: 500, color: 'var(--brio-muted)', fontSize: '10px' }}>₹/m²</th>
            <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: 500 }}>Subtotal</th>
            <th style={{ width: '24px' }}></th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row, rIdx) => {
            const c = rowCalc(row);
            return (
              <tr key={rIdx} style={{ borderBottom: '1px solid var(--brio-border, #f1f5f9)' }}>
                <td style={{ padding: '2px 3px' }}>
                  <input type="text" value={row.category || ''} onChange={e => onUpdateRow(rIdx, 'category', e.target.value)}
                    style={{ ...INPUT_STYLE, padding: '2px 5px', fontSize: '11px' }} />
                </td>
                <td style={{ padding: '2px 3px' }}>
                  <input type="number" value={row.areaSqft || ''} onChange={e => onUpdateRow(rIdx, 'areaSqft', e.target.value)}
                    style={{ ...INPUT_STYLE, padding: '2px 5px', fontSize: '11px', textAlign: 'right', maxWidth: '80px' }} />
                </td>
                <td style={{ padding: '2px 3px', textAlign: 'right', color: 'var(--brio-muted)', fontSize: '10px' }}>
                  {c.areaM2 > 0 ? c.areaM2.toFixed(2) : '—'}
                </td>
                <td style={{ padding: '2px 3px' }}>
                  <input type="number" value={row.rateSqft || ''} onChange={e => onUpdateRow(rIdx, 'rateSqft', e.target.value)}
                    style={{ ...INPUT_STYLE, padding: '2px 5px', fontSize: '11px', textAlign: 'right', maxWidth: '80px' }} />
                </td>
                <td style={{ padding: '2px 3px', textAlign: 'right', color: 'var(--brio-muted)', fontSize: '10px' }}>
                  {c.rateM2 > 0 ? c.rateM2.toFixed(2) : '—'}
                </td>
                <td style={{ padding: '2px 6px', textAlign: 'right', fontWeight: 500, color: 'var(--brio-gold)', fontSize: '11px' }}>
                  {c.subtotal > 0 ? (fmtCurr ? fmtCurr(c.subtotal) : c.subtotal.toLocaleString()) : '—'}
                </td>
                <td>
                  <button onClick={() => onRemoveRow(rIdx)} title="Remove"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '13px', padding: '1px' }}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
        <button onClick={onAddRow}
          style={{ fontSize: '11px', padding: '2px 8px', borderRadius: 0, border: '1px solid var(--brio-border)', background: 'transparent', cursor: 'pointer', color: 'var(--brio-text)' }}>
          + Add Row
        </button>
        <div style={{ padding: '4px 8px', fontWeight: 700, fontSize: '12px', borderTop: '2px solid var(--brio-gold)' }}>
          Total: <span style={{ color: 'var(--brio-gold)', marginLeft: '6px' }}>{fmtCurr ? fmtCurr(total) : total.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

// ── AttachmentBriefLink ─────────────────────────────────────────────
function AttachmentBriefLink({ att, getAttachmentUrl }) {
  const [cachedUrl, setCachedUrl] = useState(null);
  const [fetching, setFetching] = useState(false);

  const sizeLabel = att.size
    ? att.size >= 1024 * 1024
      ? (att.size / 1024 / 1024).toFixed(1) + ' MB'
      : Math.round(att.size / 1024) + ' KB'
    : '';

  const isSkipped = att.skipped || (att.storagePath && (att.storagePath.startsWith('skipped:') || att.storagePath.startsWith('error:')));

  const linkStyle = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '11px', color: 'var(--brio-gold)', textDecoration: 'none',
    background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit',
  };
  const mutedStyle = { color: 'var(--brio-grey, #94a3b8)', marginLeft: '2px' };

  if (att.dropboxUrl) {
    return (
      <a href={att.dropboxUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        <Paperclip size={11} />
        {att.filename}
        {sizeLabel && <span style={mutedStyle}>({sizeLabel})</span>}
        <ExternalLink size={10} />
      </a>
    );
  }

  if (isSkipped) {
    return (
      <span style={{ ...linkStyle, color: 'var(--brio-grey, #94a3b8)', cursor: 'default' }}>
        <Paperclip size={11} />
        {att.filename}
        {sizeLabel && <span style={mutedStyle}>({sizeLabel})</span>}
        <span style={{ fontStyle: 'italic', marginLeft: '2px' }}>– not stored</span>
      </span>
    );
  }

  const handleClick = async () => {
    if (cachedUrl) { window.open(cachedUrl, '_blank'); return; }
    setFetching(true);
    const res = await getAttachmentUrl(att.storagePath);
    setFetching(false);
    if (res.success) { setCachedUrl(res.url); window.open(res.url, '_blank'); }
    else alert('Could not load attachment: ' + res.error);
  };

  return (
    <button onClick={handleClick} disabled={fetching} style={linkStyle}>
      <Paperclip size={11} />
      {att.filename}
      {sizeLabel && <span style={mutedStyle}>({sizeLabel})</span>}
      {fetching ? <span style={mutedStyle}>…</span> : <ExternalLink size={10} />}
    </button>
  );
}

// ── FormRow (BRIO 1b) ───────────────────────────────────────────────
function FormRow({ label, hint, children, sectionHeader }) {
  if (sectionHeader) {
    return (
      <tr><td colSpan="2" style={SECTION_HEADER_STYLE}>{label}</td></tr>
    );
  }
  return (
    <tr>
      <td style={{
        padding: '6px 10px', verticalAlign: 'top', whiteSpace: 'nowrap',
        fontWeight: 500, fontSize: '12px', color: 'var(--brio-text, #475569)',
        width: '140px', minWidth: '110px',
      }}>
        {label}
        {hint && <div style={HINT_STYLE}>{hint}</div>}
      </td>
      <td style={{ padding: '4px 10px', verticalAlign: 'top' }}>{children}</td>
    </tr>
  );
}


/**
 * PurposePill — the per-row selection control on Fee Intelligence comparable
 * lists. Click to flip "this project counts toward Recommended Fee" or
 * "...toward Avg Loaded Cost". A row with no pills lit is unincluded; lighting
 * either pill includes the row with that purpose. CTC pills are locked off
 * when the project's status isn't 'completed' (its cost is still accruing).
 */
function PurposePill({ kind, on, locked = false, lockedReason = '', onClick }) {
  const label = kind === 'fee' ? 'Fee' : 'CTC';
  const onBg = kind === 'fee' ? 'rgba(159,144,42,0.20)' : 'rgba(234,88,12,0.16)';
  const onFg = kind === 'fee' ? 'var(--brio-gold, #9F902A)' : '#ea580c';
  const onBorder = kind === 'fee' ? 'rgba(159,144,42,0.55)' : 'rgba(234,88,12,0.55)';
  const title = locked
    ? `Locked: ${lockedReason || 'cannot toggle'}`
    : on
      ? `Counts toward ${kind === 'fee' ? 'Recommended Fee' : 'Avg Loaded Cost'} — click to remove`
      : `Add to ${kind === 'fee' ? 'Recommended Fee' : 'Avg Loaded Cost'}`;
  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      title={title}
      aria-pressed={on}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '10px', fontWeight: 600, fontFamily: 'inherit',
        padding: '2px 8px', minWidth: '34px',
        borderRadius: '3px',
        border: '1px solid ' + (on ? onBorder : 'var(--brio-border, #cbd5e1)'),
        background: on ? onBg : '#fff',
        color: on ? onFg : 'var(--brio-muted, #94a3b8)',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.45 : 1,
        userSelect: 'none', lineHeight: '1.2',
        flexShrink: 0,
      }}
    >
      {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function InquiriesPage({
  currentUser,
  isAdmin,
  activeClients,
  activeProjects,
  useSupabaseDB,
  supabaseClients,
  supabaseInquiries,
  supabaseProjects,
  fmtCurr,
  // (#7) Users list for follow-up attribution dropdown
  activeUsers,
  // Fallback users list (some callers pass users instead of activeUsers)
  users: usersProp,
  // Supabase users hook result (another possible source)
  supabaseUsers: supabaseUsersProp,
  // Admin-configurable priority options (from useSettings)
  priorityOptions: priorityOptionsProp,
  // Contract scopes hook for SOW tab
  contractScopesHook,
  // (#18) Cached per-project loaded cost (totalLoadedCostInr,
  //       projectSize, projectStartYear, projectCountryCode) keyed by
  //       project_id. Written by the Project Profitability page; read
  //       here without recomputing the time-entry × ledger pipeline.
  projectLoadedCosts,
  projectLoadedCostsOldest, // ISO timestamp of the staleest row, for the freshness label
  projectLoadedCostsLoading,
  // Callable that recomputes + writes the cache. Used by the manual
  // "Refresh" button on the Fee Intelligence section so the user can
  // force a refresh without leaving the page.
  refreshProjectLoadedCosts,
  // Opens the pitch deck builder in Comms with this inquiry preselected. Left
  // out by a caller that cannot navigate, and the button hides itself.
  onOpenPitchDeck,
}) {
  // contract_scopes is left out of the initial settings fetch because of its
  // size — the SOW tab needs it, so pull it now.
  const ensureScopes = contractScopesHook?.ensureLoaded;
  useEffect(() => { ensureScopes?.(); }, [ensureScopes]);

  // Build dynamic priority options from admin settings (lowercase for DB compat)
  const dynPriorityLabels = (priorityOptionsProp && priorityOptionsProp.length > 0)
    ? priorityOptionsProp.map(p => p.toLowerCase())
    : PRIORITY_OPTIONS;
  const tableProps = getTablePropsFromCode(TABLE_CODE);
  const formTableProps = getTablePropsFromCode(FORM_CODE);

  // ── Self-contained user fetch ──────────────────────────────────────
  // Call useSupabaseUsers directly so the dropdown works regardless of
  // which props the parent passes.  Prop-based lists are still tried first
  // so the parent can override if desired.
  const ownSupabaseUsers = useSupabaseUsers();

  const reliableUsers = useMemo(() => {
    const src =
      (activeUsers && activeUsers.length > 0) ? activeUsers :
      (usersProp && usersProp.length > 0) ? usersProp :
      (supabaseUsersProp?.users && supabaseUsersProp.users.length > 0) ? supabaseUsersProp.users :
      (ownSupabaseUsers?.users && ownSupabaseUsers.users.length > 0) ? ownSupabaseUsers.users :
      [];
    const filtered = src.filter(u => u && (u.name || u.full_name || u.email));
    // Ensure currentUser is always in the list
    if (currentUser?.id && !filtered.find(u => String(u.id) === String(currentUser.id))) {
      filtered.unshift(currentUser);
    }
    return filtered;
  }, [activeUsers, usersProp, supabaseUsersProp, ownSupabaseUsers, currentUser]);

  // ── State ─────────────────────────────────────────────────────────
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('inquiryDate');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [drawerSection, setDrawerSection] = useState('client');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [editDraft, setEditDraft] = useState({});
  // Live-draft + last-persisted snapshot refs power the Comms-style on-blur /
  // before-unload autosave (see flushDraft). Refs (not state) so unload
  // handlers and blur callbacks always read the newest values.
  const editDraftRef = useRef({});
  const savedSnapshotRef = useRef({});
  useEffect(() => { editDraftRef.current = editDraft; }, [editDraft]);
  // Drawer autosave status: 'idle' | 'saving' | 'saved' | 'error'
  const [autosaveState, setAutosaveState] = useState('idle');
  const [followups, setFollowups] = useState([]);
  const [newFollowup, setNewFollowup] = useState({ followupDate: '', notes: '' });

  // ── XLSX Import/Export state ──────────────────────────────────────
  const xlsxFileRef = useRef(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportSheets, setExportSheets] = useState({ area: true, scenarios: true, offers: true });
  const [xlsxImporting, setXlsxImporting] = useState(false);
  const [xlsxErrors, setXlsxErrors] = useState(null);

  // ── Smart sort ────────────────────────────────────────────────────
  const [sortRules, setSortRules] = useState(() => {
    try { return JSON.parse(localStorage.getItem(INQ_SORT_LS_KEY)) || []; }
    catch { return []; }
  });
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [showCalcPanel, setShowCalcPanel] = useState(false);
  // Pipeline performance scope: 'all' inquiries vs only those with a first response.
  const [calcFunnelMode, setCalcFunnelMode] = useState(() => {
    try {
      const saved = localStorage.getItem(INQ_CALC_MODE_LS_KEY);
      return INQ_FUNNEL_MODE_KEYS.includes(saved) ? saved : 'all';
    } catch { return 'all'; }
  });
  useEffect(() => {
    localStorage.setItem(INQ_SORT_LS_KEY, JSON.stringify(sortRules));
  }, [sortRules]);
  useEffect(() => {
    try { localStorage.setItem(INQ_CALC_MODE_LS_KEY, calcFunnelMode); } catch { /* ignore */ }
  }, [calcFunnelMode]);

  // (#12) Fee Recommendation Model ────────────────────────────────────
  const feeModelsHook = useSupabaseFeeModels();

  // ── Email Intake hook ─────────────────────────────────────────────────
  const intakeHook = useSupabaseInquiryIntake();

  const [feeModels, setFeeModels]                   = useState([]);
  const [activeFeeModelId, setActiveFeeModelId]     = useState(null);
  const [feeComparables, setFeeComparables]         = useState([]);
  const [feeFactors, setFeeFactors]                 = useState({});  // { [key]: { value, weight } }
  const [feeComputed, setFeeComputed]               = useState(null);
  const [feeLoading, setFeeLoading]                 = useState(false);
  const [feeError, setFeeError]                     = useState(null);  // (#E) inline error display
  const [compSort, setCompSort]                     = useState({ key: 'similarity', dir: 'desc' });
  const [feeFactorDefs, setFeeFactorDefs]           = useState([]);   // from fee_factor_definitions table
  const [feeManualSearch, setFeeManualSearch]       = useState('');
  const [inflationYear, setInflationYear]           = useState(null);  // latest CPI year from DB
  const feeDebounceRef = useRef(null);

  // ── Inquiry conversion probability (Bayesian) ────────────────────────
  const [inquiryConvProb, setInquiryConvProb]       = useState(null); // { probability, n_total, n_converted, confidence, method, bucket }
  // Inflation-adjusted Avg Loaded Cost for the active fee model
  // (compute_loaded_cost_metrics RPC). Refetched on model change and
  // after a comparable purpose flip that touches CTC.
  const [loadedCostMetrics, setLoadedCostMetrics] = useState(null);
  const [loadedCostMetricsLoading, setLoadedCostMetricsLoading] = useState(false);
  // Refresh-button state: tracks the manual-refresh round-trip.
  const [refreshingCosts, setRefreshingCosts] = useState(false);
  const [refreshCostsError, setRefreshCostsError] = useState(null);
  const [refreshCostsLastResult, setRefreshCostsLastResult] = useState(null);
  const [convProbLoading, setConvProbLoading]        = useState(false);

  // ── Project linking state ────────────────────────────────────────────
  const [linkProjectSearch, setLinkProjectSearch]    = useState('');
  const [linkingProject, setLinkingProject]          = useState(false);
  const [linkConflict, setLinkConflict]              = useState(null);  // { existingInquiryId } if conflict

  // ── Intake attachments (for brief drawer) ────────────────────────────
  const [intakeAttachments, setIntakeAttachments]    = useState([]);

  // Resolve user display name with fallbacks (matches other pages like HFB, hiring, etc.)
  const getUserDisplayName = useCallback((u) => u?.full_name || u?.name || u?.email || '—', []);

  // (#13) Initialize follow-up "by" to current employee when not set
  useEffect(() => {
    if (currentUser?.id && !newFollowup.followupByEmployeeId) {
      setNewFollowup(f => ({
        ...f,
        followupByEmployeeId: currentUser.id,
        followupByName: getUserDisplayName(currentUser),
      }));
    }
  }, [currentUser, newFollowup.followupByEmployeeId, getUserDisplayName]);
  // (#1) Fullscreen mode - mirrors TeamMemberFullScreenPage pattern
  const [isFullscreen, setIsFullscreen] = useState(false);

  // (#1) Fullscreen: lock body scroll when in fullscreen mode
  useEffect(() => {
    if (!isFullscreen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [isFullscreen]);

  // (#1) Fullscreen: Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  // ── Derived lists ─────────────────────────────────────────────────
  const allInquiries = supabaseInquiries?.inquiries || [];

  // ── Client helpers (must be above filteredList to avoid TDZ crash) ──
  const getClientName = useCallback((inq) => {
    if (!inq) return '—';
    if (inq.clientId) {
      const c = (activeClients || []).find(x => x.id === inq.clientId || String(x.id) === String(inq.clientId));
      if (c) return c.name || c.organisation || '—';
    }
    return inq.clientNameOverride || inq.clientOrgNameOverride || '—';
  }, [activeClients]);

  const getClient = useCallback((inq) => {
    if (!inq || !inq.clientId) return null;
    return (activeClients || []).find(x => x.id === inq.clientId || String(x.id) === String(inq.clientId)) || null;
  }, [activeClients]);

  // (#3) Filter using the four-state status model
  const filteredList = useMemo(() => {
    let list = (allInquiries || []).map(inq => ({
      ...inq,
      _status: deriveInquiryStatus(inq),
      _priority: migratePriority(inq.priority),
    }));

    // Tab filter
    list = list.filter(i => i._status === tab);

    // Exclude email-intake inquiries that haven't been reviewed yet (still in needs_review state)
    // They should only appear in the active list after being approved via the email intake section.
    list = list.filter(i => i.platformDetail !== 'gmail_intake');

    // (#14) Search filter — crash-proofed with try-catch + String() coercion
    if (search.trim()) {
      const q = String(search || '').toLowerCase();
      list = list.filter(i => {
        try {
          const clientName = String(getClientName(i) || '').toLowerCase();
          const city = String(i.locationCity || '').toLowerCase();
          const pType = String(i.projectType || '').toLowerCase();
          const platform = String(i.platformType || i.platform || '').toLowerCase();
          const nextSteps = String(i.nextSteps || '').toLowerCase();
          const org = String(i.clientOrgNameOverride || '').toLowerCase();
          const nameOvr = String(i.clientNameOverride || '').toLowerCase();
          const notes = String(i.briefNotes || '').toLowerCase();
          return clientName.includes(q) ||
            city.includes(q) ||
            pType.includes(q) ||
            platform.includes(q) ||
            nextSteps.includes(q) ||
            org.includes(q) ||
            nameOvr.includes(q) ||
            notes.includes(q);
        } catch (_filterErr) {
          // (#14) Never crash from search — silently include row on error
          return true;
        }
      });
    }
    // Smart sort rules (take precedence over column header sort when rules are set)
    if (sortRules.length > 0) {
      return applyInqSorts(list, sortRules, getClientName);
    }

    list = [...list].sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'client':
          va = (getClientName(a) || '').toLowerCase();
          vb = (getClientName(b) || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'inquiryDate':
          va = a.inquiryDate || '';
          vb = b.inquiryDate || '';
          break;
        case 'nextFollowup':
          va = a.nextFollowupDate || '';
          vb = b.nextFollowupDate || '';
          break;
        case 'overdue':
          va = getOverdueDays(a);
          vb = getOverdueDays(b);
          break;
        case 'priority': {
          va = PRIORITY_SORT_ORDER[a._priority] || 0;
          vb = PRIORITY_SORT_ORDER[b._priority] || 0;
          break;
        }
        case 'potential': {
          va = parseInt(a.potential) || 0;
          vb = parseInt(b.potential) || 0;
          break;
        }
        case 'projectType':
          va = (a.projectType || '').toLowerCase();
          vb = (b.projectType || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'location':
          va = (a.locationCity || '').toLowerCase();
          vb = (b.locationCity || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        case 'estimatedFee':
          va = parseFloat(a.estimatedFee) || 0;
          vb = parseFloat(b.estimatedFee) || 0;
          break;
        case 'estimatedStartDate':
          va = a.estimatedStartDate || '';
          vb = b.estimatedStartDate || '';
          break;
        case 'nextSteps':
          va = (a.nextSteps || '').toLowerCase();
          vb = (b.nextSteps || '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        default:
          va = ''; vb = '';
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [allInquiries, tab, search, sortBy, sortDir, getClientName, sortRules]);

  // Pipeline funnel list: all inquiries across all statuses (excluding gmail intake drafts).
  // Excludes any inquiry whose last follow-up date is before 2026-01-01 — those are stale
  // records from before the system was actively used and would skew conversion rates.
  // Inquiries with no follow-up date are kept (they may be newly entered).
  // EXCEPTION: converted inquiries are real, successful outcomes and are always kept, even
  // when their follow-up date predates the cutoff. Several wins were imported from before the
  // app existed and carry old (or empty) follow-up dates; dropping them made the Appointment
  // stage report fewer conversions than the "Converted" tab badge (e.g. 3 vs 9).
  const funnelBaseList = useMemo(() => {
    const cutoff = new Date('2026-01-01');
    return (allInquiries || []).filter(i => {
      if (i.platformDetail === 'gmail_intake') return false;
      if (i.status === 'converted' || i.converted === true) return true;
      if (i.lastFollowupDate && new Date(i.lastFollowupDate) < cutoff) return false;
      return true;
    });
  }, [allInquiries]);

  // (#3) Counts per tab for badges
  const tabCounts = useMemo(() => {
    const counts = { active: 0, not_converted: 0, converted: 0, archived: 0 };
    allInquiries.forEach(inq => {
      if (inq.platformDetail === 'gmail_intake') return; // exclude unreviewed intake inquiries
      const s = deriveInquiryStatus(inq);
      if (counts[s] !== undefined) counts[s]++;
    });
    return counts;
  }, [allInquiries]);

  // ── Client field mapping (linked client vs inquiry overrides) ──────
  const CLIENT_FIELD_MAP = {
    name:             { clientKey: 'name',             inquiryKey: 'clientNameOverride',                dbClientKey: 'name',             dbInquiryKey: 'clientNameOverride' },
    organisationName: { clientKey: 'organisation',    inquiryKey: 'clientOrgNameOverride',             dbClientKey: 'organisation',     dbInquiryKey: 'clientOrgNameOverride' },
    phone:            { clientKey: 'mobile',           inquiryKey: 'clientPhoneOverride',               dbClientKey: 'mobile',           dbInquiryKey: 'clientPhoneOverride' },
    email:            { clientKey: 'primaryEmail',     inquiryKey: 'clientEmailOverride',               dbClientKey: 'primary_email',    dbInquiryKey: 'clientEmailOverride' },
    address:          { clientKey: 'address',          inquiryKey: 'clientAddressOverride',             dbClientKey: 'address',          dbInquiryKey: 'clientAddressOverride' },
    linkedinProfile:  { clientKey: 'linkedinUrl',      inquiryKey: 'clientLinkedinOverride',            dbClientKey: 'linkedin_url',     dbInquiryKey: 'clientLinkedinOverride' },
    interestingLinks: { clientKey: 'likesDislikes',    inquiryKey: 'clientInterestingLinksOverride',    dbClientKey: 'likes_dislikes',   dbInquiryKey: 'clientInterestingLinksOverride' },
    personalNotes:    { clientKey: 'likesDislikes',    inquiryKey: 'clientPersonalNotesOverride',       dbClientKey: 'likes_dislikes',   dbInquiryKey: 'clientPersonalNotesOverride' },
  };

  // hasLinkedClient: true when inquiry has a clientId AND we found the client record
  const linkedClient = editDraft.clientId ? getClient(editDraft) : null;
  const hasLinkedClient = Boolean(editDraft.clientId && linkedClient);

  // Temp key map for optimistic local editing of linked client fields
  const CLIENT_TEMP_KEYS = {
    name: '_clientName',
    organisationName: '_clientOrg',
    phone: '_clientPhone',
    email: '_clientEmail',
    address: '_clientAddr',
    linkedinProfile: '_clientLi',
    interestingLinks: '_clientLinks',
    personalNotes: '_clientNotes',
  };

  const getClientField = useCallback((fieldKey) => {
    const map = CLIENT_FIELD_MAP[fieldKey];
    if (!map) return '';
    if (hasLinkedClient) {
      // Prefer local temp value (optimistic) over stale linked record
      const tempKey = CLIENT_TEMP_KEYS[fieldKey];
      if (tempKey && editDraft[tempKey] !== undefined) return editDraft[tempKey];
      return linkedClient[map.clientKey] || '';
    }
    return editDraft[map.inquiryKey] || '';
  }, [hasLinkedClient, linkedClient, editDraft]);

  const setClientField = useCallback(async (fieldKey, value) => {
    const map = CLIENT_FIELD_MAP[fieldKey];
    if (!map) return;
    if (hasLinkedClient) {
      // Save to clients table, then clear temp key after refetch completes
      if (supabaseClients?.updateClient) {
        const res = await supabaseClients.updateClient(linkedClient.id, { [map.dbClientKey]: value });
        if (res.success) {
          const tempKey = CLIENT_TEMP_KEYS[fieldKey];
          if (tempKey) setEditDraft(d => { const nd = { ...d }; delete nd[tempKey]; return nd; });
        } else {
          console.error('Client field save failed:', res.error);
        }
      }
    } else {
      // Save to inquiry override field
      setEditDraft(d => ({ ...d, [map.inquiryKey]: value }));
    }
  }, [hasLinkedClient, linkedClient, supabaseClients]);

  // ── Expand / Drawer ───────────────────────────────────────────────
  const openDrawer = async (inq) => {
    // Persist any pending edits from the currently-open drawer before we switch
    // records or collapse — mirrors Comms' commit-before-leaving behavior.
    await flushDraft();
    if (expandedId === inq.id) { setExpandedId(null); return; }
    setExpandedId(inq.id);
    setDrawerSection('client');
    // Clone inquiry into editDraft; clear any stale temp keys
    const draft = { ...inq };
    Object.keys(draft).forEach(k => { if (k.startsWith('_client')) delete draft[k]; });
    // Priority: computed (offer → scenario) > manual estimatedFee
    const autoFee = computeLatestOfferTotal(inq);
    if (autoFee) {
      draft.estimatedFee = autoFee;
      draft._estimatedFeeAutoFilled = true;
    } else {
      draft._estimatedFeeAutoFilled = false;
    }
    setEditDraft(draft);
    // Reset the autosave baseline to the freshly-opened record so the first
    // real edit (not the initial clone/auto-fill) is what triggers a write.
    editDraftRef.current = draft;
    savedSnapshotRef.current = inquiryDraftSnapshot(draft);
    setAutosaveState('idle');
    // Load followups
    if (supabaseInquiries?.fetchFollowups) {
      const res = await supabaseInquiries.fetchFollowups(inq.id);
      setFollowups(res.success ? res.data : []);
    }
    setNewFollowup({
      followupDate: new Date().toISOString().slice(0, 10),
      notes: '',
      // (#13) Pre-fill attribution with employee BIGINT ID (not auth UUID)
      followupByEmployeeId: currentUser?.id || null,
      followupByName: getUserDisplayName(currentUser) || '',
    });
    // Reset link state
    setLinkProjectSearch('');
    setLinkConflict(null);
  };

  // ── Save inline field ─────────────────────────────────────────────
  const saveField = async (id, field, value) => {
    const res = await supabaseInquiries.updateInquiry(id, { [field]: value });
    if (!res.success) alert('Save failed: ' + res.error);
  };

  // ── Autosave drawer edits (Comms-style: on blur / before page unload) ──
  // Diffs the live draft against the last-persisted snapshot and writes only
  // the changed whitelisted fields. A no-op when nothing changed, so it is safe
  // to call redundantly (every field blur, record/tab switch, page unload).
  // Reads from refs so unload handlers always see the latest values.
  const flushDraft = useCallback(async () => {
    const d = editDraftRef.current;
    if (!d || !d.id) return { success: true };
    const snap = savedSnapshotRef.current || {};
    const changes = {};
    for (const key of INQUIRY_AUTOSAVE_FIELDS) {
      if (!inquiryValueEqual(d[key], snap[key])) changes[key] = d[key];
    }
    if (Object.keys(changes).length === 0) return { success: true };
    const prevSnap = snap;
    // Advance the snapshot up-front (deep-copied) so an overlapping flush — or a
    // later in-place edit of a saved array — doesn't re-send or get missed.
    savedSnapshotRef.current = { ...snap, ...inquiryDraftSnapshot({ ...snap, ...changes }) };
    setAutosaveState('saving');
    const res = await supabaseInquiries.updateInquiry(d.id, changes);
    if (res && res.success) {
      setAutosaveState('saved');
    } else {
      savedSnapshotRef.current = prevSnap;  // revert so a later attempt retries
      setAutosaveState('error');
      console.error('[INQUIRIES] autosave failed:', res && res.error);
    }
    return res || { success: false };
  }, [supabaseInquiries]);

  // Flush pending drawer edits before the page unloads or is hidden, so a
  // refresh / tab-close doesn't drop them (best-effort, like the Comms page).
  useEffect(() => {
    const flush = () => { flushDraft(); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushDraft(); };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushDraft]);

  // ── Add inquiry ───────────────────────────────────────────────────
  const handleAdd = async () => {
    setSaving(true);
    const payload = {
      clientId: addForm.clientId || null,
      clientOrgNameOverride: addForm.clientId ? null : (addForm.orgName || null),
      inquiryDate: addForm.inquiryDate || new Date().toISOString().slice(0, 10),
      platformType: addForm.platformType || 'other',
      platformDetail: addForm.platformDetail || '',
      potential: addForm.potential || '50',
      // (#6) Categorical priority
      priority: addForm.priority || 'medium',
      projectType: addForm.projectType || '',
      locationCity: addForm.locationCity || '',
      // (#3) New inquiries always start as 'active'
      status: 'active',
    };
    const res = await supabaseInquiries.createInquiry(payload);
    setSaving(false);
    if (res.success) { setShowAddModal(false); setAddForm({}); }
    else alert('Error: ' + res.error);
  };

  // ── Create client from inquiry ────────────────────────────────────
  const createClientAndLink = async (inq) => {
    if (!supabaseClients?.createClient) return;
    const orgName = inq.clientOrgNameOverride || 'New Client';
    const payload = {
      name: inq.clientNameOverride || orgName,
      organisation: orgName,
      email: inq.clientEmailOverride || '',
      primary_email: inq.clientEmailOverride || '',
      mobile: inq.clientPhoneOverride || '',
      address: inq.clientAddressOverride || '',
      linkedin_url: inq.clientLinkedinOverride || '',
      instagram_url: '',
      likes_dislikes: [inq.clientInterestingLinksOverride, inq.clientPersonalNotesOverride].filter(Boolean).join('\n') || '',
      invoicingName: inq.invoicingName || null,
      invoiceAddress: inq.invoicingAddress || null,
      // (#2) Mark client as inquiry-sourced - hidden from ClientsPage until conversion
      origin: 'inquiry',
      isConverted: false,
      inquiryId: inq.id,
    };
    setSaving(true);
    const res = await supabaseClients.createClient(payload);
    if (res.success && res.data) {
      // Clear overrides and link to new client
      const clearOverrides = {
        clientId: res.data.id,
        clientNameOverride: '',
        clientOrgNameOverride: '',
        clientPhoneOverride: '',
        clientEmailOverride: '',
        clientAddressOverride: '',
        clientLinkedinOverride: '',
        clientInterestingLinksOverride: '',
        clientPersonalNotesOverride: '',
      };
      await supabaseInquiries.updateInquiry(inq.id, clearOverrides);
      setEditDraft(d => ({ ...d, ...clearOverrides }));
    } else {
      alert('Failed to create client: ' + (res.error || 'Unknown'));
    }
    setSaving(false);
  };

  // ── Link existing client (from dropdown) ──────────────────────────
  const linkClient = async (clientIdStr) => {
    const cid = clientIdStr || null;
    await supabaseInquiries.updateInquiry(editDraft.id, { clientId: cid });
    setEditDraft(d => ({ ...d, clientId: cid }));
    if (cid) {
      const c = activeClients.find(x => String(x.id) === String(cid));
      if (c) {
        // Silently migrate non-empty overrides into client where client field is blank
        const migrated = {};
        const MIGRATE = [
          { override: 'clientNameOverride',                clientDb: 'name',             clientApp: 'name' },
          { override: 'clientOrgNameOverride',             clientDb: 'organisation',     clientApp: 'organisation' },
          { override: 'clientPhoneOverride',               clientDb: 'mobile',           clientApp: 'mobile' },
          { override: 'clientEmailOverride',               clientDb: 'primary_email',    clientApp: 'primaryEmail' },
          { override: 'clientAddressOverride',             clientDb: 'address',          clientApp: 'address' },
          { override: 'clientLinkedinOverride',            clientDb: 'linkedin_url',     clientApp: 'linkedinUrl' },
          { override: 'clientInterestingLinksOverride',    clientDb: 'likes_dislikes',   clientApp: 'likesDislikes' },
          { override: 'clientPersonalNotesOverride',       clientDb: 'likes_dislikes',   clientApp: 'likesDislikes' },
        ];
        for (const m of MIGRATE) {
          const overrideVal = editDraft[m.override];
          if (overrideVal && !c[m.clientApp]) {
            migrated[m.clientDb] = overrideVal;
          }
        }
        if (Object.keys(migrated).length > 0 && supabaseClients?.updateClient) {
          await supabaseClients.updateClient(c.id, migrated);
        }
        // Clear overrides on inquiry
        const clearOverrides = {
          clientNameOverride: '',
          clientOrgNameOverride: '',
          clientPhoneOverride: '',
          clientEmailOverride: '',
          clientAddressOverride: '',
          clientLinkedinOverride: '',
          clientInterestingLinksOverride: '',
          clientPersonalNotesOverride: '',
        };
        await supabaseInquiries.updateInquiry(editDraft.id, clearOverrides);
        setEditDraft(d => ({ ...d, ...clearOverrides }));
      }
    }
  };

  // (#3) Status change handler - replaces legacy toggleArchive
  const changeStatus = async (inq, newStatus) => {
    if (newStatus === 'converted') {
      setShowConvertConfirm(inq);
      return;
    }
    const updates = {
      status: newStatus,
      archived: newStatus === 'archived',
      converted: false,
    };
    await supabaseInquiries.updateInquiry(inq.id, updates);
  };

  // ── Convert → Create Project ──────────────────────────────────────
  const handleConvert = async (inq) => {
    setSaving(true);
    let clientId = inq.clientId;
    if (!clientId && inq.clientOrgNameOverride && supabaseClients?.createClient) {
      const cr = await supabaseClients.createClient({
        name: inq.clientNameOverride || inq.clientOrgNameOverride,
        organisation: inq.clientOrgNameOverride,
        email: inq.clientEmailOverride || '',
        primary_email: inq.clientEmailOverride || '',
        mobile: inq.clientPhoneOverride || '',
        address: inq.clientAddressOverride || '',
        linkedin_url: inq.clientLinkedinOverride || '',
        likes_dislikes: [inq.clientInterestingLinksOverride, inq.clientPersonalNotesOverride].filter(Boolean).join('\n') || '',
        invoicingName: inq.invoicingName || null,
        invoiceAddress: inq.invoicingAddress || null,
      });
      if (cr.success && cr.data) {
        clientId = cr.data.id;
        await supabaseInquiries.updateInquiry(inq.id, {
          clientId: cr.data.id,
          clientNameOverride: '', clientOrgNameOverride: '', clientPhoneOverride: '', clientEmailOverride: '',
          clientAddressOverride: '', clientLinkedinOverride: '',
          clientInterestingLinksOverride: '', clientPersonalNotesOverride: '',
        });
      }
    }

    // (#2) On conversion, mark the linked client as converted
    // so it becomes visible on ClientsPage, and copy invoicing fields from inquiry
    if (clientId && supabaseClients?.updateClient) {
      const conversionUpdates = { isConverted: true };
      if (inq.invoicingName) conversionUpdates.invoicingName = inq.invoicingName;
      if (inq.invoicingAddress) conversionUpdates.invoiceAddress = inq.invoicingAddress;
      await supabaseClients.updateClient(clientId, conversionUpdates);
    }

    const projName = `${getClientName(inq)} - ${inq.projectType || 'New Project'}`;
    // The inquiry's Location is free text; the registry keeps city and country
    // apart, so it is split rather than copied. Only ever a starting value —
    // both fields are editable in place on the projects overview.
    const { city, country } = parseInquiryLocation(inq.locationCity);
    const projData = {
      name: projName,
      clientId: clientId ? parseInt(clientId) : null,
      category: inq.projectType || '',
      status: 'active',
      budget: 0,
      projectSize: 0,
      plotArea: inq.plotArea ?? null,
      city,
      country,
    };
    let projId = null;
    if (supabaseProjects?.createProject) {
      const pr = await supabaseProjects.createProject(projData);
      if (pr.success && pr.data) {
        projId = pr.data.id;
        // Set reverse link: project → inquiry
        if (supabaseProjects?.updateProject) {
          await supabaseProjects.updateProject(projId, { sourceInquiryId: inq.id });
        }
      }
    }
    await supabaseInquiries.updateInquiry(inq.id, {
      status: 'converted',
      converted: true,
      convertedProjectId: projId,
    });
    if (projId && contractScopesHook?.relinkScopeToProject) {
      contractScopesHook.relinkScopeToProject(inq.id, projId);
    }
    setSaving(false);
    setShowConvertConfirm(null);
    if (projId) alert(`Project "${projName}" created successfully.`);
  };

  // ── Link/Unlink inquiry to existing project (post-hoc) ─────────────
  const handleLinkProject = async (projectId) => {
    if (!editDraft?.id || !projectId) return;
    setLinkingProject(true);
    setLinkConflict(null);
    const res = await feeModelsHook.linkInquiryToProject(editDraft.id, projectId);
    if (res.success) {
      if (editDraft?.id && contractScopesHook?.relinkScopeToProject) {
        contractScopesHook.relinkScopeToProject(editDraft.id, projectId);
      }
      setEditDraft(d => ({
        ...d,
        convertedProjectId: projectId,
        status: 'converted',
        converted: true,
      }));
      setLinkProjectSearch('');
      // Refresh inquiries list
      supabaseInquiries?.refetch?.();
    } else if (res.error === 'conflict') {
      setLinkConflict(res);
    } else {
      alert('Link failed: ' + (res.error || 'Unknown error'));
    }
    setLinkingProject(false);
  };

  const handleForceLink = async (projectId) => {
    if (!editDraft?.id || !projectId) return;
    setLinkingProject(true);
    try {
      // Clear old project link first (set source_inquiry_id to null)
      if (supabaseProjects?.updateProject) {
        await supabaseProjects.updateProject(projectId, { sourceInquiryId: null });
      }
      // Now re-try the normal link flow
      const res = await feeModelsHook.linkInquiryToProject(editDraft.id, projectId);
      if (res.success) {
        if (editDraft?.id && contractScopesHook?.relinkScopeToProject) {
          contractScopesHook.relinkScopeToProject(editDraft.id, projectId);
        }
        setEditDraft(d => ({
          ...d,
          convertedProjectId: projectId,
          status: 'converted',
          converted: true,
        }));
        setLinkConflict(null);
        setLinkProjectSearch('');
        supabaseInquiries?.refetch?.();
      } else {
        alert('Force link failed: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Force link failed: ' + err.message);
    }
    setLinkingProject(false);
  };

  const handleUnlinkProject = async () => {
    if (!editDraft?.id || !editDraft.convertedProjectId) return;
    setLinkingProject(true);
    const res = await feeModelsHook.unlinkInquiryFromProject(editDraft.id, editDraft.convertedProjectId);
    if (res.success) {
      setEditDraft(d => ({ ...d, convertedProjectId: null }));
      supabaseInquiries?.refetch?.();
    } else {
      alert('Unlink failed: ' + (res.error || 'Unknown error'));
    }
    setLinkingProject(false);
  };

  // ── Fetch intake attachments when inquiry is expanded ────────────
  useEffect(() => {
    if (!expandedId) { setIntakeAttachments([]); return; }
    let cancelled = false;
    intakeHook.fetchAttachmentsForInquiry(expandedId).then(atts => {
      if (!cancelled) setIntakeAttachments(atts);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // ── Fetch conversion probability when inquiry is expanded ──────────
  // NOTE: Only depend on expandedId — feeModelsHook functions are recreated
  // every render by useSupabaseFeeModels(), so including them causes an
  // infinite request loop.
  useEffect(() => {
    if (!expandedId) {
      setInquiryConvProb(null);
      return;
    }
    if (!feeModelsHook?.computeInquiryConversionProbability) return;
    let cancelled = false;
    setConvProbLoading(true);
    feeModelsHook.computeInquiryConversionProbability(expandedId).then(res => {
      if (!cancelled) {
        setInquiryConvProb(res.success ? res.data : null);
        setConvProbLoading(false);
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // ── Follow-up handlers ────────────────────────────────────────────
  // (#7) Follow-ups now include userId + displayName attribution
  const [followupError, setFollowupError] = useState(null);
  const handleAddFollowup = async () => {
    if (!expandedId) return;
    if (!newFollowup.followupDate) {
      setFollowupError('Please select a date');
      return;
    }
    setFollowupError(null);
    try {
      const payload = {
        followupDate: newFollowup.followupDate,
        notes: newFollowup.notes || '',
        followupByName: newFollowup.followupByName || getUserDisplayName(currentUser) || '',
        // (#13) Pass employee BIGINT ID for dropdown matching.
        // Don't pass followupByUserId — the hook defaults to session UUID.
        followupByEmployeeId: newFollowup.followupByEmployeeId || currentUser?.id || null,
      };
      const res = await supabaseInquiries.addFollowup(expandedId, payload);
      if (res.success) {
        setFollowups(prev => [res.data, ...prev]);
        setNewFollowup({
          followupDate: new Date().toISOString().slice(0, 10),
          notes: '',
          followupByEmployeeId: currentUser?.id || null,
          followupByName: getUserDisplayName(currentUser) || '',
        });
        // Refresh inquiry list so last_followup_date in the main table reflects the new entry
        supabaseInquiries?.refetch?.();
      } else {
        setFollowupError(typeof res.error === 'string' ? res.error : 'Failed to save follow-up');
      }
    } catch (err) {
      console.error('addFollowup error:', err);
      setFollowupError(err?.message || 'Failed to save follow-up');
    }
  };
  const handleDeleteFollowup = async (fid) => {
    const res = await supabaseInquiries.deleteFollowup(fid);
    if (res.success) {
      setFollowups(prev => prev.filter(f => f.id !== fid));
      // Refresh so last_followup_date in the main table shows the new max (or null)
      supabaseInquiries?.refetch?.();
    }
  };

  // (#5) Last follow-up date comes from the inquiry record (kept in sync via refetch after add/delete).
  // Do NOT fall back to the local followups array — that causes the inline table picker to be masked
  // by the open drawer's stale followup list, making edits appear not to persist.
  const getLastFollowupDate = useCallback((inq) => {
    return inq.lastFollowupDate || inq.last_followup_date || null;
  }, []);

  // ── (#12) Fee Model helpers ─────────────────────────────────────────

  /** Safely get numeric project size (handles both camelCase and snake_case) */
  const getProjSize = useCallback((p) => {
    return parseFloat(p?.projectSize || p?.project_size) || 0;
  }, []);

  /** Score a project for similarity to the current inquiry */
  const scoreFeeComparable = useCallback((proj, inq) => {
    const pSize = getProjSize(proj);
    if (!proj || !proj.budget || proj.budget <= 0 || pSize <= 0) return -1;
    let score = 10; // base for having valid data
    // Type match
    const pType = (proj.category || proj.projectType || proj.project_type || '').toLowerCase();
    const iType = (inq.projectType || '').toLowerCase();
    if (pType && iType && pType === iType)                         score += 30;
    else if (pType && iType && pType.includes(iType.slice(0, 4)))  score += 10;
    // City match
    const pCity = (proj.city || proj.locationCity || proj.location_city || '').toLowerCase();
    const iCity = (inq.locationCity || '').toLowerCase();
    if (pCity && iCity && pCity === iCity) score += 20;
    // Country match
    const pCountry = (proj.country || '').toLowerCase();
    const iCountry = (inq.locationCountry || '').toLowerCase();
    if (pCountry && iCountry && pCountry === iCountry) score += 10;
    // Size similarity
    const iArea = parseFloat(inq.scaleArea) || 0;
    if (iArea > 0 && pSize > 0) {
      const ratio = Math.min(iArea, pSize) / Math.max(iArea, pSize);
      score += Math.round(ratio * 20);
    }
    return score;
  }, [getProjSize]);

  /** Load comparables + factors + computed for a single model.
   *  Also fetches fee_factor_definitions once (cached in feeFactorDefs). */
  const loadFeeModelData = useCallback(async (modelId) => {
    if (!modelId) return;
    setFeeLoading(true);
    try {
      const needDefs = feeFactorDefs.length === 0;
      const promises = [
        feeModelsHook.fetchComparables(modelId),
        feeModelsHook.fetchFactors(modelId),
        feeModelsHook.computeFeeModel(modelId),
      ];
      if (needDefs) promises.push(feeModelsHook.fetchFeeFactorDefinitions());

      const results = await Promise.all(promises);
      const [compR, factR, computeR] = results;

      if (compR.success)    setFeeComparables(compR.data);
      if (factR.success)    setFeeFactors(feeModelsHook.normalizeFactorsMap(factR.data));
      if (computeR.success) setFeeComputed(computeR.data);
      if (needDefs && results[3]?.success) setFeeFactorDefs(results[3].data);
    } catch (err) {
      console.error('[FeeModel] loadFeeModelData error:', err);
    } finally { setFeeLoading(false); }
  }, [feeModelsHook, feeFactorDefs.length]);

  /** Fetch all models for the currently expanded inquiry */
  const loadFeeModelsForInquiry = useCallback(async (inquiryId) => {
    if (!inquiryId) return;
    const res = await feeModelsHook.fetchFeeModelsForInquiry(inquiryId);
    if (res.success) {
      setFeeModels(res.data);
      if (res.data.length > 0 && !activeFeeModelId) {
        setActiveFeeModelId(res.data[0].id);
        loadFeeModelData(res.data[0].id);
      }
    }
  }, [feeModelsHook, activeFeeModelId, loadFeeModelData]);

  /** Create a new fee model for the open inquiry */
  /** Create a new fee model version (RPC deactivates previous, seeds factors) */
  const handleCreateFeeModel = useCallback(async () => {
    if (!expandedId) return;
    setFeeLoading(true);
    setFeeError(null);
    const inq = editDraft;
    try {
      const res = await feeModelsHook.createFeeModel({
        inquiryId: expandedId,
        name: `Model ${feeModels.length + 1}`,
        currency: 'INR',
        unit: 'sqm',
        inquiryProjectType:      inq.projectType || inq.project_type || null,
        inquiryLocationCountry:  inq.countryCode || inq.locationCountry || null,
        inquiryLocationCity:     inq.locationCity || null,
        inquiryScaleArea:        null,
        inquiryScope:            inq.scope || null,
      });
      if (res.success && res.data) {
        // RPC deactivated old models → refetch full list to get updated is_active flags
        const listRes = await feeModelsHook.fetchFeeModelsForInquiry(expandedId);
        if (listRes.success) setFeeModels(listRes.data);
        else setFeeModels(prev => [res.data, ...prev]);
        setActiveFeeModelId(res.data.id);
        await loadFeeModelData(res.data.id);
        setFeeError(null);
      } else {
        setFeeError(res.error || 'Failed to create fee model');
        console.error('[FeeModel] createFeeModel failed:', res.error);
      }
    } catch (err) {
      setFeeError(err?.message || 'Unexpected error creating fee model');
      console.error('[FeeModel] createFeeModel exception:', err);
    } finally {
      setFeeLoading(false);
    }
  }, [expandedId, editDraft, feeModels.length, feeModelsHook, loadFeeModelData]);

  /** Refetch the inflation-adjusted Avg Loaded Cost for the active model.
   *  Lightweight RPC (no compute, just a join + aggregate over cached cost
   *  rows). Called when the model changes, when a CTC pill flips, and
   *  after a manual cache refresh. */
  const refetchLoadedCostMetrics = useCallback(async () => {
    if (!activeFeeModelId) { setLoadedCostMetrics(null); return; }
    if (!feeModelsHook?.computeLoadedCostMetrics) return;
    setLoadedCostMetricsLoading(true);
    const res = await feeModelsHook.computeLoadedCostMetrics(activeFeeModelId);
    setLoadedCostMetrics(res.success ? res.data : null);
    setLoadedCostMetricsLoading(false);
  // feeModelsHook is recreated each render; only re-bind when the model id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFeeModelId]);

  useEffect(() => {
    refetchLoadedCostMetrics();
  }, [refetchLoadedCostMetrics, projectLoadedCostsOldest]);

  /** Manual "Refresh cost data" handler — drives App.js's
   *  refreshProjectLoadedCosts, then refetches the metric. */
  const handleRefreshCosts = useCallback(async () => {
    if (typeof refreshProjectLoadedCosts !== 'function') return;
    setRefreshingCosts(true);
    setRefreshCostsError(null);
    setRefreshCostsLastResult(null);
    const res = await refreshProjectLoadedCosts({ awaitDataLoads: true });
    if (res?.success) {
      setRefreshCostsLastResult({ at: new Date(), count: res.count || 0 });
      await refetchLoadedCostMetrics();
    } else {
      setRefreshCostsError(res?.error || 'refresh_failed');
    }
    setRefreshingCosts(false);
  }, [refreshProjectLoadedCosts, refetchLoadedCostMetrics]);

  /** Toggle a comparable project include/exclude (used by manual-add search
   *  and the legacy "uninclude" flow). Default purpose is status-aware. */
  const handleToggleComparable = useCallback(async (projectId, include, selectionSource) => {
    if (!activeFeeModelId) return;
    const p = (activeProjects || []).find(
      x => x.id === projectId || String(x.id) === String(projectId));
    const snapshot = p
      ? { name: p.name, budget: p.budget, projectSize: getProjSize(p) }
      : null;
    const isNew = include && !feeComparables.find(c => String(c.project_id) === String(projectId));
    const defaultPurpose = isNew
      ? { fee: true, ctc: (p?.status || '') === 'completed' }
      : undefined;
    await feeModelsHook.upsertComparable({
      modelId: activeFeeModelId,
      projectId,
      selectionSource: selectionSource || 'auto',
      include,
      snapshot,
      purpose: defaultPurpose,
    });
    const [compR, computeR, sugR] = await Promise.all([
      feeModelsHook.fetchComparables(activeFeeModelId),
      feeModelsHook.computeFeeModel(activeFeeModelId),
      feeModelsHook.suggestFeeComparables(activeFeeModelId, 10),
    ]);
    if (compR.success)    setFeeComparables(compR.data);
    if (computeR.success) setFeeComputed(computeR.data);
    if (sugR.success)     setRpcSuggestions(sugR.data || []);
    refetchLoadedCostMetrics();
  }, [activeFeeModelId, activeProjects, feeComparables, feeModelsHook, getProjSize]);

  /** Unified pill click — the primary selection action on both Suggested
   *  and Included rows.
   *
   *  - If the project is not yet in the model: include it with purpose
   *    set to that single kind (e.g. clicking "Fee" includes with
   *    {fee:true, ctc:false}). Subsequent clicks on the other pill add
   *    that purpose on top.
   *  - If the project is in the model: toggle that kind. If both flags
   *    end up false, the project is removed from the model.
   *
   *  CTC clicks are ignored when the project's status isn't 'completed' —
   *  the calling code guards on that and shows a tooltip.
   */
  const handlePurposeClick = useCallback(async (projectId, kind, selectionSource = 'auto') => {
    if (!activeFeeModelId) return;
    const existing = feeComparables.find(c => String(c.project_id) === String(projectId));
    // A row may live in feeComparables with include=false (from a prior
    // unselect-all that uninclude'd it). Treat those as "not currently
    // included" — clicking a pill should re-include the row with this
    // single purpose, not toggle the preserved-but-ignored old purpose.
    const isActive = !!(existing && existing.include);

    if (isActive) {
      const curPurpose = existing.purpose || { fee: true, ctc: true };
      const nextPurpose = { ...curPurpose, [kind]: !curPurpose[kind] };
      // Both off → remove from the model entirely so the row reverts to a
      // pure suggestion (no half-included state).
      if (!nextPurpose.fee && !nextPurpose.ctc) {
        await handleToggleComparable(projectId, false, selectionSource);
        return;
      }
      // Optimistic local flip, then persist.
      setFeeComparables(prev => prev.map(c =>
        String(c.project_id) === String(projectId) ? { ...c, purpose: nextPurpose } : c
      ));
      await feeModelsHook.setComparablePurpose({
        modelId: activeFeeModelId,
        projectId,
        purpose: nextPurpose,
      });
      // Fee flip → recompute the fee model. CTC flip → refresh the
      // cost-side metric RPC (no full recompute needed, just an
      // aggregate over the cached cost rows).
      if (kind === 'fee') {
        const computeR = await feeModelsHook.computeFeeModel(activeFeeModelId);
        if (computeR.success) setFeeComputed(computeR.data);
      } else {
        refetchLoadedCostMetrics();
      }
      return;
    }

    // Not currently included (new row, or previously uninclude'd) → add
    // with just this kind set. Always set include=true so a re-click
    // after a both-off cycle actually re-includes the row.
    const proj = (activeProjects || []).find(
      x => x.id === projectId || String(x.id) === String(projectId));
    const snapshot = proj
      ? { name: proj.name, budget: proj.budget, projectSize: getProjSize(proj) }
      : null;
    const purpose = { fee: kind === 'fee', ctc: kind === 'ctc' };
    await feeModelsHook.upsertComparable({
      modelId: activeFeeModelId,
      projectId,
      selectionSource,
      include: true,
      snapshot,
      purpose,
    });
    const [compR, computeR, sugR] = await Promise.all([
      feeModelsHook.fetchComparables(activeFeeModelId),
      feeModelsHook.computeFeeModel(activeFeeModelId),
      feeModelsHook.suggestFeeComparables(activeFeeModelId, 10),
    ]);
    if (compR.success)    setFeeComparables(compR.data);
    if (computeR.success) setFeeComputed(computeR.data);
    if (sugR.success)     setRpcSuggestions(sugR.data || []);
    refetchLoadedCostMetrics();
  }, [activeFeeModelId, feeComparables, activeProjects, feeModelsHook, getProjSize, handleToggleComparable]);

  // Legacy alias for the Included list (older code paths still call this).
  const handleTogglePurpose = handlePurposeClick;

  /** Debounced factor slider change — uses weight from factor row or def default */
  const handleFactorChange = useCallback((factorKey, newValue) => {
    setFeeFactors(prev => ({
      ...prev,
      [factorKey]: { ...(prev[factorKey] || {}), value: newValue },
    }));
    if (feeDebounceRef.current) clearTimeout(feeDebounceRef.current);
    feeDebounceRef.current = setTimeout(async () => {
      if (!activeFeeModelId) return;
      // Weight priority: existing factor row → definition default → 1.0
      const existingWeight = feeFactors[factorKey]?.weight;
      const defWeight = feeFactorDefs.find(d => d.factor_key === factorKey)?.default_weight;
      await feeModelsHook.upsertFactor({
        modelId: activeFeeModelId,
        factorKey,
        value: newValue,
        weight: existingWeight ?? defWeight ?? 1.0,
      });
      const res = await feeModelsHook.computeFeeModel(activeFeeModelId);
      if (res.success) setFeeComputed(res.data);
    }, 400);
  }, [activeFeeModelId, feeModelsHook, feeFactors, feeFactorDefs]);

  /** RPC-driven Top-N suggestions (uses the same compute_similarity_score
   *  function that scores already-included comparables, so the order shown
   *  in "Suggested" matches the post-add Sim badge exactly, plus a light
   *  recency tiebreaker). Falls back to the JS heuristic only if the RPC
   *  call fails or returns empty. */
  const [rpcSuggestions, setRpcSuggestions] = useState([]);
  const [rpcSuggestionsLoading, setRpcSuggestionsLoading] = useState(false);
  const [rpcSuggestionsError, setRpcSuggestionsError] = useState(null);

  useEffect(() => {
    if (!activeFeeModelId) { setRpcSuggestions([]); return; }
    if (!feeModelsHook?.suggestFeeComparables) return;
    let cancelled = false;
    setRpcSuggestionsLoading(true);
    setRpcSuggestionsError(null);
    feeModelsHook.suggestFeeComparables(activeFeeModelId, 10).then(res => {
      if (cancelled) return;
      if (res.success) {
        setRpcSuggestions(res.data || []);
      } else {
        setRpcSuggestionsError(res.error || 'Suggest RPC failed');
        setRpcSuggestions([]);
      }
      setRpcSuggestionsLoading(false);
    });
    return () => { cancelled = true; };
  // feeModelsHook is recreated every render; only re-fetch when the model changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFeeModelId]);

  const suggestedComparables = useMemo(() => {
    const inq = editDraft;
    if (!inq.id) return [];
    const added = new Set(feeComparables.filter(c => c.include).map(c => String(c.project_id)));
    // Build a lookup of activeProjects by id so we can attach the full project
    // record (the RPC only returns a slim row).
    const projById = new Map((activeProjects || []).map(p => [String(p.id), p]));

    // Prefer RPC results (DB-side similarity scoring, identical to post-add badge)
    if (rpcSuggestions && rpcSuggestions.length > 0) {
      return rpcSuggestions.map(r => {
        const proj = projById.get(String(r.project_id)) || {};
        // Convert 0..1 similarity into a 0..100 "_score" so the existing
        // badge in the UI still reads naturally.
        const scaledScore = Math.round((parseFloat(r.combined_score) || 0) * 100);
        return {
          ...proj,
          id: r.project_id,
          name: r.name || proj.name,
          projectCode: r.project_code || proj.projectCode,
          budget: parseFloat(r.budget) || proj.budget || 0,
          projectSize: parseFloat(r.project_size) || proj.projectSize || 0,
          _score: scaledScore,
          _pSize: parseFloat(r.project_size) || getProjSize(proj),
          _similarity: parseFloat(r.similarity_score) || 0,
          _recencyWeight: parseFloat(r.recency_weight) || 0,
          _ratePerM2: parseFloat(r.rate_per_m2) || 0,
          _alreadyAdded: added.has(String(r.project_id)),
        };
      });
    }

    // Fallback: legacy JS heuristic
    return (activeProjects || [])
      .filter(p => p.budget > 0 && getProjSize(p) > 0)
      .map(p => ({ ...p, _score: scoreFeeComparable(p, inq), _pSize: getProjSize(p) }))
      .filter(p => p._score >= 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 10)
      .map(p => ({ ...p, _alreadyAdded: added.has(String(p.id)) }));
  }, [activeProjects, editDraft, feeComparables, scoreFeeComparable, getProjSize, rpcSuggestions]);

  /** Manual-add search results. Subsequence match against project name OR
   *  projectCode (each query letter appears in order somewhere in the
   *  target, with other characters allowed between). So "mf" finds
   *  "Mafatlal" (m·a·f) and "vc" still finds "Vishal Chokshi". Projects
   *  already in the model are included but flagged so the UI can render
   *  them disabled — that way "Mafatlal" is findable even after adding. */
  const manualSearchResults = useMemo(() => {
    if (!feeManualSearch.trim()) return [];
    const q = feeManualSearch.toLowerCase().replace(/\s+/g, '');
    if (!q) return [];
    const matchesSubseq = (target) => {
      const t = (target || '').toLowerCase();
      let i = 0;
      for (let j = 0; j < t.length && i < q.length; j++) {
        if (t[j] === q[i]) i++;
      }
      return i === q.length;
    };
    const added = new Set(feeComparables.filter(c => c.include).map(c => String(c.project_id)));
    return (activeProjects || [])
      .filter(p => p.budget > 0 && getProjSize(p) > 0 &&
        (matchesSubseq(p.name) || matchesSubseq(p.projectCode)))
      .map(p => ({ ...p, _alreadyAdded: added.has(String(p.id)) }))
      .slice(0, 8);
  }, [activeProjects, feeManualSearch, feeComparables, getProjSize]);

  /** Reset fee state when drawer closes / inquiry changes */
  useEffect(() => {
    if (!expandedId) {
      setFeeModels([]); setActiveFeeModelId(null);
      setFeeComparables([]); setFeeFactors({});
      setFeeComputed(null); setFeeManualSearch('');
      setFeeError(null); setFeeFactorDefs([]);
      setInflationYear(null);
    }
  }, [expandedId]);

  /** Fetch latest inflation CPI year when feeComputed provides a country code */
  useEffect(() => {
    const cc = feeComputed?.inflation?.countryCode || null;
    if (!cc) { setInflationYear(null); return; }
    let cancelled = false;
    feeModelsHook.getLatestInflationYear(cc).then(res => {
      if (!cancelled && res.success) setInflationYear(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [feeComputed, feeModelsHook]);

  /** Auto-load fee models when user navigates to 'historical' tab */
  useEffect(() => {
    if (drawerSection === 'historical' && expandedId && feeModels.length === 0) {
      loadFeeModelsForInquiry(expandedId);
    }
  }, [drawerSection, expandedId, feeModels.length, loadFeeModelsForInquiry]);

  // ── Sort helpers (matches ProjectExpensesPage pattern) ──────────────
  // Map column keys to sort keys
  const COL_SORT_KEYS = {
    client: 'client', date: 'inquiryDate', nextFollowup: 'nextFollowup', overdue: 'overdue',
    potential: 'potential', priority: 'priority', projectType: 'projectType',
    location: 'location', estimatedFee: 'estimatedFee', estimatedStartDate: 'estimatedStartDate', nextSteps: 'nextSteps',
  };
  const handleSortClick = (sortKey) => {
    if (sortBy === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(sortKey); setSortDir('desc'); }
  };
  const getSortIndicator = (colKey) => {
    const sortKey = COL_SORT_KEYS[colKey];
    if (!sortKey || sortBy !== sortKey) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Area Statement helpers (#8) ─────────────────────────────────────
  // New model: version.groups = [{ id, code, label, collapsed, subrows: [{ id, num, label, indoor, outdoor, back_of_house }] }]
  // All area values stored in sq.ft. - unit toggle converts display only.
  const [areaUnit, setAreaUnit] = useState('sqft'); // 'sqft' or 'm2'

  const getAreaStatements = () => {
    const raw = editDraft.areaStatements;
    if (raw && raw.length > 0) return raw.map(migrateAreaVersion);
    return [blankAreaStatement('Version 1')];
  };
  const getActiveAreaIdx = () => {
    const vs = getAreaStatements();
    const idx = editDraft._areaTab ?? (vs.length - 1);
    return Math.min(idx, vs.length - 1);
  };
  const getActiveArea = () => getAreaStatements()[getActiveAreaIdx()];

  // Group-level operations
  const updateAreaGroup = (vIdx, gIdx, field, value) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    groups[gIdx] = { ...groups[gIdx], [field]: value };
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const addAreaGroup = (vIdx) => {
    const vs = [...getAreaStatements()];
    const codes = vs[vIdx].groups.map(g => g.code);
    let next = '';
    for (let i = 0; i < 26; i++) { const c = String.fromCharCode(65 + i); if (!codes.includes(c)) { next = c; break; } }
    vs[vIdx] = { ...vs[vIdx], groups: [...vs[vIdx].groups, blankAreaGroup(next)] };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const removeAreaGroup = (vIdx, gIdx) => {
    const vs = [...getAreaStatements()];
    const g = vs[vIdx].groups[gIdx];
    // Warn if linked from scenarios
    const scenarios = editDraft.feeScenarios || [];
    const isLinked = scenarios.some(sc => (sc.areaGroupIds || []).includes(g?.id));
    if (isLinked && !window.confirm(`Group "${g.code}" is referenced by a fee scenario. Removing will break the link. Continue?`)) return;
    vs[vIdx] = { ...vs[vIdx], groups: vs[vIdx].groups.filter((_, i) => i !== gIdx) };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const toggleGroupCollapse = (vIdx, gIdx) => {
    const vs = [...getAreaStatements()];
    vs[vIdx].groups[gIdx] = { ...vs[vIdx].groups[gIdx], collapsed: !vs[vIdx].groups[gIdx].collapsed };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };

  // Subrow operations
  const updateSubrow = (vIdx, gIdx, sIdx, field, value) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    const subrows = [...groups[gIdx].subrows];
    if (['indoor', 'covered_outdoor', 'non_covered_outdoor'].includes(field)) {
      subrows[sIdx] = { ...subrows[sIdx], [field]: parseAreaInput(value, areaUnit) };
    } else {
      subrows[sIdx] = { ...subrows[sIdx], [field]: value };
    }
    groups[gIdx] = { ...groups[gIdx], subrows };
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const addSubrow = (vIdx, gIdx) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    const subs = groups[gIdx].subrows || [];
    // Auto-number: find next number
    const lastNum = subs.length > 0 ? subs[subs.length - 1].num : '0';
    const baseInt = parseInt(lastNum) || subs.length;
    const nextNum = String(baseInt + 1);
    groups[gIdx] = { ...groups[gIdx], subrows: [...subs, blankSubrow(nextNum)] };
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const removeSubrow = (vIdx, gIdx, sIdx) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    groups[gIdx] = { ...groups[gIdx], subrows: groups[gIdx].subrows.filter((_, i) => i !== sIdx) };
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };

  // DnD reorder: move group up/down
  // DnD reorder: move group up/down
  const moveGroup = (vIdx, gIdx, dir) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    const newIdx = gIdx + dir;
    if (newIdx < 0 || newIdx >= groups.length) return;
    [groups[gIdx], groups[newIdx]] = [groups[newIdx], groups[gIdx]];
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };

  // DnD reorder: move subrow up/down within a group
  const moveSubrow = (vIdx, gIdx, sIdx, dir) => {
    const vs = [...getAreaStatements()];
    const groups = [...vs[vIdx].groups];
    const subs = [...groups[gIdx].subrows];
    const newIdx = sIdx + dir;
    if (newIdx < 0 || newIdx >= subs.length) return;
    [subs[sIdx], subs[newIdx]] = [subs[newIdx], subs[sIdx]];
    // Auto-renumber after reorder
    subs.forEach((s, i) => { s.num = String(i + 1); });
    groups[gIdx] = { ...groups[gIdx], subrows: subs };
    vs[vIdx] = { ...vs[vIdx], groups };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };

  // Legacy compat: keep addAreaVersion + renameAreaVersion
  const addAreaVersion = () => {
    const vs = [...getAreaStatements()];
    const latest = vs[vs.length - 1];
    const newV = {
      id: genAreaVersionId(),
      name: `Version ${vs.length + 1}`,
      createdAt: new Date().toISOString(),
      groups: latest.groups.map(g => ({ ...g, id: genAreaGroupId(), wallsCirculationPct: g.wallsCirculationPct || '', subrows: g.subrows.map(r => ({ ...r, id: genAreaRowId() })) })),
      rows: [],
    };
    vs.push(newV);
    setEditDraft(d => ({ ...d, areaStatements: vs, _areaTab: vs.length - 1 }));
  };
  const renameAreaVersion = (vIdx, name) => {
    const vs = [...getAreaStatements()];
    vs[vIdx] = { ...vs[vIdx], name };
    setEditDraft(d => ({ ...d, areaStatements: vs }));
  };
  const deleteAreaVersion = (vIdx) => {
    const vs = [...getAreaStatements()];
    if (vs.length <= 1) return;
    vs.splice(vIdx, 1);
    const newTab = Math.min(vIdx, vs.length - 1);
    setEditDraft(d => ({ ...d, areaStatements: vs, _areaTab: newTab }));
  };

  // ── Scenario helpers ──────────────────────────────────────────────
  const getScenarios = () => {
    const raw = editDraft.feeScenarios;
    // (#9) Existing saved scenarios preserved via normScenario - defaults only for new
    if (raw && raw.length > 0) return raw.map(normScenario);
    return [blankScenario('Scenario 1'), blankScenario('Scenario 2'), blankScenario('Scenario 3')];
  };
  const updateScenarioRow = (sIdx, rIdx, field, value) => {
    const scenarios = getScenarios();
    const rows = [...scenarios[sIdx].rows];
    rows[rIdx] = { ...rows[rIdx], [field]: value };
    scenarios[sIdx] = { ...scenarios[sIdx], rows };
    setEditDraft(d => ({ ...d, feeScenarios: scenarios }));
  };
  const addScenarioRow = (sIdx) => {
    const scenarios = getScenarios();
    scenarios[sIdx] = { ...scenarios[sIdx], rows: [...scenarios[sIdx].rows, blankScenarioRow()] };
    setEditDraft(d => ({ ...d, feeScenarios: scenarios }));
  };
  const removeScenarioRow = (sIdx, rIdx) => {
    const scenarios = getScenarios();
    scenarios[sIdx] = { ...scenarios[sIdx], rows: scenarios[sIdx].rows.filter((_, i) => i !== rIdx) };
    setEditDraft(d => ({ ...d, feeScenarios: scenarios }));
  };
  const addScenario = () => {
    const scenarios = getScenarios();
    scenarios.push(blankScenario(`Scenario ${scenarios.length + 1}`));
    setEditDraft(d => ({ ...d, feeScenarios: scenarios }));
  };
  const deleteScenario = (sIdx) => {
    const scenarios = getScenarios();
    if (scenarios.length <= 1) return;
    scenarios.splice(sIdx, 1);
    const newTab = Math.min(sIdx, scenarios.length - 1);
    setEditDraft(d => ({ ...d, feeScenarios: scenarios, _scenarioTab: newTab }));
  };

  // ── Offer helpers (#10) ───────────────────────────────────────────
  // (#10) getOffers() live-syncs category + areaSqft from the active area statement.
  // This ensures changing the area statement immediately updates all linked offers.
  const getOffers = () => {
    const raw = editDraft.feeOffers;
    const areaStmt = getActiveArea();
    const areaMap = new Map((areaStmt?.rows || []).map(r => [r.id, r]));

    if (raw && raw.length > 0) {
      return raw.map(normOffer).map(offer => ({
        ...offer,
        rows: (offer.rows || []).map(row => {
          // (#10) If row is linked, sync category + areaSqft from area statement
          if (row.areaStatementRowId && areaMap.has(row.areaStatementRowId)) {
            const src = areaMap.get(row.areaStatementRowId);
            return { ...row, category: src.category, areaSqft: src.areaSqft };
          }
          return row;  // Unlinked manual rows pass through unchanged
        }),
      }));
    }
    // Default offers - built from current area statement
    return [
      { ...blankOfferFromArea(areaStmt), label: 'Offer 1' },
      { ...blankOfferFromArea(areaStmt), label: 'Offer 2' },
      { ...blankOfferFromArea(areaStmt), label: 'Offer 3' },
      { ...blankOfferFromArea(areaStmt), label: 'Final Agreement' },
    ];
  };
  const updateOfferRow = (oIdx, rIdx, field, value) => {
    const offers = getOffers();
    const rows = [...offers[oIdx].rows];
    rows[rIdx] = { ...rows[rIdx], [field]: value };
    offers[oIdx] = { ...offers[oIdx], rows };
    setEditDraft(d => ({ ...d, feeOffers: offers }));
  };
  const addOfferRow = (oIdx) => {
    const offers = getOffers();
    offers[oIdx] = { ...offers[oIdx], rows: [...offers[oIdx].rows, blankScenarioRow()] };
    setEditDraft(d => ({ ...d, feeOffers: offers }));
  };
  const removeOfferRow = (oIdx, rIdx) => {
    const offers = getOffers();
    offers[oIdx] = { ...offers[oIdx], rows: offers[oIdx].rows.filter((_, i) => i !== rIdx) };
    setEditDraft(d => ({ ...d, feeOffers: offers }));
  };
  const updateOfferField = (oIdx, field, value) => {
    const offers = getOffers();
    offers[oIdx] = { ...offers[oIdx], [field]: value };
    setEditDraft(d => ({ ...d, feeOffers: offers }));
  };
  const deleteOffer = (oIdx) => {
    const offers = getOffers();
    if (offers.length <= 1) return;
    offers.splice(oIdx, 1);
    const newTab = Math.min(oIdx, offers.length - 1);
    setEditDraft(d => ({ ...d, feeOffers: offers, _offerTab: newTab }));
  };
  const addOffer = () => {
    const offers = getOffers();
    const areaStmt = getActiveArea();
    const newOffer = { ...blankOfferFromArea(areaStmt), label: `Offer ${offers.length + 1}` };
    offers.push(newOffer);
    setEditDraft(d => ({ ...d, feeOffers: offers, _offerTab: offers.length - 1 }));
  };

  // ── XLSX Export handler ───────────────────────────────────────────
  const handleXlsxExport = async () => {
    try {
      const sheetsToExport = [];
      if (exportSheets.area) sheetsToExport.push('area');
      if (exportSheets.scenarios) sheetsToExport.push('scenarios');
      if (exportSheets.offers) sheetsToExport.push('offers');
      if (sheetsToExport.length === 0) return;

      const activeVersion = getActiveArea();
      const scenarios = getScenarios();
      const offers = getOffers();
      const clientName = getClientName(editDraft);

      const buffer = await exportInquiryToXlsx({
        inquiryName: clientName || 'Inquiry',
        activeAreaVersion: activeVersion,
        scenarios,
        offers,
        areaUnit,
        sheets: sheetsToExport,
      });

      const filename = `${(clientName || 'Inquiry').replace(/[^a-zA-Z0-9_-]/g, '_')}_Area_Statement.xlsx`;
      downloadXlsxBuffer(buffer, filename);
      setShowExportDialog(false);
    } catch (err) {
      console.error('XLSX export error:', err);
      alert('Export failed: ' + err.message);
    }
  };

  // ── XLSX Import handler ───────────────────────────────────────────
  const handleXlsxImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so the same file can be re-selected
    e.target.value = '';

    setXlsxImporting(true);
    setXlsxErrors(null);

    try {
      const result = await importInquiryFromXlsx(file);

      if (!result.success) {
        setXlsxErrors(result.errors || ['Unknown import error']);
        setXlsxImporting(false);
        return;
      }

      const { data } = result;
      const updates = {};

      // Merge imported area version into area statements
      if (data.areaVersion) {
        const currentVersions = [...getAreaStatements()];
        // Add as a new version (preserves existing versions)
        currentVersions.push(data.areaVersion);
        updates.areaStatements = currentVersions;
        updates._areaTab = currentVersions.length - 1;
      }

      // Replace scenarios if imported
      if (data.scenarios && data.scenarios.length > 0) {
        updates.feeScenarios = data.scenarios.map(sc => ({
          ...sc,
          rows: DEFAULT_AREA_CATEGORIES.map(cat => ({ category: AREA_CAT_LABELS[cat] || cat, areaSqft: '', rateSqft: '' })),
        }));
      }

      // Replace offers if imported
      if (data.offers && data.offers.length > 0) {
        updates.feeOffers = data.offers.map(off => ({
          ...off,
          rows: [],
        }));
      }

      setEditDraft(d => ({ ...d, ...updates }));

      // Show warnings if any
      if (result.errors && result.errors.length > 0) {
        setXlsxErrors(result.errors);
      }

      // Auto-save
      const finalDraft = { ...editDraft, ...updates };
      if (finalDraft.id && supabaseInquiries?.updateInquiry) {
        const saveRes = await supabaseInquiries.updateInquiry(finalDraft.id, finalDraft);
        if (!saveRes.success) {
          setXlsxErrors(prev => [...(prev || []), `Auto-save failed: ${saveRes.error}`]);
        }
      }

      setXlsxImporting(false);
    } catch (err) {
      console.error('XLSX import error:', err);
      setXlsxErrors([`Import failed: ${err.message}`]);
      setXlsxImporting(false);
    }
  };

  // ── Column rendering map ──────────────────────────────────────────
  const columns = DEFAULT_COLUMNS;

  const renderCell = (col, inq) => {
    switch (col.key) {
      case 'client': {
        const client = getClient(inq);
        const orgName = client ? (client.organisation || '') : (inq.clientOrgNameOverride || '');
        const contactName = client ? (client.name || '') : (inq.clientNameOverride || '');
        const phone = client ? (client.mobile || '') : (inq.clientPhoneOverride || '');
        const secondLine = orgName || contactName;
        return (
          <div style={{ maxWidth: '160px', ...CELL_TRUNCATE }}>
            <div style={{ fontWeight: 500, fontSize: '12px', ...CELL_TRUNCATE }}>{inq.projectName || '—'}</div>
            {secondLine && (
              <div style={{ fontSize: '10px', color: 'var(--brio-grey)', ...CELL_TRUNCATE }}>
                {secondLine}{phone ? ` · ${phone}` : ''}
              </div>
            )}
            {!inq.clientId && inq.clientOrgNameOverride && (
              <div style={{ fontSize: '10px', color: 'var(--brio-orange, #DB6B5D)', fontStyle: 'italic' }}>unlinked</div>
            )}
          </div>
        );
      }
      // (#5) Show Last Follow-Up Date — creates a follow-up entry so the DB trigger
      // updates last_followup_date correctly (writing the column directly is overridden
      // by the trigger that recalculates MAX(followup_date) from inquiry_followups).
      case 'date': {
        const lastFup = getLastFollowupDate(inq);
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()}>
            <BrioDatePicker
              value={lastFup || ''}
              onChange={async v => {
                if (!v) return;
                const res = await supabaseInquiries.addFollowup(inq.id, {
                  followupDate: v,
                  notes: '',
                  followupByEmployeeId: currentUser?.id || null,
                  followupByName: getUserDisplayName(currentUser) || '',
                });
                if (res.success) {
                  if (expandedId === inq.id) {
                    setFollowups(prev => [res.data, ...prev].sort((a, b) =>
                      (b.followupDate || '').localeCompare(a.followupDate || '')));
                  }
                  supabaseInquiries?.refetch?.();
                }
              }}
              clearable
              className="brio-datepicker-trigger--compact"
              style={INLINE_CELL_DATE}
            />
          </div>
        );
      }
      case 'nextFollowup': {
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()}>
            <BrioDatePicker
              value={inq.nextFollowupDate || ''}
              onChange={v => saveField(inq.id, 'nextFollowupDate', v || null)}
              clearable
              className="brio-datepicker-trigger--compact"
              style={INLINE_CELL_DATE}
            />
          </div>
        );
      }
      case 'overdue': {
        const overdue = getOverdueDays(inq);
        if (overdue <= 0) return null;
        return (
          <span
            title={`Next follow-up was due on ${fmtDate(inq.nextFollowupDate)} — ${overdue} day${overdue === 1 ? '' : 's'} overdue`}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: '24px', height: '24px', padding: '0 3px',
              borderRadius: '50%', boxSizing: 'border-box',
              fontSize: '9px', fontWeight: 600, lineHeight: 1,
              color: '#fff', background: overdueColor(overdue),
              whiteSpace: 'nowrap', cursor: 'default',
            }}
          >
            {overdue}d
          </span>
        );
      }
      case 'potential':
        return (
          <BrioSelect
            value={inq.potential || '50'}
            onChange={e => saveField(inq.id, 'potential', e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ ...COMPACT_SELECT, maxWidth: '90px',
              color: getPotentialColor(inq.potential), fontWeight: 500 }}
          >
            {POTENTIAL_OPTIONS.map(o => <option key={o} value={o}>{o}%</option>)}
          </BrioSelect>
        );
      // (#6) Priority: categorical select instead of numeric input
      case 'priority': {
        const pri = inq._priority || migratePriority(inq.priority);
        return (
          <BrioSelect
            value={pri}
            onChange={e => saveField(inq.id, 'priority', e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ ...COMPACT_SELECT, maxWidth: '90px',
              color: PRIORITY_COLORS[pri] || 'var(--brio-grey)', fontWeight: 500 }}
          >
            {dynPriorityLabels.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
          </BrioSelect>
        );
      }
      case 'projectType':
        return (
          <BrioSelect
            value={inq.projectType || ''}
            onChange={e => saveField(inq.id, 'projectType', e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ ...COMPACT_SELECT, maxWidth: '110px' }}
          >
            <option value="">—</option>
            {BRIO_PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </BrioSelect>
        );
      case 'location':
        return (
          <div style={{ fontSize: '11px', maxWidth: '100px', ...CELL_TRUNCATE }}>
            {inq.locationCity || <span style={{ color: 'var(--brio-muted)' }}>—</span>}
          </div>
        );
      case 'estimatedFee': {
        const feeRaw = inq.estimatedFee;
        // Priority: computed (offer → scenario) > manual (estimatedFee)
        const computedFee = computeLatestOfferTotal(inq);
        const displayFee = computedFee ?? feeRaw;
        const isAutoFilled = computedFee != null;
        const displayValue = displayFee != null ? `Rs. ${Number(displayFee).toLocaleString('en-IN')}` : '';
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '95px' }}>
            <input
              type="text"
              defaultValue={displayValue}
              key={`${feeRaw}-${computedFee}`}
              placeholder="—"
              title={isAutoFilled ? 'Auto-filled from fee offer / scenario' : undefined}
              style={{
                width: '100%', fontSize: '10px', padding: '2px 4px',
                border: '1px solid transparent', borderRadius: 0,
                background: 'transparent',
                color: isAutoFilled ? 'var(--brio-muted)' : 'var(--brio-black, #111111)',
                fontStyle: isAutoFilled ? 'italic' : 'normal',
                boxSizing: 'border-box', outline: 'none',
              }}
              onFocus={e => {
                e.target.value = feeRaw || '';
                e.target.style.border = '1px solid var(--brio-border, rgba(0,0,0,0.15))';
                e.target.style.background = '#fff';
                e.target.style.color = 'var(--brio-black, #111111)';
                e.target.style.fontStyle = 'normal';
              }}
              onBlur={e => {
                const val = e.target.value ? Number(e.target.value) : null;
                const afterFee = computedFee ?? val;
                e.target.value = afterFee != null ? `Rs. ${Number(afterFee).toLocaleString('en-IN')}` : '';
                e.target.style.border = '1px solid transparent';
                e.target.style.background = 'transparent';
                e.target.style.color = computedFee != null ? 'var(--brio-muted)' : 'var(--brio-black, #111111)';
                e.target.style.fontStyle = computedFee != null ? 'italic' : 'normal';
                saveField(inq.id, 'estimatedFee', val);
              }}
            />
          </div>
        );
      }
      case 'estimatedStartDate':
        return (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()}>
            <BrioDatePicker
              value={inq.estimatedStartDate || ''}
              onChange={v => saveField(inq.id, 'estimatedStartDate', v || null)}
              clearable
              className="brio-datepicker-trigger--compact"
              style={INLINE_CELL_DATE}
            />
          </div>
        );
      case 'nextSteps':
        return (
          <div style={{ fontSize: '11px', color: 'var(--brio-text)', maxWidth: '140px', ...CELL_TRUNCATE }}>
            {inq.nextSteps || <span style={{ color: 'var(--brio-muted)' }}>—</span>}
          </div>
        );
      default: return null;
    }
  };

  // Drawer sidebar sections - clean text labels, no emojis
  const DRAWER_SECTIONS = [
    { k: 'client', l: 'Client' },
    { k: 'site', l: 'Site Info' },
    { k: 'brief', l: 'Brief' },
    { k: 'areaStatement', l: 'Area Statement' },
    { k: 'scenarios', l: 'Fee Scenarios' },
    { k: 'historical', l: 'Fee Benchmarking' },
    { k: 'offers', l: 'Offers' },
    { k: 'sow', l: 'Scope of Work' },
    { k: 'pitch', l: 'Pitch Deck' },
    { k: 'status', l: 'Status' },
  ];

  // ═════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════

  // Guard: admin / admin-pa only
  if (!isAdmin) return (
    <BrioFrame>
      <div className="brio-text-center brio-analytics-muted" style={{ padding: '64px 0' }}>
        Access denied — Inquiries is available to admins only.
      </div>
    </BrioFrame>
  );

  return (
    <div className={`brio-flex brio-flex-col brio-analytics-font${isFullscreen ? ' inquiries-fullscreen' : ''}`} style={isFullscreen
      ? { position: 'fixed', inset: 0, zIndex: 10000, background: 'var(--brio-bg, #ffffff)', display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100vw', height: '100vh' }
      : { maxWidth: '875px', margin: '0 auto', width: '100%' }
    }>
      {/* (#15) Fullscreen: override ALL max-width so content + tables go edge-to-edge */}
      {isFullscreen && (
        <style>{`
          .inquiries-fullscreen .brio-frame,
          .inquiries-fullscreen [class*="brio-frame"],
          .inquiries-fullscreen [class*="BrioFrame"] {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
          .inquiries-fullscreen .brio-table-wrap,
          .inquiries-fullscreen [class*="brio-table-wrap"] {
            max-width: 100% !important;
          }
          .inquiries-fullscreen .brio-table {
            min-width: 0 !important;
          }
          .inquiries-fullscreen .brio-analytics-surface {
            max-width: 100% !important;
          }
        `}</style>
      )}

      {/* ── PAGE HEADER ────────────────────────────────────────────── */}
      <div className="brio-pageband brio-pageband--header" style={isFullscreen ? { flexShrink: 0, borderBottom: '1px solid var(--brio-border, #e2e8f0)', width: '100%' } : undefined}>
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">
              <h3 className="brio-analytics-section-title">Inquiries</h3>
              <span style={{ fontSize: '11px', color: 'var(--brio-grey)', marginLeft: '8px' }}>
                {filteredList.length} {tab === 'not_converted' ? 'not converted' : tab}
              </span>
            </div>
            <div className="brio-page-header__right brio-flex brio-items-center brio-gap-xs">
              {/* (#1) Fullscreen toggle */}
              <button
                onClick={() => setIsFullscreen(f => !f)}
                className="brio-btn brio-btn-secondary"
                title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px' }}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button onClick={() => { setAddForm({ inquiryDate: new Date().toISOString().slice(0, 10), potential: 'medium', priority: 'medium', platformType: 'other' }); setShowAddModal(true); }}
                className="brio-btn brio-btn-primary">
                <Plus className="w-4 h-4" /> New Inquiry
              </button>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* Scrollable body wrapper for fullscreen mode */}
      <div style={isFullscreen ? { flex: 1, overflowY: 'auto', overflowX: 'hidden', width: '100%' } : {}}>

      {/* ── TABS + SEARCH ──────────────────────────────────────────── */}
      <BrioFrame>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0 6px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '2px' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => { flushDraft(); setTab(t.key); }}
                style={{
                  padding: '4px 12px', borderRadius: 0, fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                  border: tab === t.key ? '1px solid var(--brio-gold, #9F902A)' : '1px solid var(--brio-border, #e2e8f0)',
                  background: tab === t.key ? 'var(--brio-gold, #9F902A)' : 'transparent',
                  color: tab === t.key ? '#fff' : 'var(--brio-text, #475569)',
                }}>
                {t.label}
                <span style={{ marginLeft: '4px', opacity: 0.7 }}>
                  {tabCounts[t.key] || 0}
                </span>
              </button>
            ))}
          </div>
          <div style={{ position: 'relative', flex: 1, maxWidth: '240px' }}>
            <Search style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', width: '13px', height: '13px', color: 'var(--brio-muted)' }} />
            <input
              type="text" placeholder="Search client, city, type…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...INPUT_STYLE, paddingLeft: '26px', fontSize: '11px' }}
            />
          </div>
          {/* Sort button + panel */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowSortPanel(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '4px 10px', fontSize: '11px', borderRadius: 0, cursor: 'pointer',
                border: sortRules.length > 0
                  ? '1px solid var(--brio-gold, #9F902A)'
                  : '1px solid var(--brio-border, #e2e8f0)',
                background: sortRules.length > 0 ? 'rgba(159,144,42,0.08)' : 'transparent',
                color: sortRules.length > 0 ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #475569)',
                fontWeight: sortRules.length > 0 ? 600 : 400,
              }}>
              <ArrowUpDown size={11} />
              Sort
              {sortRules.length > 0 && (
                <span style={{ background: 'var(--brio-gold, #9F902A)', color: '#fff', borderRadius: '8px', fontSize: '9px', padding: '0 5px', fontWeight: 700 }}>
                  {sortRules.length}
                </span>
              )}
            </button>
            {showSortPanel && (
              <InqSortPanel
                sortRules={sortRules}
                onChangeSortRules={setSortRules}
                onClose={() => setShowSortPanel(false)}
              />
            )}
          </div>
          {/* Pipeline performance calculator (auto-derived from filtered list) */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowCalcPanel(v => !v)}
              title="Pipeline performance"
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '4px 10px', fontSize: '11px', borderRadius: 0, cursor: 'pointer',
                border: showCalcPanel
                  ? '1px solid var(--brio-gold, #9F902A)'
                  : '1px solid var(--brio-border, #e2e8f0)',
                background: showCalcPanel ? 'rgba(159,144,42,0.08)' : 'transparent',
                color: showCalcPanel ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #475569)',
                fontWeight: showCalcPanel ? 600 : 400,
              }}>
              <BarChart3 size={11} />
              Performance
              <ChevronDown size={10} style={{ opacity: 0.6 }} />
            </button>
            {showCalcPanel && (
              <InqCalculatorPanel
                funnel={computeInquiryFunnel(
                  calcFunnelMode === 'firstResponse'
                    ? funnelBaseList.filter(inquiryReachedFirstResponse)
                    : funnelBaseList
                )}
                mode={calcFunnelMode}
                onChangeMode={setCalcFunnelMode}
                scopeLabel={`${calcFunnelMode === 'firstResponse' ? 'first-response' : 'all'} inquiries${search.trim() ? ' · filtered' : ''}`}
                onClose={() => setShowCalcPanel(false)}
              />
            )}
          </div>
        </div>
      </BrioFrame>

      {/* ── EMAIL INTAKE (NEEDS REVIEW) ──────────────────────────────── */}
      {tab === 'active' && (
        <BrioFrame>
          <EmailIntakeSection
            intakeMessages={intakeHook.intakeMessages}
            loading={intakeHook.loading}
            isAdmin={isAdmin}
            onApprove={async (id) => {
              const res = await intakeHook.approveIntake(id);
              supabaseInquiries?.refetch?.();
              return res;
            }}
            onReject={async (id, reason) => {
              const res = await intakeHook.rejectIntake(id, reason);
              supabaseInquiries?.refetch?.();
              return res;
            }}
            onRefresh={() => {
              intakeHook.refetch();
              supabaseInquiries?.refetch?.();
            }}
            getAttachmentUrl={intakeHook.getAttachmentUrl}
          />
        </BrioFrame>
      )}

      {/* ── TABLE ──────────────────────────────────────────────────── */}
      <BrioFrame>
        <BrioAnalyticsSurface padding="m">
          <div style={{ overflowX: 'auto' }}>
            <BrioByCode code={TABLE_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th key={col.key}
                        style={{ cursor: 'pointer', minWidth: col.minWidth, padding: '4px 6px', fontSize: '11px' }}
                        onClick={() => handleSortClick(COL_SORT_KEYS[col.key])}
                      >
                        {col.label}{getSortIndicator(col.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length === 0 && (
                    <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--brio-muted)', fontSize: '12px' }}>
                      {search ? 'No inquiries match your search' : `No ${tab === 'not_converted' ? 'not converted' : tab} inquiries`}
                    </td></tr>
                  )}
                  {filteredList.map(inq => {
                    const overdue = getOverdueDays(inq);
                    const rowBorderStyle = overdue > 0
                      ? { boxShadow: `inset 3px 0 0 0 ${overdueColor(overdue)}` }
                      : undefined;
                    return (
                    <React.Fragment key={inq.id}>
                      <tr className="brio-row--hoverable" onClick={() => openDrawer(inq)} style={{ cursor: 'pointer', ...rowBorderStyle }}>
                        {columns.map(col => (
                          <td key={col.key} style={{ padding: '4px 6px', verticalAlign: 'middle' }}>{renderCell(col, inq)}</td>
                        ))}
                      </tr>

                      {/* ── EXPANDED DRAWER ─────────────────────────── */}
                      {expandedId === inq.id && (
                        <tr>
                          <td colSpan={columns.length} style={{ padding: 0, background: 'var(--brio-bg-subtle, #fafaf6)' }}>
                            <div style={{ display: 'flex', borderTop: '2px solid var(--brio-gold, #9F902A)' }}>
                              {/* Sidebar nav */}
                              <div style={{ width: '130px', minWidth: '110px', borderRight: '1px solid var(--brio-border, #e2e8f0)', padding: '6px 0', flexShrink: 0 }}>
                                {DRAWER_SECTIONS.map(s => (
                                  <button key={s.k} onClick={(e) => { e.stopPropagation(); setDrawerSection(s.k); }}
                                    style={{
                                      display: 'block', width: '100%', textAlign: 'left', padding: '5px 10px',
                                      fontSize: '11px', fontWeight: drawerSection === s.k ? 700 : 400,
                                      background: drawerSection === s.k ? 'var(--brio-gold-light, #f5f0d0)' : 'transparent',
                                      color: drawerSection === s.k ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #475569)',
                                      border: 'none', cursor: 'pointer', borderLeft: drawerSection === s.k ? '3px solid var(--brio-gold, #9F902A)' : '3px solid transparent',
                                    }}>
                                    {s.l}
                                  </button>
                                ))}
                              </div>

                              {/* Content area — (#15) fullscreen uses flex to fill available space.
                                  onBlur here bubbles from every child field (React focusout) → the
                                  drawer autosaves each field as it loses focus, just like Comms. */}
                              <div onBlur={() => flushDraft()} style={{ flex: 1, padding: '10px 14px', maxHeight: isFullscreen ? 'calc(100vh - 180px)' : '500px', overflowY: 'auto' }}>

                                {/* Autosave status — fields save automatically on blur */}
                                {autosaveState !== 'idle' && (
                                  <div style={{
                                    textAlign: 'right', fontSize: '10px', marginBottom: '4px', minHeight: '13px',
                                    color: autosaveState === 'error' ? 'var(--brio-red, #934948)'
                                      : autosaveState === 'saved' ? 'var(--brio-teal, #689581)'
                                      : 'var(--brio-muted, #94a3b8)',
                                  }}>
                                    {autosaveState === 'saving' ? 'Saving…'
                                      : autosaveState === 'saved' ? 'All changes saved'
                                      : 'Save failed — check your connection'}
                                  </div>
                                )}

                                {/* ── CLIENT SECTION ─── */}
                                {drawerSection === 'client' && (
                                  <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                                      <FormRow sectionHeader label="Client Details" />
                                      <FormRow label="Project Name">
                                        <input type="text"
                                          value={editDraft.projectName || ''}
                                          onChange={e => setEditDraft(d => ({ ...d, projectName: e.target.value }))}
                                          style={INPUT_STYLE} placeholder="Project name" />
                                      </FormRow>
                                      {/* Name — editable in both modes via override/client field map */}
                                      <FormRow label="Name">
                                        <input type="text"
                                          value={getClientField('name')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientName: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientNameOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('name', e.target.value)}
                                          style={INPUT_STYLE} placeholder="Contact name" />
                                      </FormRow>
                                      <FormRow label="Organisation">
                                        <input type="text"
                                          value={getClientField('organisationName')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientOrg: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientOrgNameOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('organisationName', e.target.value)}
                                          style={INPUT_STYLE} placeholder="Organisation name" />
                                      </FormRow>
                                      <FormRow label="Phone">
                                        <input type="tel"
                                          value={getClientField('phone')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientPhone: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientPhoneOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('phone', e.target.value)}
                                          style={INPUT_STYLE} placeholder="Phone number" />
                                      </FormRow>
                                      <FormRow label="Email">
                                        <input type="email"
                                          value={getClientField('email')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientEmail: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientEmailOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('email', e.target.value)}
                                          style={INPUT_STYLE} placeholder="Email address" />
                                      </FormRow>
                                      <FormRow label="Address">
                                        <textarea
                                          value={getClientField('address')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientAddr: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientAddressOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('address', e.target.value)}
                                          style={TEXTAREA_STYLE} rows={2} />
                                      </FormRow>
                                      <FormRow label="LinkedIn">
                                        <input type="url"
                                          value={getClientField('linkedinProfile')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientLi: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientLinkedinOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('linkedinProfile', e.target.value)}
                                          style={INPUT_STYLE} placeholder="https://linkedin.com/in/…" />
                                      </FormRow>
                                      <FormRow label="Links / Notes" hint="Interesting links, personal info">
                                        <textarea
                                          value={getClientField('interestingLinks')}
                                          onChange={e => {
                                            if (hasLinkedClient) setEditDraft(d => ({ ...d, _clientLinks: e.target.value }));
                                            else setEditDraft(d => ({ ...d, clientInterestingLinksOverride: e.target.value }));
                                          }}
                                          onBlur={e => setClientField('interestingLinks', e.target.value)}
                                          style={TEXTAREA_STYLE} rows={2} placeholder="Interesting links, personal notes…" />
                                      </FormRow>
                                      <FormRow sectionHeader label="Invoicing" />
                                      <FormRow label="Invoicing Name" hint="Name on contracts & invoices">
                                        <input type="text"
                                          value={editDraft.invoicingName || ''}
                                          onChange={e => setEditDraft(d => ({ ...d, invoicingName: e.target.value }))}
                                          style={INPUT_STYLE} placeholder="Legal / invoicing name…" />
                                      </FormRow>
                                      <FormRow label="Invoicing Address" hint="Address on contracts & invoices">
                                        <textarea
                                          value={editDraft.invoicingAddress || ''}
                                          onChange={e => setEditDraft(d => ({ ...d, invoicingAddress: e.target.value }))}
                                          style={TEXTAREA_STYLE} rows={2} placeholder="Invoicing address…" />
                                      </FormRow>
                                    </tbody></table>
                                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                      {!hasLinkedClient && (editDraft.clientNameOverride || editDraft.clientOrgNameOverride || editDraft.clientPhoneOverride || editDraft.clientEmailOverride) && (
                                        <button onClick={() => createClientAndLink(editDraft)} disabled={saving}
                                          style={{ fontSize: '11px', padding: '4px 10px', borderRadius: 0, border: '1px solid var(--brio-gold)', background: 'transparent', color: 'var(--brio-gold)', cursor: 'pointer', fontWeight: 500 }}>
                                          Create as Client
                                        </button>
                                      )}
                                      <span style={{ fontSize: '10px', color: 'var(--brio-muted)', alignSelf: 'center' }}>
                                        {hasLinkedClient ? 'Fields save to client record on blur' : 'Changes save automatically'}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {/* ── SITE INFO SECTION ─── */}
                                {drawerSection === 'site' && (
                                  <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                                      <FormRow sectionHeader label="Site Information" />
                                      <FormRow label="Dropbox Link">
                                        <input type="url" value={editDraft.siteDropboxLink || ''} onChange={e => setEditDraft(d => ({ ...d, siteDropboxLink: e.target.value }))} style={INPUT_STYLE} placeholder="https://dropbox.com/…" />
                                      </FormRow>
                                      <FormRow label="Site Notes">
                                        <textarea value={editDraft.siteNotes || ''} onChange={e => setEditDraft(d => ({ ...d, siteNotes: e.target.value }))} style={TEXTAREA_STYLE} rows={3} />
                                      </FormRow>
                                      <FormRow label="Plot Area" hint="Land / site area in m²">
                                        <input type="number" min="0" step="any" value={editDraft.plotArea ?? ''} onChange={e => setEditDraft(d => ({ ...d, plotArea: e.target.value === '' ? null : Number(e.target.value) }))} style={{ ...INPUT_STYLE, maxWidth: '160px' }} placeholder="e.g. 500" />
                                      </FormRow>
                                      <FormRow sectionHeader label="Location" />
                                      <FormRow label="City">
                                        <input type="text" value={editDraft.locationCity || ''} onChange={e => setEditDraft(d => ({ ...d, locationCity: e.target.value }))} style={{ ...INPUT_STYLE, maxWidth: '200px' }} />
                                      </FormRow>
                                      <FormRow label="Coordinates" hint="lat, lng">
                                        <input type="text" value={editDraft.locationCoordinates || ''} onChange={e => setEditDraft(d => ({ ...d, locationCoordinates: e.target.value }))} style={{ ...INPUT_STYLE, maxWidth: '220px' }} placeholder="19.076, 72.877" />
                                      </FormRow>
                                      {(editDraft.locationCity || editDraft.locationCoordinates) && (
                                        <FormRow label="Map">
                                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(editDraft.locationCoordinates || editDraft.locationCity)}`}
                                            target="_blank" rel="noopener noreferrer" style={{ display: 'block', textDecoration: 'none' }}>
                                            <div style={{ width: '100%', maxWidth: '300px', height: '130px', borderRadius: '8px', border: '1px solid var(--brio-border)', overflow: 'hidden', position: 'relative', background: '#1a2e1a' }}>
                                              <iframe title="Location" width="100%" height="100%" style={{ border: 0, pointerEvents: 'none' }} loading="lazy"
                                                src={`https://maps.google.com/maps?q=${encodeURIComponent(editDraft.locationCoordinates || editDraft.locationCity)}&t=k&z=16&ie=UTF8&iwloc=&output=embed`} />
                                              <div style={{ position: 'absolute', bottom: '3px', right: '5px', background: 'rgba(0,0,0,0.65)', borderRadius: 0, padding: '2px 5px', fontSize: '9px', fontWeight: 500, color: '#fff', pointerEvents: 'none' }}>Open in Maps</div>
                                            </div>
                                          </a>
                                        </FormRow>
                                      )}
                                    </tbody></table>
                                  </div>
                                )}

                                {/* ── BRIEF SECTION ─── */}
                                {drawerSection === 'brief' && (
                                  <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                                      <FormRow sectionHeader label="Project Brief" />
                                      <FormRow label="Intake Notes">
                                        <textarea value={editDraft.briefNotes || ''} onChange={e => setEditDraft(d => ({ ...d, briefNotes: e.target.value }))} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} style={{ ...TEXTAREA_STYLE, minHeight: '100px', resize: 'none', overflow: 'hidden' }} rows={5} />
                                      </FormRow>
                                      <FormRow label="Estimated Fee" hint={editDraft._estimatedFeeAutoFilled ? 'Auto-filled from latest fee offer' : 'High-level fee estimate (INR)'}>
                                        <input type="number" value={editDraft.estimatedFee || ''} onChange={e => setEditDraft(d => ({ ...d, estimatedFee: e.target.value, _estimatedFeeAutoFilled: false }))} style={{ ...INPUT_STYLE, fontStyle: editDraft._estimatedFeeAutoFilled ? 'italic' : 'normal', color: editDraft._estimatedFeeAutoFilled ? 'var(--brio-muted)' : undefined }} placeholder="e.g. 5000000" />
                                      </FormRow>
                                      <FormRow label="Estimated Start Date">
                                        <BrioDatePicker value={editDraft.estimatedStartDate || ''} onChange={v => setEditDraft(d => ({ ...d, estimatedStartDate: v }))} placeholder="Start date" />
                                      </FormRow>
                                      <FormRow label="Dropbox Link" hint="External documents">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                          <input type="url" value={editDraft.briefAreaStatementLink || ''} onChange={e => setEditDraft(d => ({ ...d, briefAreaStatementLink: e.target.value }))} style={{ ...INPUT_STYLE, flex: 1 }} placeholder="https://dropbox.com/…" />
                                          {(() => {
                                            const raw = (editDraft.briefAreaStatementLink || '').trim();
                                            if (!raw) return null;
                                            const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
                                            return (
                                              <a href={href} target="_blank" rel="noopener noreferrer"
                                                title="Open in new tab"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--brio-gold)', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                <ExternalLink size={12} /> Open
                                              </a>
                                            );
                                          })()}
                                        </div>
                                      </FormRow>
                                      {intakeAttachments.length > 0 && (
                                        <FormRow label="Attachments" hint="From email intake">
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', paddingTop: '2px' }}>
                                            {intakeAttachments.map((att, i) => (
                                              <AttachmentBriefLink key={i} att={att} getAttachmentUrl={intakeHook.getAttachmentUrl} />
                                            ))}
                                          </div>
                                        </FormRow>
                                      )}
                                    </tbody></table>
                                  </div>
                                )}

                                {/* ── AREA STATEMENT SECTION (#8) ─── */}
                                {/* Excel-style table with versioning. Versions stored in editDraft.areaStatements[].
                                    Each row has a stable ID used by Offers (#10) for linking. */}
                                {drawerSection === 'areaStatement' && (() => {
                                  const versions = getAreaStatements();
                                  const activeIdx = getActiveAreaIdx();
                                  const activeVersion = versions[activeIdx];
                                  const groups = activeVersion.groups || [];
                                  const unitLabel = areaUnit === 'm2' ? 'm²' : 'ft²';
                                  const dA = (v) => displayArea(v, areaUnit);

                                  // Category totals across all groups
                                  let tIndoor = 0, tCvrdOut = 0, tNonCvrd = 0;
                                  groups.forEach(g => (g.subrows || []).forEach(r => {
                                    tIndoor += parseFloat(r.indoor) || 0;
                                    tCvrdOut += parseFloat(r.covered_outdoor) || 0;
                                    tNonCvrd += parseFloat(r.non_covered_outdoor) || 0;
                                  }));
                                  const grandTotal = tIndoor + tCvrdOut + tNonCvrd;

                                  return (
                                    <div>
                                      {/* Version tabs */}
                                      <div style={{ display: 'flex', gap: '3px', marginBottom: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {versions.map((v, i) => (
                                          <button key={v.id || i} onClick={() => setEditDraft(d => ({ ...d, _areaTab: i }))}
                                            style={{
                                              padding: '3px 10px', borderRadius: 0, fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                                              border: activeIdx === i ? '1px solid var(--brio-gold)' : '1px solid var(--brio-border)',
                                              background: activeIdx === i ? 'var(--brio-gold)' : 'transparent',
                                              color: activeIdx === i ? '#fff' : 'var(--brio-text)',
                                            }}>
                                            {v.name || `V${i + 1}`}
                                          </button>
                                        ))}
                                        <button onClick={addAreaVersion}
                                          title="Create new version (copies current)"
                                          style={{ padding: '3px 8px', borderRadius: 0, fontSize: '10px', fontWeight: 500, cursor: 'pointer',
                                            border: '1px dashed var(--brio-border)', background: 'transparent', color: 'var(--brio-muted)' }}>
                                          <Copy style={{ width: '10px', height: '10px', marginRight: '3px', verticalAlign: 'middle' }} />
                                          New Version
                                        </button>
                                      </div>

                                      {/* Version metadata + unit toggle */}
                                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input type="text" value={activeVersion.name || ''} onChange={e => renameAreaVersion(activeIdx, e.target.value)}
                                          style={{ ...INPUT_STYLE, maxWidth: '180px', fontSize: '11px', fontWeight: 500 }} placeholder="Version name…" />
                                        <span style={{ fontSize: '10px', color: 'var(--brio-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                          <Clock style={{ width: '10px', height: '10px' }} />
                                          {activeVersion.createdAt ? fmtDate(activeVersion.createdAt.slice(0, 10)) : '—'}
                                        </span>
                                        {versions.length > 1 && (
                                          <button onClick={() => { if (window.confirm(`Delete "${activeVersion.name || 'this version'}"?`)) deleteAreaVersion(activeIdx); }}
                                            title="Delete this version"
                                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', padding: '2px', display: 'flex', alignItems: 'center' }}>
                                            <Trash2 style={{ width: '11px', height: '11px' }} />
                                          </button>
                                        )}
                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
                                          {/* XLSX Export */}
                                          <button onClick={() => setShowExportDialog(true)}
                                            title="Export to Excel"
                                            style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '2px 8px', borderRadius: 0, fontSize: '10px', fontWeight: 500, cursor: 'pointer',
                                              border: '1px solid var(--brio-border)', background: 'transparent', color: 'var(--brio-text)' }}>
                                            <Download style={{ width: '10px', height: '10px' }} /> XLSX
                                          </button>
                                          {/* XLSX Import */}
                                          <button onClick={() => xlsxFileRef.current?.click()}
                                            disabled={xlsxImporting}
                                            title="Import from Excel"
                                            style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '2px 8px', borderRadius: 0, fontSize: '10px', fontWeight: 500, cursor: 'pointer',
                                              border: '1px solid var(--brio-border)', background: 'transparent', color: 'var(--brio-text)' }}>
                                            <Upload style={{ width: '10px', height: '10px' }} /> {xlsxImporting ? 'Importing…' : 'Import'}
                                          </button>
                                          <input ref={xlsxFileRef} type="file" accept=".xlsx" onChange={handleXlsxImport} style={{ display: 'none' }} />
                                          {/* Unit toggle */}
                                          <div style={{ display: 'flex', borderRadius: '3px', border: '1px solid var(--brio-border)', overflow: 'hidden' }}>
                                            {['sqft', 'm2'].map(u => (
                                              <button key={u} onClick={() => setAreaUnit(u)} style={{
                                                padding: '2px 8px', fontSize: '10px', fontWeight: 500, cursor: 'pointer', border: 'none',
                                                background: areaUnit === u ? 'var(--brio-gold)' : 'transparent',
                                                color: areaUnit === u ? '#fff' : 'var(--brio-muted)',
                                              }}>{u === 'sqft' ? 'ft²' : 'm²'}</button>
                                            ))}
                                          </div>
                                        </div>
                                      </div>

                                      {/* (#8) Compact spreadsheet — BRIO table code=1b styling, colgroup for alignment */}
                                      <table className="brio-table brio-table--dense" style={{ fontSize: '11px', tableLayout: 'fixed' }}>
                                        <colgroup>
                                          <col style={{ width: '24px' }} />{/* expand/collapse or drag */}
                                          <col style={{ width: '36px' }} />{/* No. */}
                                          <col />{/* Program (flex) */}
                                          <col style={{ width: '72px' }} />{/* Indoor */}
                                          <col style={{ width: '72px' }} />{/* Covered Outdoor */}
                                          <col style={{ width: '72px' }} />{/* Non-Covered Outdoor */}
                                          <col style={{ width: '40px' }} />{/* actions */}
                                        </colgroup>
                                        <thead>
                                          <tr>
                                            <th style={{ padding: '3px' }}></th>
                                            <th style={{ textAlign: 'center' }}>No.</th>
                                            <th style={{ textAlign: 'left' }}>Program</th>
                                            <th className="text-right">Indoor<br/><span style={{ fontWeight: 400, fontSize: '9px', color: 'var(--brio-muted)' }}>{unitLabel}</span></th>
                                            <th className="text-right">Cvrd Outdr<br/><span style={{ fontWeight: 400, fontSize: '9px', color: 'var(--brio-muted)' }}>{unitLabel}</span></th>
                                            <th className="text-right">Non-Cvrd<br/><span style={{ fontWeight: 400, fontSize: '9px', color: 'var(--brio-muted)' }}>{unitLabel}</span></th>
                                            <th></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {groups.map((grp, gIdx) => {
                                            // Group subtotals
                                            let gIn = 0, gCo = 0, gNco = 0;
                                            (grp.subrows || []).forEach(r => {
                                              gIn += parseFloat(r.indoor) || 0;
                                              gCo += parseFloat(r.covered_outdoor) || 0;
                                              gNco += parseFloat(r.non_covered_outdoor) || 0;
                                            });
                                            return (
                                              <React.Fragment key={grp.id}>
                                                {/* Group header row */}
                                                <tr style={{ background: 'var(--brio-gold-light, rgba(159,144,42,0.08))' }}>
                                                  <td style={{ padding: '2px 2px', textAlign: 'center' }}>
                                                    <span style={{ cursor: 'pointer' }} onClick={() => toggleGroupCollapse(activeIdx, gIdx)}>
                                                      {grp.collapsed ? <ChevronDown style={{ width: '12px', height: '12px' }} /> : <ChevronUp style={{ width: '12px', height: '12px' }} />}
                                                    </span>
                                                  </td>
                                                  <td style={{ padding: '2px 4px', fontWeight: 700, textAlign: 'center' }}>
                                                    <input type="text" value={grp.code || ''} onChange={e => updateAreaGroup(activeIdx, gIdx, 'code', e.target.value.toUpperCase())}
                                                      style={{ ...INPUT_STYLE, width: '28px', padding: '1px 2px', fontSize: '11px', fontWeight: 700, textAlign: 'center', background: 'transparent', border: '1px solid transparent' }}
                                                      onFocus={e => { e.target.style.border = '1px solid var(--brio-border)'; }}
                                                      onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                                                    />
                                                  </td>
                                                  <td style={{ padding: '2px 4px', fontWeight: 500 }}>
                                                    <input type="text" value={grp.label || ''} onChange={e => updateAreaGroup(activeIdx, gIdx, 'label', e.target.value)}
                                                      style={{ ...INPUT_STYLE, padding: '1px 4px', fontSize: '11px', fontWeight: 500, background: 'transparent', border: '1px solid transparent' }}
                                                      placeholder="Group name…"
                                                      onFocus={e => { e.target.style.border = '1px solid var(--brio-border)'; }}
                                                      onBlur={e => { e.target.style.border = '1px solid transparent'; }}
                                                    />
                                                  </td>
                                                  <td className="text-right" style={{ fontWeight: 500, fontSize: '10px', color: 'var(--brio-gold)' }}>{gIn > 0 ? dA(gIn) : ''}</td>
                                                  <td className="text-right" style={{ fontWeight: 500, fontSize: '10px', color: 'var(--brio-gold)' }}>{gCo > 0 ? dA(gCo) : ''}</td>
                                                  <td className="text-right" style={{ fontWeight: 500, fontSize: '10px', color: 'var(--brio-gold)' }}>{gNco > 0 ? dA(gNco) : ''}</td>
                                                  <td style={{ padding: '1px', whiteSpace: 'nowrap' }}>
                                                    {gIdx > 0 && <button onClick={() => moveGroup(activeIdx, gIdx, -1)} title="Move up" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 1px', color: 'var(--brio-muted)' }}>▲</button>}
                                                    {gIdx < groups.length - 1 && <button onClick={() => moveGroup(activeIdx, gIdx, 1)} title="Move down" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 1px', color: 'var(--brio-muted)' }}>▼</button>}
                                                    <button onClick={() => removeAreaGroup(activeIdx, gIdx)} title="Remove group"
                                                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '12px', padding: '0 1px' }}>×</button>
                                                  </td>
                                                </tr>

                                                {/* Subrows (visible when not collapsed) */}
                                                {!grp.collapsed && (grp.subrows || []).map((row, sIdx) => (
                                                  <tr key={row.id || sIdx}>
                                                    <td style={{ padding: '1px', textAlign: 'center' }}>
                                                      {sIdx > 0 && <button onClick={() => moveSubrow(activeIdx, gIdx, sIdx, -1)} title="Move up" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '8px', padding: 0, color: 'var(--brio-muted)' }}>▲</button>}
                                                      {sIdx < (grp.subrows || []).length - 1 && <button onClick={() => moveSubrow(activeIdx, gIdx, sIdx, 1)} title="Move down" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '8px', padding: 0, color: 'var(--brio-muted)' }}>▼</button>}
                                                    </td>
                                                    <td style={{ padding: '1px 4px', textAlign: 'center' }}>
                                                      <input type="text" value={row.num || ''} onChange={e => updateSubrow(activeIdx, gIdx, sIdx, 'num', e.target.value)}
                                                        style={{ ...INPUT_STYLE, width: '32px', padding: '1px 2px', fontSize: '10px', textAlign: 'center' }} />
                                                    </td>
                                                    <td style={{ padding: '1px 3px' }}>
                                                      <input type="text" value={row.label || ''} onChange={e => updateSubrow(activeIdx, gIdx, sIdx, 'label', e.target.value)}
                                                        style={{ ...INPUT_STYLE, padding: '1px 4px', fontSize: '11px' }} placeholder="Room / area…" />
                                                    </td>
                                                    <td style={{ padding: '1px 2px' }}>
                                                      <input type="number" value={dA(row.indoor)} onChange={e => updateSubrow(activeIdx, gIdx, sIdx, 'indoor', e.target.value)}
                                                        style={{ ...INPUT_STYLE, padding: '1px 3px', fontSize: '11px', textAlign: 'right' }} />
                                                    </td>
                                                    <td style={{ padding: '1px 2px' }}>
                                                      <input type="number" value={dA(row.covered_outdoor)} onChange={e => updateSubrow(activeIdx, gIdx, sIdx, 'covered_outdoor', e.target.value)}
                                                        style={{ ...INPUT_STYLE, padding: '1px 3px', fontSize: '11px', textAlign: 'right' }} />
                                                    </td>
                                                    <td style={{ padding: '1px 2px' }}>
                                                      <input type="number" value={dA(row.non_covered_outdoor)} onChange={e => updateSubrow(activeIdx, gIdx, sIdx, 'non_covered_outdoor', e.target.value)}
                                                        style={{ ...INPUT_STYLE, padding: '1px 3px', fontSize: '11px', textAlign: 'right' }} />
                                                    </td>
                                                    <td>
                                                      <button onClick={() => removeSubrow(activeIdx, gIdx, sIdx)} title="Remove row"
                                                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '12px', padding: '1px' }}>×</button>
                                                    </td>
                                                  </tr>
                                                ))}

                                                {/* Add subrow button */}
                                                {!grp.collapsed && (
                                                  <tr>
                                                    <td></td>
                                                    <td colSpan="6" style={{ padding: '2px 4px' }}>
                                                      <button onClick={() => addSubrow(activeIdx, gIdx)}
                                                        style={{ fontSize: '10px', padding: '1px 6px', border: '1px dashed var(--brio-border)', background: 'transparent', cursor: 'pointer', color: 'var(--brio-muted)', borderRadius: '3px' }}>
                                                        + Row
                                                      </button>
                                                    </td>
                                                  </tr>
                                                )}

                                                {/* ── Group summary block: carpet area → walls & circ → computed → constructed ── */}
                                                {(() => {
                                                  const carpetArea = gIn + gCo + gNco;
                                                  const wcPct = getGroupWallsPct(grp);
                                                  const wallsAmount = gIn * (wcPct / 100);  // applies only to indoor
                                                  const constructedArea = carpetArea + wallsAmount;
                                                  const summaryRowStyle = { background: 'var(--brio-gold-light, rgba(159,144,42,0.05))', borderTop: '1px solid var(--brio-border, #e2e8f0)' };
                                                  const summaryLabelStyle = { padding: '2px 4px', fontWeight: 500, fontSize: '10px', color: 'var(--brio-text)', textTransform: 'lowercase' };
                                                  const summaryValStyle = { textAlign: 'right', fontWeight: 500, fontSize: '10px', color: 'var(--brio-gold)', padding: '2px 4px' };
                                                  return (
                                                    <>
                                                      {/* A1: Carpet area subtotal row (renamed from "subtotal") */}
                                                      <tr style={summaryRowStyle}>
                                                        <td></td>
                                                        <td></td>
                                                        <td style={summaryLabelStyle}>carpet area</td>
                                                        <td style={summaryValStyle}>{gIn > 0 ? dA(gIn) : ''}</td>
                                                        <td style={summaryValStyle}>{gCo > 0 ? dA(gCo) : ''}</td>
                                                        <td style={summaryValStyle}>{gNco > 0 ? dA(gNco) : ''}</td>
                                                        <td></td>
                                                      </tr>
                                                      {/* A2+A3: Walls and circulation — editable % input */}
                                                      <tr style={{ background: 'var(--brio-gold-light, rgba(159,144,42,0.03))' }}>
                                                        <td></td>
                                                        <td></td>
                                                        <td style={{ ...summaryLabelStyle, fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                          <span>walls and circulation</span>
                                                          <input
                                                            type="number"
                                                            min="0" max="100" step="0.1"
                                                            value={wcPct || ''}
                                                            onChange={e => updateAreaGroup(activeIdx, gIdx, 'wallsCirculationPct', e.target.value)}
                                                            placeholder="0"
                                                            style={{ ...INPUT_STYLE, width: '48px', padding: '1px 3px', fontSize: '10px', textAlign: 'right' }}
                                                          />
                                                          <span style={{ fontSize: '9px', color: 'var(--brio-muted)' }}>%</span>
                                                        </td>
                                                        <td colSpan="3" style={{ ...summaryValStyle, fontSize: '10px' }}>
                                                          {wallsAmount > 0 ? dA(wallsAmount) : ''}
                                                          {wallsAmount > 0 && <span style={{ fontSize: '9px', color: 'var(--brio-muted)', marginLeft: '3px' }}>{unitLabel}</span>}
                                                        </td>
                                                        <td></td>
                                                      </tr>
                                                      {/* A4: Constructed area = carpet + walls */}
                                                      <tr style={{ ...summaryRowStyle, borderTop: '1.5px solid var(--brio-gold, #9F902A)' }}>
                                                        <td></td>
                                                        <td></td>
                                                        <td style={{ ...summaryLabelStyle, fontWeight: 700, fontSize: '10px', color: 'var(--brio-gold)' }}>constructed area</td>
                                                        <td colSpan="3" style={{ ...summaryValStyle, fontWeight: 700, fontSize: '11px' }}>
                                                          {constructedArea > 0 ? `${dA(constructedArea)} ${unitLabel}` : '—'}
                                                        </td>
                                                        <td></td>
                                                      </tr>
                                                    </>
                                                  );
                                                })()}
                                              </React.Fragment>
                                            );
                                          })}

                                          {/* Category totals */}
                                          <tr style={{ borderTop: '2px solid var(--brio-gold)' }}>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 700, fontSize: '11px', padding: '3px 4px' }}>Total (carpet)</td>
                                            <td className="text-right" style={{ fontWeight: 700, color: 'var(--brio-gold)' }}>{tIndoor > 0 ? dA(tIndoor) : '—'}</td>
                                            <td className="text-right" style={{ fontWeight: 700, color: 'var(--brio-gold)' }}>{tCvrdOut > 0 ? dA(tCvrdOut) : '—'}</td>
                                            <td className="text-right" style={{ fontWeight: 700, color: 'var(--brio-gold)' }}>{tNonCvrd > 0 ? dA(tNonCvrd) : '—'}</td>
                                            <td></td>
                                          </tr>
                                          <tr>
                                            <td></td>
                                            <td></td>
                                            <td style={{ fontWeight: 700, fontSize: '12px', padding: '2px 4px' }}>Grand Total (carpet)</td>
                                            <td colSpan="3" className="text-right" style={{ fontWeight: 700, fontSize: '12px', color: 'var(--brio-gold)', padding: '2px 6px' }}>
                                              {grandTotal > 0 ? `${dA(grandTotal)} ${unitLabel}` : '—'}
                                            </td>
                                            <td></td>
                                          </tr>
                                          {/* Grand total constructed area */}
                                          {(() => {
                                            const grandConstructed = groups.reduce((s, g) => s + getGroupConstructedArea(g), 0);
                                            return grandConstructed !== grandTotal ? (
                                              <tr>
                                                <td></td>
                                                <td></td>
                                                <td style={{ fontWeight: 700, fontSize: '12px', padding: '2px 4px', color: 'var(--brio-gold)' }}>Grand Total (constructed)</td>
                                                <td colSpan="3" className="text-right" style={{ fontWeight: 700, fontSize: '12px', color: 'var(--brio-gold)', padding: '2px 6px' }}>
                                                  {grandConstructed > 0 ? `${dA(grandConstructed)} ${unitLabel}` : '—'}
                                                </td>
                                                <td></td>
                                              </tr>
                                            ) : null;
                                          })()}
                                        </tbody>
                                      </table>

                                      {/* Add group */}
                                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                                        <button onClick={() => addAreaGroup(activeIdx)}
                                          style={{ fontSize: '11px', padding: '2px 8px', borderRadius: 0, border: '1px solid var(--brio-border)', background: 'transparent', cursor: 'pointer', color: 'var(--brio-text)' }}>
                                          + Add Group
                                        </button>
                                      </div>

                                      {/* XLSX import errors/warnings */}
                                      {xlsxErrors && xlsxErrors.length > 0 && (
                                        <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(147, 73, 72, 0.08)', border: '1px solid var(--brio-red)', borderRadius: 0, fontSize: '10px' }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                            <span style={{ fontWeight: 500, color: 'var(--brio-red)' }}>Import warnings</span>
                                            <button onClick={() => setXlsxErrors(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '12px' }}>×</button>
                                          </div>
                                          {xlsxErrors.map((err, i) => (
                                            <div key={i} style={{ color: '#7f1d1d', padding: '1px 0' }}>• {err}</div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Version history */}
                                      {versions.length > 1 && (
                                        <div style={{ marginTop: '10px', borderTop: '1px solid var(--brio-border)', paddingTop: '6px' }}>
                                          <div style={{ fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', color: 'var(--brio-muted)', marginBottom: '3px' }}>Version History</div>
                                          {versions.map((v, i) => (
                                            <div key={v.id || i} style={{
                                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                              padding: '2px 6px', fontSize: '10px', borderRadius: '3px', cursor: 'pointer',
                                              background: i === activeIdx ? 'var(--brio-bg-subtle, #fafaf6)' : 'transparent',
                                              fontWeight: i === activeIdx ? 600 : 400,
                                            }} onClick={() => setEditDraft(d => ({ ...d, _areaTab: i }))}>
                                              <span>{v.name || `Version ${i + 1}`}</span>
                                              <span style={{ color: 'var(--brio-muted)' }}>
                                                {v.createdAt ? fmtDate(v.createdAt.slice(0, 10)) : '—'} · {(v.groups || []).length} groups
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* ── FEE SCENARIOS SECTION ─── */}
                                {drawerSection === 'scenarios' && (() => {
                                  const scenarios = getScenarios();
                                  const activeScenarioIdx = editDraft._scenarioTab ?? 0;
                                  const sc = scenarios[activeScenarioIdx] || blankScenario('');
                                  const areaStmt = getActiveArea();
                                  const areaGroups = areaStmt?.groups || [];

                                  // groupRates: { [groupId]: rateSqft } — migrate from legacy rows if needed
                                  const scGroupRates = sc.groupRates || {};

                                  return (
                                    <div>
                                      <div style={{ display: 'flex', gap: '3px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {scenarios.map((s, i) => (
                                          <button key={i} onClick={() => setEditDraft(d => ({ ...d, _scenarioTab: i }))}
                                            style={{
                                              padding: '3px 10px', borderRadius: 0, fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                                              border: activeScenarioIdx === i ? '1px solid var(--brio-gold)' : '1px solid var(--brio-border)',
                                              background: activeScenarioIdx === i ? 'var(--brio-gold)' : 'transparent',
                                              color: activeScenarioIdx === i ? '#fff' : 'var(--brio-text)',
                                            }}>
                                            {s.name || `Scenario ${i + 1}`}
                                          </button>
                                        ))}
                                        <button onClick={addScenario}
                                          style={{ padding: '3px 8px', borderRadius: 0, fontSize: '10px', fontWeight: 500, cursor: 'pointer',
                                            border: '1px dashed var(--brio-border)', background: 'transparent', color: 'var(--brio-muted)' }}>
                                          + Add
                                        </button>
                                        {scenarios.length > 1 && (
                                          <button onClick={() => { if (window.confirm(`Delete "${sc.name || 'this scenario'}"?`)) deleteScenario(activeScenarioIdx); }}
                                            title="Delete this scenario"
                                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', padding: '2px', display: 'flex', alignItems: 'center' }}>
                                            <Trash2 style={{ width: '11px', height: '11px' }} />
                                          </button>
                                        )}
                                        {/* Unit toggle */}
                                        <div style={{ marginLeft: 'auto', display: 'flex', borderRadius: '3px', border: '1px solid var(--brio-border)', overflow: 'hidden' }}>
                                          {['sqft', 'm2'].map(u => (
                                            <button key={u} onClick={() => setAreaUnit(u)} style={{
                                              padding: '2px 8px', fontSize: '10px', fontWeight: 500, cursor: 'pointer', border: 'none',
                                              background: areaUnit === u ? 'var(--brio-gold)' : 'transparent',
                                              color: areaUnit === u ? '#fff' : 'var(--brio-muted)',
                                            }}>{u === 'sqft' ? 'ft²' : 'm²'}</button>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Scenario name */}
                                      <div style={{ marginBottom: '6px' }}>
                                        <input type="text" value={sc.name || ''} onChange={e => {
                                          const allScenarios = getScenarios();
                                          allScenarios[activeScenarioIdx] = { ...allScenarios[activeScenarioIdx], name: e.target.value };
                                          setEditDraft(d => ({ ...d, feeScenarios: allScenarios }));
                                        }} style={{ ...INPUT_STYLE, maxWidth: '200px', fontSize: '11px', fontWeight: 500 }} placeholder="Scenario name…" />
                                      </div>

                                      {/* Fee table — one row per area group */}
                                      <AreaGroupFeeTable
                                        areaGroups={areaGroups}
                                        areaUnit={areaUnit}
                                        groupRates={scGroupRates}
                                        onGroupRateChange={(groupId, rate) => {
                                          const allScenarios = getScenarios();
                                          const updated = { ...allScenarios[activeScenarioIdx] };
                                          updated.groupRates = { ...(updated.groupRates || {}), [groupId]: rate };
                                          allScenarios[activeScenarioIdx] = updated;
                                          setEditDraft(d => ({ ...d, feeScenarios: allScenarios }));
                                        }}
                                        fmtCurr={fmtCurr}
                                      />
                                    </div>
                                  );
                                })()}

                                {/* ── FEE INTELLIGENCE / HISTORICAL (#12) ─── */}
                                {drawerSection === 'historical' && (() => {
                                  const includedComps = feeComparables.filter(c => c.include);
                                  const hasModel = Boolean(activeFeeModelId);
                                  const fc = feeComputed || {};
                                  // Defensive project size getter (handles camelCase + snake_case)
                                  const sz = (p) => parseFloat(p?.projectSize || p?.project_size) || 0;
                                  const fmtRate = (v) => v > 0 ? (fmtCurr ? fmtCurr(v) : v.toFixed(0)) : '—';
                                  const unitLabel = fc?.unit === 'sqft' ? '/ft²' : '/m²';

                                  // Model selector + hero band + formula strip are rendered as one
                                  // "The Answer" section so the headline numbers sit at the very top
                                  // of the tab, with the model picker reduced to a compact control in
                                  // the section header.
                                  return (
                                  <div>
                                    {/* ─── Error banner ───────────────────────────── */}
                                    {feeError && (
                                      <div style={{
                                        fontSize: '11px', color: 'var(--brio-red)', padding: '6px 8px',
                                        background: 'rgba(147, 73, 72, 0.08)', borderRadius: 0, marginBottom: '6px',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      }}>
                                        <span>⚠ {feeError}</span>
                                        <button onClick={() => setFeeError(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '14px', padding: 0 }}>×</button>
                                      </div>
                                    )}

                                    {/* ─── 1. THE ANSWER — hero + formula strip ─── */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <div style={FB_SECTION_LABEL}>
                                        <span>The Answer</span>
                                        <span style={{ display: 'flex', gap: '6px', alignItems: 'center', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                                          <span style={{ color: 'var(--brio-muted)' }}>Model</span>
                                          {feeLoading && (<span style={{ fontSize: '10px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>Loading…</span>)}
                                          {feeModels.length > 0 ? (
                                            <select
                                              value={activeFeeModelId || ''}
                                              onChange={e => {
                                                const mid = e.target.value || null;
                                                setActiveFeeModelId(mid);
                                                if (mid) loadFeeModelData(mid);
                                                else { setFeeComparables([]); setFeeFactors({}); setFeeComputed(null); }
                                              }}
                                              style={{ ...SELECT_STYLE, maxWidth: '180px', fontSize: '11px', padding: '3px 8px' }}
                                            >
                                              <option value="">— Select —</option>
                                              {feeModels.map(m => (<option key={m.id} value={m.id}>{m.name || 'Untitled'}</option>))}
                                            </select>
                                          ) : !feeLoading ? (
                                            <span style={{ fontSize: '11px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>No models yet</span>
                                          ) : null}
                                          <button
                                            onClick={handleCreateFeeModel}
                                            disabled={feeLoading}
                                            style={{
                                              fontSize: '10px', padding: '3px 8px', borderRadius: 0,
                                              border: '1px dashed var(--brio-border)', background: 'transparent',
                                              cursor: feeLoading ? 'wait' : 'pointer',
                                              color: 'var(--brio-gold)', fontWeight: 500,
                                              opacity: feeLoading ? 0.5 : 1,
                                            }}
                                          >{feeLoading ? 'Creating…' : '+ New Model'}</button>
                                        </span>
                                      </div>

                                      {!hasModel ? (
                                        <div style={{ border: '1px solid var(--brio-border)', padding: '14px 16px', fontSize: '12px', color: 'var(--brio-muted)' }}>
                                          Create or select a fee model to see recommendations.
                                        </div>
                                      ) : (() => {
                                        const comp = fc?.comparables || {};
                                        const mult = fc?.multipliers || {};
                                        const rec  = fc?.recommended || {};
                                        const compCount = comp.count ?? 0;
                                        const baseRate  = parseFloat(comp.baseRatePerM2) || 0;
                                        const clampedM  = parseFloat(mult.clamped ?? mult.raw) || 1;
                                        const recRate   = parseFloat(rec.ratePerM2) || 0;
                                        const minRate   = parseFloat(comp.min) || 0;
                                        const maxRate   = parseFloat(comp.max) || 0;
                                        const medRate   = parseFloat(comp.median) || 0;

                                        // Cost metrics
                                        const lcm = loadedCostMetrics || {};
                                        const costPerM2Nominal = parseFloat(lcm.avgLoadedCostPerM2) || 0;
                                        const costPerUnit = (fc?.unit === 'sqft' && costPerM2Nominal > 0)
                                          ? (costPerM2Nominal / SQFT_PER_M2)
                                          : costPerM2Nominal;
                                        const nCostSamples = lcm.sampleCount ?? 0;
                                        const skippedNoCost = lcm.skippedNoCost ?? 0;
                                        const skippedNotCompleted = lcm.skippedNotCompleted ?? 0;
                                        const isCostLoading = loadedCostMetricsLoading || projectLoadedCostsLoading;

                                        // Inflation
                                        const inflMult = parseFloat(fc?.comparables?.inflationAvgMultiplier);
                                        const hasInfl = !isNaN(inflMult);
                                        const inflPct = hasInfl ? (inflMult - 1) * 100 : null;
                                        const currentYear = new Date().getFullYear();
                                        const cc = fc?.inflation?.countryCode ?? null;
                                        const countryName = cc === 'IN' ? 'India' : cc === 'NL' ? 'Netherlands' : (cc || '');

                                        // Conversion (comparable-driven, with inquiry-level fallback)
                                        const met = fc?.metrics || {};
                                        const convProbRaw = parseFloat(met.avgConversionProbability);
                                        const convN    = met.conversionSampleCount ?? 0;
                                        const hasConv  = !isNaN(convProbRaw) && convN > 0;
                                        const iq = inquiryConvProb;
                                        const hasIq = !hasConv && iq && iq.probability != null;
                                        const showProb = hasConv ? convProbRaw : hasIq ? iq.probability : null;
                                        const showConf = hasConv ? (met.conversionConfidence || 'low') : hasIq ? iq.confidence : 'low';
                                        const showN    = hasConv ? convN : hasIq ? iq.n_total : 0;
                                        const showMeth = hasConv ? met.conversionMethod : hasIq ? iq.method : null;

                                        // Margin (fee vs cost)
                                        const hasMargin = recRate > 0 && costPerUnit > 0;
                                        const marginPct = hasMargin ? ((recRate - costPerUnit) / recRate) * 100 : null;

                                        return (
                                          <>
                                            {/* Hero band — both cells plain white. Explicit background:#fff
                                                so the cells read as white regardless of the drawer's
                                                page background. */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', border: '1px solid var(--brio-border)', background: '#fff' }}>
                                              <div style={{ padding: '14px 16px', borderRight: '1px solid var(--brio-border)', background: '#fff' }}>
                                                <div style={FB_HERO_LABEL}>Recommended Fee</div>
                                                {!feeComputed ? (
                                                  <div style={{ fontSize: '12px', color: 'var(--brio-muted)', fontStyle: 'italic', padding: '4px 0' }}>Select comparables to compute.</div>
                                                ) : compCount === 0 ? (
                                                  <div style={{ fontSize: '12px', color: 'var(--brio-orange, #DB6B5D)', padding: '4px 0' }}>No valid comparables (need budget + project_size).</div>
                                                ) : (
                                                  <>
                                                    <div style={{ fontSize: '26px', fontWeight: 700, color: 'var(--brio-gold)', lineHeight: 1.0, fontVariantNumeric: 'tabular-nums' }}>
                                                      {fmtRate(recRate)}<span style={{ fontSize: '13px', color: 'var(--brio-muted)', fontWeight: 500 }}>{unitLabel}</span>
                                                    </div>
                                                    <div style={{ marginTop: '6px', display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--brio-muted)', fontVariantNumeric: 'tabular-nums' }}>
                                                      {(minRate > 0 || maxRate > 0) && (
                                                        <span>Range <strong style={{ color: 'var(--brio-text)', fontWeight: 600 }}>{fmtRate(minRate)} – {fmtRate(maxRate)}</strong></span>
                                                      )}
                                                      {medRate > 0 && (
                                                        <span>Median <strong style={{ color: 'var(--brio-text)', fontWeight: 600 }}>{fmtRate(medRate)}</strong></span>
                                                      )}
                                                      <span>n = <strong style={{ color: 'var(--brio-text)', fontWeight: 600 }}>{compCount}</strong>{(() => {
                                                        const optedOut = includedComps.filter(c => c.purpose?.fee === false).length;
                                                        return optedOut > 0 ? ` (${optedOut} opted out)` : '';
                                                      })()}</span>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                              <div style={{ padding: '14px 16px', background: '#fff' }}>
                                                <div style={FB_HERO_LABEL}>Recommended Cost</div>
                                                {costPerUnit > 0 ? (
                                                  <>
                                                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#ea580c', lineHeight: 1.0, fontVariantNumeric: 'tabular-nums' }}>
                                                      {fmtRate(costPerUnit)}<span style={{ fontSize: '12px', color: 'var(--brio-muted)', fontWeight: 500 }}>{unitLabel}</span>
                                                    </div>
                                                    <div style={{ marginTop: '6px', display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '11px', color: 'var(--brio-muted)', alignItems: 'center' }}>
                                                      {hasMargin && (
                                                        <span>Margin <strong style={{ color: marginPct >= 0 ? 'var(--brio-green)' : 'var(--brio-red)', fontWeight: 600 }}>{marginPct >= 0 ? '+' : ''}{marginPct.toFixed(1)}%</strong></span>
                                                      )}
                                                      <span>
                                                        n = <strong style={{ color: 'var(--brio-text)', fontWeight: 600 }}>{nCostSamples}</strong>
                                                        {Array.isArray(lcm.contributors) && lcm.contributors.length > 0 && (
                                                          <> ({lcm.contributors.map((c, i) => (
                                                            <React.Fragment key={c.project_id}>
                                                              {i > 0 && ', '}
                                                              <span
                                                                title={`${c.name || ''} · ${c.project_start_year || '—'} · ${fmtRate(parseFloat(c.cost_per_m2_inflated) || 0)}${unitLabel} (×${parseFloat(c.inflation_multiplier).toFixed(2)} inflation)`}
                                                                style={{ textDecoration: 'underline dotted', textUnderlineOffset: '2px' }}
                                                              >{c.project_code || `#${c.project_id}`}</span>
                                                            </React.Fragment>
                                                          ))})</>
                                                        )}
                                                      </span>
                                                      <button
                                                        type="button"
                                                        onClick={handleRefreshCosts}
                                                        disabled={refreshingCosts || typeof refreshProjectLoadedCosts !== 'function'}
                                                        title="Recompute loaded cost cache"
                                                        style={{ border: 'none', background: 'none', padding: 0, color: refreshingCosts ? 'var(--brio-muted)' : 'var(--brio-gold)', cursor: refreshingCosts ? 'wait' : 'pointer', fontSize: '10px', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                                                      >{refreshingCosts ? '↻ Refreshing…' : '↻ Refresh'}</button>
                                                    </div>
                                                  </>
                                                ) : isCostLoading ? (
                                                  <div style={{ fontSize: '12px', color: 'var(--brio-muted)', fontStyle: 'italic', padding: '4px 0' }}>Loading cost benchmark…</div>
                                                ) : (
                                                  <>
                                                    <div style={{ fontSize: '20px', color: 'var(--brio-muted)', lineHeight: 1.0, padding: '4px 0' }}>—</div>
                                                    <div style={{ fontSize: '10px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>
                                                      {skippedNoCost > 0
                                                        ? <>{skippedNoCost} eligible comparable{skippedNoCost !== 1 ? 's' : ''} with no cached cost · <button type="button" onClick={handleRefreshCosts} disabled={refreshingCosts} style={{ border: 'none', background: 'none', color: 'var(--brio-gold)', cursor: 'pointer', fontSize: '10px', padding: 0, fontWeight: 500, textDecoration: 'underline' }}>{refreshingCosts ? 'refreshing…' : '↻ refresh'}</button></>
                                                        : skippedNotCompleted > 0 ? `${skippedNotCompleted} included but not completed`
                                                        : 'tick CTC on completed comparables to compute'}
                                                    </div>
                                                  </>
                                                )}
                                                {refreshCostsError && !refreshingCosts && (
                                                  <div style={{ fontSize: '9px', color: 'var(--brio-red)', fontStyle: 'italic', marginTop: '2px' }}>Refresh failed: {refreshCostsError}</div>
                                                )}
                                              </div>
                                            </div>

                                            {/* Formula strip */}
                                            {feeComputed && compCount > 0 && (
                                              <div style={{ borderLeft: '1px solid var(--brio-border)', borderRight: '1px solid var(--brio-border)', borderBottom: '1px solid var(--brio-border)', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', fontSize: '11px', background: '#fff' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontVariantNumeric: 'tabular-nums' }}>
                                                  <span style={FB_TERM}>
                                                    <span style={FB_TLABEL}>Base Avg</span>
                                                    <span style={FB_TVAL}>{fmtRate(baseRate)}{unitLabel}</span>
                                                  </span>
                                                  <span style={FB_OP}>×</span>
                                                  <span style={FB_TERM}>
                                                    <span style={FB_TLABEL}>Adjustment</span>
                                                    <span style={FB_TVAL}>{clampedM.toFixed(3)}</span>
                                                  </span>
                                                  <span style={FB_OP}>=</span>
                                                  <span style={FB_TERM}>
                                                    <span style={FB_TLABEL}>Recommended</span>
                                                    <span style={{ ...FB_TVAL, color: 'var(--brio-gold)', fontWeight: 700 }}>{fmtRate(recRate)}{unitLabel}</span>
                                                  </span>
                                                </span>
                                                <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                                  {hasInfl && (
                                                    <span
                                                      style={FB_CHIP}
                                                      title={`Base years (comparables): ${fc?.inflation?.sourceYearMin ?? '?'}${fc?.inflation?.sourceYearMax && fc?.inflation?.sourceYearMin !== fc?.inflation?.sourceYearMax ? '–' + fc.inflation.sourceYearMax : ''}${inflationYear ? ` · CPI data through ${inflationYear}` : ''}`}
                                                    >
                                                      Inflation <strong style={{ color: 'var(--brio-gold)', fontWeight: 600 }}>{inflPct >= 0 ? '+' : ''}{inflPct.toFixed(1)}%</strong>{countryName ? ` · ${countryName}` : ''} → {currentYear}
                                                    </span>
                                                  )}
                                                  {showProb != null && (
                                                    <span
                                                      style={FB_CHIP}
                                                      title={`Method: ${showMeth === 'bayesian_smoothed' ? 'Bayesian' : (showMeth || 'historical')}`}
                                                    >
                                                      Conversion <strong style={{ color: showProb >= 0.5 ? 'var(--brio-green)' : 'var(--brio-orange, #DB6B5D)', fontWeight: 600 }}>{(showProb * 100).toFixed(1)}%</strong> · {showConf} (n={showN})
                                                    </span>
                                                  )}
                                                  {(comp.sumSimilarityWeights != null || comp.avgRecencyWeight != null) && (
                                                    <span
                                                      style={{ ...FB_CHIP, color: 'var(--brio-muted)' }}
                                                      title={`Σ sim ${comp.sumSimilarityWeights != null ? parseFloat(comp.sumSimilarityWeights).toFixed(2) : '?'}${comp.sumFinalWeights != null ? ` · Σ final ${parseFloat(comp.sumFinalWeights).toFixed(2)}` : ''}${comp.avgRecencyWeight != null ? ` · avg recency ${parseFloat(comp.avgRecencyWeight).toFixed(2)}` : ''}`}
                                                    >Weights</span>
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>

                                    {/* ─── 2. COMPARABLES — unified table ─── */}
                                    {hasModel && (() => {
                                      // Build unified rows: every suggested project plus any included
                                      // comparable that isn't in the suggestion list (e.g. manual adds).
                                      // The include checkbox is the "in/out of model" control; Purpose
                                      // pills (Fee/CTC) remain a separate column so it stays clear they
                                      // toggle independently from inclusion.
                                      const rowMap = new Map();
                                      (suggestedComparables || []).forEach(p => {
                                        const pid = String(p.id);
                                        const pSize = p._pSize || sz(p);
                                        const rate = p._ratePerM2 > 0 ? p._ratePerM2 : (pSize > 0 ? (p.budget / pSize) : 0);
                                        const ic = feeComparables.find(c => String(c.project_id) === pid);
                                        const isIncluded = !!ic && ic.include;
                                        const purpose = ic?.purpose || { fee: false, ctc: false };
                                        const geo = ic?.similarity_breakdown?.geo || {};
                                        rowMap.set(pid, {
                                          projectId: p.id,
                                          name: p.projectCode ? `${p.projectCode} — ${p.name}` : p.name,
                                          similarity: typeof p._similarity === 'number' ? p._similarity : (ic?.similarity_score ?? null),
                                          score: p._score,
                                          rate,
                                          size: pSize,
                                          status: p.status || ic?.projectStatus || null,
                                          geo,
                                          isIncluded,
                                          feeOn: isIncluded && purpose.fee !== false,
                                          ctcOn: isIncluded && purpose.ctc === true,
                                          ctcLocked: (p.status || ic?.projectStatus || '') !== 'completed',
                                        });
                                      });
                                      // Pull in manually-included rows that didn't show up in suggestions.
                                      includedComps.forEach(c => {
                                        const pid = String(c.project_id);
                                        if (rowMap.has(pid)) return;
                                        const cSize = sz(c);
                                        const rate = cSize > 0 ? (c.projectBudget / cSize) : 0;
                                        const purpose = c.purpose || { fee: true, ctc: true };
                                        rowMap.set(pid, {
                                          projectId: c.project_id,
                                          name: c.projectName,
                                          similarity: c.similarity_score ?? null,
                                          score: null,
                                          rate,
                                          size: cSize,
                                          status: c.projectStatus || null,
                                          geo: c.similarity_breakdown?.geo || {},
                                          isIncluded: true,
                                          feeOn: purpose.fee !== false,
                                          ctcOn: purpose.ctc !== false,
                                          ctcLocked: c.projectStatus !== 'completed',
                                        });
                                      });
                                      // Per-row cost lookup from loadedCostMetrics.contributors
                                      const costByPid = new Map();
                                      (loadedCostMetrics?.contributors || []).forEach(c => {
                                        costByPid.set(String(c.project_id), parseFloat(c.cost_per_m2_inflated) || 0);
                                      });
                                      // Attach cost so the column is sortable too.
                                      const rawRows = Array.from(rowMap.values()).map(r => ({
                                        ...r,
                                        cost: costByPid.get(String(r.projectId)) || 0,
                                      }));
                                      // Generic sort: nulls + 0s sink to the bottom for desc
                                      // and rise to the top for asc, matching the visual cue.
                                      const dir = compSort.dir === 'asc' ? 1 : -1;
                                      const cmp = (a, b) => {
                                        const va = a[compSort.key];
                                        const vb = b[compSort.key];
                                        if (compSort.key === 'name') {
                                          return (va || '').localeCompare(vb || '') * dir;
                                        }
                                        const na = (va == null || va === '') ? -Infinity : Number(va);
                                        const nb = (vb == null || vb === '') ? -Infinity : Number(vb);
                                        if (na === nb) return 0;
                                        return (na < nb ? -1 : 1) * dir;
                                      };
                                      const rows = rawRows.sort(cmp);
                                      const includedCount = rows.filter(r => r.isIncluded).length;
                                      const toggleCompSort = (key) => {
                                        setCompSort(prev => prev.key === key
                                          ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                          : { key, dir: key === 'name' ? 'asc' : 'desc' });
                                      };
                                      const SortIcon = ({ k }) => {
                                        if (compSort.key !== k) return null;
                                        const Icon = compSort.dir === 'asc' ? ChevronUp : ChevronDown;
                                        return <Icon size={9} style={{ color: 'var(--brio-gold)', marginLeft: 2, verticalAlign: 'middle' }} />;
                                      };
                                      const sortableTh = (key, label, extraStyle) => (
                                        <th
                                          style={{ ...(extraStyle || FB_TH), cursor: 'pointer', userSelect: 'none' }}
                                          onClick={() => toggleCompSort(key)}
                                          title={`Sort by ${label}`}
                                        >
                                          {label}<SortIcon k={key} />
                                        </th>
                                      );

                                      return (
                                        <div style={{ marginBottom: '14px' }}>
                                          <div style={FB_SECTION_LABEL}>
                                            <span>Comparables{rows.length > 0 ? ` — ${includedCount} included of ${rows.length}` : ''}</span>
                                            <span style={{ display: 'flex', gap: '6px', alignItems: 'center', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                                              <span style={{ color: 'var(--brio-muted)', fontSize: '10px' }}>Add</span>
                                              <input
                                                type="text"
                                                value={feeManualSearch}
                                                onChange={e => setFeeManualSearch(e.target.value)}
                                                style={{ ...INPUT_STYLE, fontSize: '11px', maxWidth: '200px', padding: '3px 8px' }}
                                                placeholder="Type project name…"
                                              />
                                            </span>
                                          </div>

                                          {feeManualSearch.trim() && (
                                            manualSearchResults.length > 0 ? (
                                              <div style={{ maxHeight: '160px', overflowY: 'auto', marginBottom: '6px', border: '1px solid var(--brio-border)', background: '#fff' }}>
                                                {manualSearchResults.map(p => {
                                                  const pSize = sz(p);
                                                  const rate = pSize > 0 ? (p.budget / pSize) : 0;
                                                  const isAdded = p._alreadyAdded;
                                                  return (
                                                    <div
                                                      key={p.id}
                                                      onClick={() => { if (!isAdded) { handleToggleComparable(p.id, true, 'manual'); setFeeManualSearch(''); } }}
                                                      title={isAdded ? 'Already in the model' : 'Click to add to the model'}
                                                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderBottom: '1px solid var(--brio-border)', fontSize: '11px', cursor: isAdded ? 'default' : 'pointer', background: '#fff', opacity: isAdded ? 0.55 : 1 }}
                                                    >
                                                      {isAdded
                                                        ? <span style={{ width: 12, fontSize: '10px', color: 'var(--brio-green)', flexShrink: 0, textAlign: 'center' }}>✓</span>
                                                        : <Plus style={{ width: 10, height: 10, color: 'var(--brio-gold)', flexShrink: 0 }} />}
                                                      <span style={{ fontWeight: 500, flex: 1, ...CELL_TRUNCATE }}>{p.projectCode ? `${p.projectCode} — ${p.name}` : p.name}</span>
                                                      {isAdded && (
                                                        <span style={{ fontSize: '9px', color: 'var(--brio-muted)', fontStyle: 'italic', flexShrink: 0 }}>already added</span>
                                                      )}
                                                      <span style={{ color: 'var(--brio-gold)', fontWeight: 500, flexShrink: 0 }}>
                                                        {fmtCurr ? fmtCurr(rate) : rate.toFixed(0)}/m²
                                                      </span>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : (
                                              <div style={{ marginBottom: '6px', padding: '6px 10px', border: '1px solid var(--brio-border)', background: '#fff', fontSize: '11px', color: 'var(--brio-muted)', fontStyle: 'italic' }}>
                                                No projects matching “{feeManualSearch}” (only projects with budget + size are searchable)
                                              </div>
                                            )
                                          )}

                                          {rpcSuggestionsLoading && rows.length === 0 ? (
                                            <div style={{ fontSize: '11px', color: 'var(--brio-muted)', fontStyle: 'italic', padding: '8px 10px', border: '1px solid var(--brio-border)' }}>Ranking projects by similarity…</div>
                                          ) : rows.length === 0 ? (
                                            <div style={{ fontSize: '11px', color: 'var(--brio-muted)', fontStyle: 'italic', padding: '8px 10px', border: '1px solid var(--brio-border)' }}>No projects with budget &amp; size data</div>
                                          ) : (
                                            <div style={{ maxHeight: '360px', overflowY: 'auto', border: '1px solid var(--brio-border)', background: '#fff' }}>
                                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                                <thead>
                                                  <tr>
                                                    <th style={{ ...FB_TH, width: '28px' }}></th>
                                                    {sortableTh('name', 'Project')}
                                                    {sortableTh('similarity', 'Sim', { ...FB_TH, width: '72px' })}
                                                    {sortableTh('rate', `Fee${unitLabel}`, { ...FB_TH_NUM, width: '78px' })}
                                                    {sortableTh('cost', `Cost${unitLabel}`, { ...FB_TH_NUM, width: '78px' })}
                                                    {sortableTh('size', 'Area', { ...FB_TH_NUM, width: '58px' })}
                                                    <th style={{ ...FB_TH, width: '92px' }}>Purpose</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {rows.map(r => {
                                                    const cost = r.cost;
                                                    const locParts = [];
                                                    if (r.geo?.nearest_city && r.geo?.distance_to_city_km != null) {
                                                      locParts.push(`${r.geo.nearest_city} (${Math.round(r.geo.distance_to_city_km)} km)`);
                                                    }
                                                    if (r.geo?.is_urban != null) locParts.push(r.geo.is_urban ? 'Urban' : 'Rural');
                                                    if (r.geo?.project_inferred_country) locParts.push(r.geo.project_inferred_country);
                                                    const statusColor = r.status === 'completed' ? 'var(--brio-green)'
                                                      : r.status === 'paused' ? '#a16207'
                                                      : r.status === 'archived' ? 'var(--brio-muted)'
                                                      : 'var(--brio-blue, #4a6f8a)';
                                                    return (
                                                      <tr key={r.projectId} style={{ borderTop: '1px solid var(--brio-border)' }}>
                                                        <td style={FB_TD}>
                                                          <input
                                                            type="checkbox"
                                                            checked={r.isIncluded}
                                                            onChange={() => {
                                                              if (r.isIncluded) handleToggleComparable(r.projectId, false);
                                                              else handleToggleComparable(r.projectId, true, 'auto');
                                                            }}
                                                            title={r.isIncluded ? 'Remove from model' : 'Add to model'}
                                                            style={{ cursor: 'pointer', accentColor: 'var(--brio-gold)' }}
                                                          />
                                                        </td>
                                                        <td style={FB_TD}>
                                                          <div style={{ fontWeight: 500, ...CELL_TRUNCATE, maxWidth: '240px', color: r.isIncluded ? 'var(--brio-text)' : 'var(--brio-muted)' }}>{r.name}</div>
                                                          {(r.status || locParts.length > 0) && (
                                                            <div style={{ fontSize: '9px', color: 'var(--brio-muted)', marginTop: '1px', ...CELL_TRUNCATE, maxWidth: '240px' }}>
                                                              {r.status && <span style={{ color: statusColor, fontWeight: 500 }}>{r.status}</span>}
                                                              {r.status && locParts.length > 0 && <span> · </span>}
                                                              {locParts.join(' · ')}
                                                            </div>
                                                          )}
                                                        </td>
                                                        <td style={FB_TD}>
                                                          {r.similarity != null ? (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }} title={`Similarity ${r.similarity.toFixed(2)}`}>
                                                              <span style={{ position: 'relative', display: 'inline-block', width: '34px', height: '3px', background: 'var(--brio-border)' }}>
                                                                <span style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${Math.max(0, Math.min(100, r.similarity * 100))}%`, background: 'var(--brio-gold)' }} />
                                                              </span>
                                                              <span style={{ fontSize: '10px', color: 'var(--brio-muted)', fontVariantNumeric: 'tabular-nums', minWidth: '24px' }}>{r.similarity.toFixed(2)}</span>
                                                            </span>
                                                          ) : (
                                                            <span style={{ color: 'var(--brio-muted)', fontSize: '10px' }}>{r.score ?? '—'}</span>
                                                          )}
                                                        </td>
                                                        <td style={{ ...FB_TD_NUM, color: 'var(--brio-gold)', fontWeight: 600 }}>{fmtRate(r.rate)}</td>
                                                        <td style={{ ...FB_TD_NUM, color: cost > 0 ? '#ea580c' : 'var(--brio-muted)', fontWeight: cost > 0 ? 500 : 400 }}>{cost > 0 ? fmtRate(cost) : '—'}</td>
                                                        <td style={FB_TD_NUM}>{r.size > 0 ? r.size.toLocaleString() : '—'}</td>
                                                        <td style={FB_TD}>
                                                          <span style={{ display: 'inline-flex', gap: '3px' }}>
                                                            <PurposePill
                                                              kind="fee"
                                                              on={r.feeOn}
                                                              onClick={() => handlePurposeClick(r.projectId, 'fee', 'auto')}
                                                            />
                                                            <PurposePill
                                                              kind="ctc"
                                                              on={r.ctcOn}
                                                              locked={r.ctcLocked}
                                                              lockedReason={`only completed projects can count toward Avg Loaded Cost (status: ${r.status || 'unknown'})`}
                                                              onClick={() => { if (!r.ctcLocked) handlePurposeClick(r.projectId, 'ctc', 'auto'); }}
                                                            />
                                                          </span>
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}

                                          {rpcSuggestionsError && (
                                            <div style={{ fontSize: '9px', color: 'var(--brio-orange, #DB6B5D)', fontStyle: 'italic', marginTop: '3px' }}>
                                              Using fallback ranking (RPC: {rpcSuggestionsError})
                                            </div>
                                          )}
                                          {refreshCostsLastResult && !refreshingCosts && (
                                            <div style={{ fontSize: '9px', color: 'var(--brio-green)', marginTop: '3px' }}>
                                              Cost cache: updated {refreshCostsLastResult.count} project{refreshCostsLastResult.count !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                          {!refreshCostsLastResult && projectLoadedCostsOldest && (
                                            <div style={{ fontSize: '9px', color: 'var(--brio-muted)', fontStyle: 'italic', marginTop: '3px' }}>
                                              Cost cache: oldest row {(() => {
                                                const ageMs = Date.now() - new Date(projectLoadedCostsOldest).getTime();
                                                const days = Math.floor(ageMs / 86400000);
                                                if (days >= 1) return `${days}d old`;
                                                const hrs = Math.floor(ageMs / 3600000);
                                                if (hrs >= 1) return `${hrs}h old`;
                                                return `${Math.floor(ageMs / 60000)}m old`;
                                              })()}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* ─── 3. ADJUSTMENT FACTORS ─── */}
                                    {hasModel && (
                                      <div style={{ marginBottom: '14px' }}>
                                        <div style={FB_SECTION_LABEL}>
                                          <span>Adjustment Factors</span>
                                          <span style={{ color: 'var(--brio-muted)', fontSize: '10px', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                                            Drives the ×{(parseFloat(fc?.multipliers?.clamped ?? fc?.multipliers?.raw) || 1).toFixed(3)} multiplier
                                          </span>
                                        </div>
                                        {feeFactorDefs.length === 0 ? (
                                          <div style={{ fontSize: '11px', color: 'var(--brio-orange, #DB6B5D)', padding: '8px 12px', border: '1px solid var(--brio-border)' }}>
                                            ⚠ No factor definitions found. Ask admin to seed <code style={{ fontSize: '10px' }}>fee_factor_definitions</code>.
                                          </div>
                                        ) : (
                                          <div style={{ border: '1px solid var(--brio-border)', padding: '10px 14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '8px 24px', background: '#fff' }}>
                                            {feeFactorDefs.map(def => {
                                              const val = feeFactors[def.factor_key]?.value ?? def.default_value ?? 0;
                                              const minV = def.min_value ?? -1;
                                              const maxV = def.max_value ?? 1;
                                              const stepV = def.step_value ?? 0.1;
                                              const neutral = (minV + maxV) / 2;
                                              return (
                                                <div key={def.factor_key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                  <div style={{ width: '120px', flexShrink: 0 }}>
                                                    <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--brio-text)' }}>{def.label}</div>
                                                    <div style={{ fontSize: '9px', color: 'var(--brio-muted)' }}>{def.description || ''}</div>
                                                  </div>
                                                  <input
                                                    type="range"
                                                    min={minV} max={maxV} step={stepV}
                                                    value={val}
                                                    onChange={e => handleFactorChange(def.factor_key, parseFloat(e.target.value))}
                                                    style={{ flex: 1, accentColor: 'var(--brio-gold, #9F902A)', height: '4px' }}
                                                  />
                                                  <span style={{
                                                    width: '36px', textAlign: 'right', fontSize: '11px',
                                                    fontWeight: 500, flexShrink: 0, fontVariantNumeric: 'tabular-nums',
                                                    color: val > neutral ? 'var(--brio-gold)' : val < neutral ? 'var(--brio-red)' : 'var(--brio-muted)',
                                                  }}>
                                                    {val > 0 ? '+' : ''}{Number(val).toFixed(1)}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* ─── All projects reference (collapsed) ─── */}
                                    {hasModel && (
                                      <details style={{ marginTop: '8px' }}>
                                        <summary style={{ fontSize: '10px', color: 'var(--brio-muted)', cursor: 'pointer', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                          All projects with fee data ({activeProjects.filter(p => p.budget > 0 && sz(p) > 0).length})
                                        </summary>
                                        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '140px', overflowY: 'auto' }}>
                                          {activeProjects.filter(p => p.budget > 0 && sz(p) > 0).slice(0, 20).map(p => {
                                            const pSize = sz(p);
                                            const rate = pSize > 0 ? (p.budget / pSize) : 0;
                                            return (
                                              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 8px', border: '1px solid var(--brio-border)', fontSize: '10px' }}>
                                                <span style={{ fontWeight: 500, ...CELL_TRUNCATE, maxWidth: '180px' }}>{p.projectCode ? `${p.projectCode} — ${p.name}` : p.name}</span>
                                                <span style={{ color: 'var(--brio-gold)', fontWeight: 500, flexShrink: 0 }}>
                                                  {fmtCurr ? fmtCurr(rate) : rate.toFixed(0)}/m² · {pSize?.toLocaleString()} m²
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </details>
                                    )}

                                    {feeLoading && hasModel && (
                                      <div style={{ fontSize: '10px', color: 'var(--brio-muted)', padding: '4px 0', fontStyle: 'italic' }}>Loading…</div>
                                    )}

                                  </div>
                                  );
                                })()}

                                {/* ── FEE OFFERS SECTION (#10) ─── */}
                                {/* Offers link to Area Statement by row ID. Category + Area are
                                    read-only when linked. Only Rate is editable per offer. */}
                                {drawerSection === 'offers' && (() => {
                                  const offers = getOffers();
                                  const activeOfferIdx = editDraft._offerTab ?? 0;
                                  const off = offers[activeOfferIdx] || blankOffer();
                                  const areaStmt = getActiveArea();
                                  const areaGroups = areaStmt?.groups || [];

                                  const offGroupRates = off.groupRates || {};

                                  return (
                                    <div>
                                      <div style={{ display: 'flex', gap: '3px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        {offers.map((o, i) => (
                                          <button key={i} onClick={() => setEditDraft(d => ({ ...d, _offerTab: i }))}
                                            style={{
                                              padding: '3px 10px', borderRadius: 0, fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                                              border: activeOfferIdx === i ? '1px solid var(--brio-gold)' : '1px solid var(--brio-border)',
                                              background: activeOfferIdx === i ? 'var(--brio-gold)' : 'transparent',
                                              color: activeOfferIdx === i ? '#fff' : 'var(--brio-text)',
                                            }}>
                                            {o.label || `Offer ${i + 1}`}
                                          </button>
                                        ))}
                                        <button onClick={addOffer}
                                          style={{ padding: '3px 8px', borderRadius: 0, fontSize: '10px', fontWeight: 500, cursor: 'pointer',
                                            border: '1px dashed var(--brio-border)', background: 'transparent', color: 'var(--brio-muted)' }}>
                                          + Add
                                        </button>
                                        {offers.length > 1 && (
                                          <button onClick={() => { if (window.confirm(`Delete "${off.label || 'this offer'}"?`)) deleteOffer(activeOfferIdx); }}
                                            title="Delete this offer"
                                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', padding: '2px', display: 'flex', alignItems: 'center' }}>
                                            <Trash2 style={{ width: '11px', height: '11px' }} />
                                          </button>
                                        )}
                                        {/* Unit toggle */}
                                        <div style={{ marginLeft: 'auto', display: 'flex', borderRadius: '3px', border: '1px solid var(--brio-border)', overflow: 'hidden' }}>
                                          {['sqft', 'm2'].map(u => (
                                            <button key={u} onClick={() => setAreaUnit(u)} style={{
                                              padding: '2px 8px', fontSize: '10px', fontWeight: 500, cursor: 'pointer', border: 'none',
                                              background: areaUnit === u ? 'var(--brio-gold)' : 'transparent',
                                              color: areaUnit === u ? '#fff' : 'var(--brio-muted)',
                                            }}>{u === 'sqft' ? 'ft²' : 'm²'}</button>
                                          ))}
                                        </div>
                                      </div>

                                      {/* Fee table — one row per area group */}
                                      <AreaGroupFeeTable
                                        areaGroups={areaGroups}
                                        areaUnit={areaUnit}
                                        groupRates={offGroupRates}
                                        onGroupRateChange={(groupId, rate) => {
                                          const allOffers = getOffers();
                                          const updated = { ...allOffers[activeOfferIdx] };
                                          updated.groupRates = { ...(updated.groupRates || {}), [groupId]: rate };
                                          allOffers[activeOfferIdx] = updated;
                                          setEditDraft(d => ({ ...d, feeOffers: allOffers }));
                                        }}
                                        fmtCurr={fmtCurr}
                                      />

                                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}><tbody>
                                        <FormRow label="Offer Label">
                                          <input type="text" value={off.label || ''} onChange={e => updateOfferField(activeOfferIdx, 'label', e.target.value)} style={{ ...INPUT_STYLE, maxWidth: '200px' }} placeholder="e.g. Offer A" />
                                        </FormRow>
                                        <FormRow label="CC">
                                          <input type="text" value={off.cc || ''} onChange={e => updateOfferField(activeOfferIdx, 'cc', e.target.value)} style={{ ...INPUT_STYLE, maxWidth: '180px' }} />
                                        </FormRow>
                                        <FormRow label="Date">
                                          <BrioDatePicker value={off.date || ''} onChange={v => updateOfferField(activeOfferIdx, 'date', v)} placeholder="Date" />
                                        </FormRow>
                                        <FormRow label="Notes">
                                          <textarea value={off.notes || ''} onChange={e => updateOfferField(activeOfferIdx, 'notes', e.target.value)} style={TEXTAREA_STYLE} />
                                        </FormRow>
                                      </tbody></table>
                                    </div>
                                  );
                                })()}

                                {/* ── SCOPE OF WORK SECTION ─── */}
                                {drawerSection === 'sow' && (() => {
                                  const projId = editDraft.convertedProjectId;
                                  const scopesForInquiry = contractScopesHook
                                    ? [
                                        ...(projId ? contractScopesHook.getScopesForProject(projId) : []),
                                        ...(contractScopesHook.getScopesForInquiry
                                          ? contractScopesHook.getScopesForInquiry(editDraft.id).filter(s => !projId || s.projectId == null)
                                          : []),
                                      ]
                                    : [];
                                  return (
                                    <div>
                                      <div style={{ fontWeight: 500, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--brio-gold)', marginBottom: '6px' }}>Scope of Work</div>
                                      {scopesForInquiry.length === 0 && (
                                        <p style={{ fontSize: '11px', color: '#888' }}>No scope of work documents yet. Create one in the Contracts &amp; Scope page.</p>
                                      )}
                                      {scopesForInquiry.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                          {scopesForInquiry.map(s => (
                                            <div
                                              key={s.id}
                                              style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                padding: '6px 10px', borderRadius: '6px',
                                                border: '1px solid var(--brio-border)',
                                                fontSize: '11px',
                                              }}
                                            >
                                              <div>
                                                <span style={{ fontWeight: 500 }}>{s.title}</span>
                                                <span style={{ marginLeft: 6, color: '#888' }}>v{s.version}</span>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{
                                                  padding: '1px 6px', borderRadius: '8px', fontSize: '9px',
                                                  background: s.status === 'confirmed' ? 'var(--brio-green-light)' : s.status === 'negotiation' ? '#FFF3CD' : 'rgba(0,0,0,0.06)',
                                                  color: s.status === 'confirmed' ? 'var(--brio-green)' : s.status === 'negotiation' ? '#856404' : '#888',
                                                }}>{s.status}</span>
                                                <span style={{ color: '#888', fontSize: '10px' }}>
                                                  {new Date(s.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                          <p style={{ fontSize: '10px', color: '#aaa', marginTop: '4px' }}>
                                            Open the Contracts &amp; Scope page to edit these documents.
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                                {/* ── STATUS SECTION (merged with follow-ups) ─── */}
                                {drawerSection === 'pitch' && (
                                  <InquiryPitchDecks
                                    inquiryId={editDraft.id}
                                    onOpenBuilder={onOpenPitchDeck ? (id => onOpenPitchDeck(id)) : null}
                                  />
                                )}

                                {drawerSection === 'status' && (
                                  <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                                      {/* ── Inquiry Status ── */}
                                      <FormRow sectionHeader label="Inquiry Status" />
                                      <FormRow label="Status">
                                        <select
                                          value={editDraft.status || deriveInquiryStatus(editDraft)}
                                          onChange={e => {
                                            const newStatus = e.target.value;
                                            if (newStatus === 'deleted') {
                                              setShowDeleteConfirm(editDraft);
                                            } else if (newStatus === 'converted' && !editDraft.converted) {
                                              setShowConvertConfirm(editDraft);
                                            } else {
                                              setEditDraft(d => ({
                                                ...d,
                                                status: newStatus,
                                                archived: newStatus === 'archived',
                                                converted: newStatus === 'converted',
                                              }));
                                              // Persist the status change immediately (discrete
                                              // control — saves on change, Comms-style). toDb keeps
                                              // the legacy converted/archived booleans in sync.
                                              saveField(editDraft.id, 'status', newStatus);
                                            }
                                          }}
                                          style={{ ...SELECT_STYLE, maxWidth: '180px' }}
                                        >
                                          <option value="active">Active</option>
                                          <option value="not_converted">Not Converted</option>
                                          <option value="converted">Converted</option>
                                          <option value="archived">Archived</option>
                                          <option value="deleted">Delete Inquiry</option>
                                        </select>
                                        <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--brio-muted)' }}>
                                          {editDraft.status === 'not_converted' || deriveInquiryStatus(editDraft) === 'not_converted'
                                            ? 'Paused / on hold — to be revisited'
                                            : editDraft.status === 'archived' || deriveInquiryStatus(editDraft) === 'archived'
                                            ? 'Very unlikely to convert — retained for analytics'
                                            : editDraft.status === 'converted' || deriveInquiryStatus(editDraft) === 'converted'
                                            ? 'Successfully converted to project'
                                            : 'Ongoing inquiry'}
                                        </div>
                                      </FormRow>
                                      <FormRow label="Next Steps">
                                        <textarea value={editDraft.nextSteps || ''} onChange={e => setEditDraft(d => ({ ...d, nextSteps: e.target.value }))} style={TEXTAREA_STYLE} />
                                      </FormRow>

                                      {/* ── Dates & Platform ── */}
                                      <FormRow sectionHeader label="Dates & Platform" />
                                      <FormRow label="Inquiry Date">
                                        <BrioDatePicker value={editDraft.inquiryDate || ''} onChange={v => setEditDraft(d => ({ ...d, inquiryDate: v }))} placeholder="Inquiry date" />
                                      </FormRow>
                                      <FormRow label="Platform">
                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                          <select value={editDraft.platformType || 'other'} onChange={e => setEditDraft(d => ({ ...d, platformType: e.target.value }))} style={{ ...SELECT_STYLE, maxWidth: '140px' }}>
                                            {PLATFORM_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                          </select>
                                          <input type="text" value={editDraft.platformDetail || ''} onChange={e => setEditDraft(d => ({ ...d, platformDetail: e.target.value }))} style={{ ...INPUT_STYLE, maxWidth: '160px' }} placeholder="Detail…" />
                                        </div>
                                      </FormRow>

                                      {/* ── Follow-ups ── */}
                                      <FormRow sectionHeader label="Follow-ups" />
                                      <FormRow label="Next Follow-up">
                                        <BrioDatePicker value={editDraft.nextFollowupDate || ''}
                                          onChange={v => {
                                            setEditDraft(d => ({ ...d, nextFollowupDate: v }));
                                            saveField(editDraft.id, 'nextFollowupDate', v);
                                          }}
                                          placeholder="Next follow-up" />
                                      </FormRow>

                                      {/* ── Pipeline Touchpoints ── */}
                                      <FormRow sectionHeader label="Pipeline Touchpoints" />
                                      <FormRow label="Founder Meeting" hint="Date of the meeting with the founders (used by the Performance calculator)">
                                        <BrioDatePicker value={editDraft.founderMeetingDate || ''}
                                          onChange={v => {
                                            setEditDraft(d => ({ ...d, founderMeetingDate: v }));
                                            saveField(editDraft.id, 'founderMeetingDate', v);
                                          }}
                                          placeholder="Founder meeting date" />
                                      </FormRow>
                                    </tbody></table>

                                    {/* Add follow-up form */}
                                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                      <div>
                                        <div style={HINT_STYLE}>Date</div>
                                        <BrioDatePicker value={newFollowup.followupDate || ''} onChange={v => setNewFollowup(f => ({ ...f, followupDate: v }))} placeholder="Date" />
                                      </div>
                                      <div style={{ flex: 1, minWidth: '150px' }}>
                                        <div style={HINT_STYLE}>Notes</div>
                                        <input type="text" value={newFollowup.notes || ''} onChange={e => setNewFollowup(f => ({ ...f, notes: e.target.value }))} style={{ ...INPUT_STYLE, fontSize: '11px' }} placeholder="Follow-up notes…" />
                                      </div>
                                      {/* (#13) Follow-up By selector — uses employee BIGINT ID */}
                                      <div>
                                        <div style={HINT_STYLE}>Follow-up by</div>
                                        {reliableUsers.length > 0 ? (
                                          <select
                                            value={newFollowup.followupByEmployeeId || currentUser?.id || ''}
                                            onChange={e => {
                                              const eid = e.target.value;
                                              const user = reliableUsers.find(u => String(u.id) === String(eid));
                                              setNewFollowup(f => ({
                                                ...f,
                                                followupByEmployeeId: eid ? (parseInt(eid, 10) || eid) : null,
                                                followupByName: user ? getUserDisplayName(user) : '',
                                              }));
                                            }}
                                            style={{ ...SELECT_STYLE, maxWidth: '150px', fontSize: '11px' }}
                                          >
                                            <option value="">— Select —</option>
                                            {reliableUsers.filter(u => u.isActive !== false && (u.role === 'admin' || u.role === 'admin-pa' || u.role === 'supervisor')).map(u => (
                                              <option key={u.id} value={u.id}>{getUserDisplayName(u)}</option>
                                            ))}
                                          </select>
                                        ) : (
                                          <div>
                                            <div style={{ fontSize: '10px', color: 'var(--brio-orange, #DB6B5D)', marginBottom: '2px' }}>No users loaded</div>
                                            <input
                                              type="text"
                                              value={newFollowup.followupByName || ''}
                                              onChange={e => setNewFollowup(f => ({ ...f, followupByName: e.target.value, followupByEmployeeId: null }))}
                                              style={{ ...INPUT_STYLE, maxWidth: '150px', fontSize: '11px' }}
                                              placeholder="Enter name…"
                                            />
                                          </div>
                                        )}
                                      </div>
                                      <button onClick={handleAddFollowup} className="brio-btn brio-btn-primary" style={{ fontSize: '10px', padding: '4px 10px' }}>+ Add</button>
                                    </div>
                                    {followupError && (
                                      <div style={{ fontSize: '11px', color: 'var(--brio-red)', padding: '4px 6px', background: 'rgba(147, 73, 72, 0.08)', borderRadius: 0, marginBottom: '6px' }}>{followupError}</div>
                                    )}
                                    {followups.length === 0 && <div style={{ fontSize: '11px', color: 'var(--brio-muted)', padding: '10px 0' }}>No follow-ups yet</div>}
                                    {followups.map(f => (
                                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '4px 6px', borderBottom: '1px solid var(--brio-border, #f1f5f9)', gap: '6px' }}>
                                        <div>
                                          <div style={{ fontSize: '11px', fontWeight: 500 }}>
                                            {fmtDate(f.followupDate)}
                                            <span style={{ fontWeight: 400, color: 'var(--brio-grey)', marginLeft: '6px' }}>
                                              · by {f.followupByName || (f.followupByEmployeeId || f.followupByUserId ? 'Unknown' : '—')}
                                            </span>
                                          </div>
                                          <div style={{ fontSize: '11px', color: 'var(--brio-text)' }}>{f.notes || '—'}</div>
                                        </div>
                                        <button onClick={() => handleDeleteFollowup(f.id)} title="Delete"
                                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--brio-red)', fontSize: '12px', flexShrink: 0 }}>
                                          <Trash2 style={{ width: '11px', height: '11px' }} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>
          </div>
        </BrioAnalyticsSurface>
      </BrioFrame>

      {/* Close scrollable body wrapper for fullscreen mode */}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          ADD INQUIRY MODAL
         ════════════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <BrioModal
          title="New Inquiry"
          onClose={() => { setShowAddModal(false); setAddForm({}); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button onClick={() => { setShowAddModal(false); setAddForm({}); }} className="brio-btn brio-btn-secondary">Cancel</button>
              <button onClick={handleAdd} disabled={saving} className="brio-btn brio-btn-primary">{saving ? 'Creating…' : 'Create Inquiry'}</button>
            </BrioActionGroup>
          }
        >
          <BrioByCode code={FORM_CODE}>
            <BrioTable {...formTableProps}>
              <tbody>
                <FormRow sectionHeader label="Inquiry Details" />
                <FormRow label="Client">
                  <select value={addForm.clientId || ''} onChange={e => setAddForm(f => ({ ...f, clientId: e.target.value || null }))} style={SELECT_STYLE}>
                    <option value="">— Select existing client —</option>
                    {activeClients.map(c => <option key={c.id} value={c.id}>{c.organisation || c.name}</option>)}
                  </select>
                </FormRow>
                {!addForm.clientId && (
                  <FormRow label="Or New Org" hint="Will be linked later">
                    <input type="text" value={addForm.orgName || ''} onChange={e => setAddForm(f => ({ ...f, orgName: e.target.value }))} style={INPUT_STYLE} placeholder="Organisation name" />
                  </FormRow>
                )}
                <FormRow label="Inquiry Date">
                  <BrioDatePicker value={addForm.inquiryDate || ''} onChange={v => setAddForm(f => ({ ...f, inquiryDate: v }))} placeholder="Inquiry date" />
                </FormRow>
                <FormRow label="Platform">
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <select value={addForm.platformType || 'other'} onChange={e => setAddForm(f => ({ ...f, platformType: e.target.value }))} style={{ ...SELECT_STYLE, maxWidth: '140px' }}>
                      {PLATFORM_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <input type="text" value={addForm.platformDetail || ''} onChange={e => setAddForm(f => ({ ...f, platformDetail: e.target.value }))} style={{ ...INPUT_STYLE, maxWidth: '160px' }} placeholder="Detail (e.g. Instagram)" />
                  </div>
                </FormRow>
                <FormRow label="Potential">
                  <select value={addForm.potential || '50'} onChange={e => setAddForm(f => ({ ...f, potential: e.target.value }))} style={{ ...SELECT_STYLE, maxWidth: '120px' }}>
                    {POTENTIAL_OPTIONS.map(o => <option key={o} value={o}>{o}%</option>)}
                  </select>
                </FormRow>
                <FormRow label="Priority">
                  <select value={addForm.priority || 'medium'} onChange={e => setAddForm(f => ({ ...f, priority: e.target.value }))} style={{ ...SELECT_STYLE, maxWidth: '120px' }}>
                    {dynPriorityLabels.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
                  </select>
                </FormRow>
                <FormRow label="Project Type">
                  <select value={addForm.projectType || ''} onChange={e => setAddForm(f => ({ ...f, projectType: e.target.value }))} style={{ ...SELECT_STYLE, maxWidth: '180px' }}>
                    <option value="">— Select —</option>
                    {BRIO_PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FormRow>
                <FormRow label="City">
                  <input type="text" value={addForm.locationCity || ''} onChange={e => setAddForm(f => ({ ...f, locationCity: e.target.value }))} style={{ ...INPUT_STYLE, maxWidth: '180px' }} />
                </FormRow>
              </tbody>
            </BrioTable>
          </BrioByCode>
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════════
          XLSX EXPORT DIALOG
         ════════════════════════════════════════════════════════════════ */}
      {showExportDialog && (
        <BrioModal
          title="Export to Excel"
          onClose={() => setShowExportDialog(false)}
          width="sm"
          footer={
            <BrioActionGroup>
              <button onClick={() => setShowExportDialog(false)} className="brio-btn brio-btn-secondary">Cancel</button>
              <button onClick={handleXlsxExport} className="brio-btn brio-btn-primary"
                disabled={!exportSheets.area && !exportSheets.scenarios && !exportSheets.offers}>
                <Download style={{ width: '12px', height: '12px', marginRight: '4px' }} /> Export XLSX
              </button>
            </BrioActionGroup>
          }
        >
          <div style={{ padding: '6px 4px', fontSize: '12px' }}>
            <p style={{ marginBottom: '8px', fontWeight: 500 }}>Select sheets to include:</p>
            {[
              { key: 'area', label: 'Area Statement', desc: 'Groups, subrows, carpet/walls/constructed areas' },
              { key: 'scenarios', label: 'Fee Scenarios', desc: 'All scenarios with group rates and fees' },
              { key: 'offers', label: 'Offers', desc: 'All offers with group rates, CC, dates, notes' },
            ].map(sh => (
              <label key={sh.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '4px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={exportSheets[sh.key]}
                  onChange={e => setExportSheets(prev => ({ ...prev, [sh.key]: e.target.checked }))}
                  style={{ marginTop: '2px' }} />
                <div>
                  <div style={{ fontWeight: 500, fontSize: '12px' }}>{sh.label}</div>
                  <div style={{ fontSize: '10px', color: 'var(--brio-muted)' }}>{sh.desc}</div>
                </div>
              </label>
            ))}
            <p style={{ marginTop: '10px', fontSize: '10px', color: 'var(--brio-muted)' }}>
              Areas and rates are exported in <strong>{areaUnit === 'm2' ? 'm²' : 'ft²'}</strong>, matching the unit toggle on the Area Statement tab.
            </p>
          </div>
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════════
          CONVERT CONFIRM MODAL
         ════════════════════════════════════════════════════════════════ */}
      {showConvertConfirm && (
        <BrioModal
          title="Convert Inquiry to Project"
          onClose={() => setShowConvertConfirm(null)}
          width="sm"
          footer={
            <BrioActionGroup>
              <button onClick={() => setShowConvertConfirm(null)} className="brio-btn brio-btn-secondary">Cancel</button>
              <button onClick={() => handleConvert(showConvertConfirm)} disabled={saving} className="brio-btn brio-btn-primary">{saving ? 'Converting…' : 'Convert and Create Project'}</button>
            </BrioActionGroup>
          }
        >
          <div style={{ padding: '6px 4px', fontSize: '12px' }}>
            <p>This will:</p>
            <ul style={{ margin: '6px 0', paddingLeft: '18px', lineHeight: '1.8' }}>
              <li>Mark the inquiry as <strong>Converted</strong></li>
              <li>Create a new project: <strong>{getClientName(showConvertConfirm)} - {showConvertConfirm.projectType || 'New Project'}</strong></li>
              {!showConvertConfirm.clientId && showConvertConfirm.clientOrgNameOverride && (
                <li>Create a new client: <strong>{showConvertConfirm.clientOrgNameOverride}</strong></li>
              )}
              {/* Shown because the inquiry's Location is free text and this is
                  the last point at which a bad one can be fixed cheaply — after
                  conversion it has to be corrected on the projects overview. */}
              {(() => {
                const loc = parseInquiryLocation(showConvertConfirm.locationCity);
                if (!loc.city && !loc.country) return null;
                return (
                  <li>Set the project location: <strong>{[loc.city, loc.country].filter(Boolean).join(', ')}</strong></li>
                );
              })()}
            </ul>
          </div>
        </BrioModal>
      )}
      {showDeleteConfirm && (
        <BrioModal
          title="Delete Inquiry"
          onClose={() => setShowDeleteConfirm(null)}
          width="sm"
          footer={
            <BrioActionGroup>
              <button onClick={() => setShowDeleteConfirm(null)} className="brio-btn brio-btn-secondary">Cancel</button>
              <button
                onClick={async () => {
                  setSaving(true);
                  const res = await supabaseInquiries.deleteInquiry(showDeleteConfirm.id);
                  setSaving(false);
                  if (res.success) {
                    setShowDeleteConfirm(null);
                    setExpandedId(null);
                  }
                }}
                disabled={saving}
                style={{
                  padding: '6px 16px', fontSize: '12px', fontWeight: 500,
                  borderRadius: 0, cursor: 'pointer',
                  border: '1px solid var(--brio-red, #dc2626)',
                  background: 'var(--brio-red, #dc2626)',
                  color: '#fff',
                }}
              >
                {saving ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </BrioActionGroup>
          }
        >
          <div style={{ padding: '6px 4px', fontSize: '12px' }}>
            <p>Are you sure you want to permanently delete this inquiry?</p>
            <p style={{ marginTop: '8px', fontWeight: 500 }}>{getClientName(showDeleteConfirm)} — {showDeleteConfirm.projectType || 'Unknown type'}</p>
            <p style={{ marginTop: '8px', fontSize: '11px', color: 'var(--brio-red)' }}>This action cannot be undone.</p>
          </div>
        </BrioModal>
      )}
    </div>
  );
}
